import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import EventsMaster from './EventsMaster';
import { downloadHtmlAsDocx } from '../../utils/docxExport';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// ═══════════════════════════════════════════════════════════════════════
// ACCENT PALETTE — the same 6 colors Registers.js already uses per
// register type (navy/purple/cyan/emerald/amber/orange), reused here so
// nothing new is introduced. Each color pairs a light tint (for badges,
// card left-borders, icon chips) with a dark-mode-safe translucent tint.
// ═══════════════════════════════════════════════════════════════════════
const ACCENTS = {
  navy:    { bg: 'bg-blue-50',    strong: 'bg-blue-100',    text: 'text-blue-700',    textLight: 'text-blue-600',    border: 'border-l-blue-400' },
  purple:  { bg: 'bg-purple-50',  strong: 'bg-purple-100',  text: 'text-purple-700',  textLight: 'text-purple-600',  border: 'border-l-purple-400' },
  cyan:    { bg: 'bg-cyan-50',    strong: 'bg-cyan-100',    text: 'text-cyan-700',    textLight: 'text-cyan-600',    border: 'border-l-cyan-400' },
  emerald: { bg: 'bg-emerald-50', strong: 'bg-emerald-100', text: 'text-emerald-700', textLight: 'text-emerald-600', border: 'border-l-emerald-400' },
  amber:   { bg: 'bg-amber-50',   strong: 'bg-amber-100',   text: 'text-amber-700',   textLight: 'text-amber-600',   border: 'border-l-amber-400' },
  orange:  { bg: 'bg-orange-50',  strong: 'bg-orange-100',  text: 'text-orange-700',  textLight: 'text-orange-600',  border: 'border-l-orange-400' },
  red:     { bg: 'bg-red-50',     strong: 'bg-red-100',     text: 'text-red-700',     textLight: 'text-red-600',     border: 'border-l-red-400' },
  slate:   { bg: 'bg-gray-100',   strong: 'bg-gray-100',    text: 'text-gray-600',    textLight: 'text-gray-500',    border: 'border-l-gray-300' },
};

// BM/AGM/EGM colored the same way Board vs General meetings already are
// in the Statutory Registers module (board=navy, general-family=purple) —
// AGM gets its own amber so the two general-meeting types stay visually distinct.
function meetingAccent(meetingType) {
  if (meetingType === 'BM') return ACCENTS.navy;
  if (meetingType === 'AGM') return ACCENTS.amber;
  if (meetingType === 'EGM') return ACCENTS.purple;
  return ACCENTS.slate;
}
function filingAccent(status) {
  if (status === 'Approved') return ACCENTS.emerald;
  if (status === 'Filed') return ACCENTS.orange;
  if (status === 'Rejected') return ACCENTS.red;
  return ACCENTS.slate;
}

// ═══════════════════════════════════════════════════════════════════════
// SHARED DOC-EXPORT HELPERS
// ═══════════════════════════════════════════════════════════════════════
function textToHtml(text) {
  return (text || '')
    .split('\n\n')
    .map(para => `<p>${para.split('\n').map(l => l.replace(/&/g, '&amp;').replace(/</g, '&lt;')).join('<br/>')}</p>`)
    .join('');
}

function buildWordDoc(title, bodyHtml) {
  return `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
<head><meta charset="utf-8"><title>${title}</title>
<style>
  @page { size: A4; margin: 72pt 72pt 72pt 90pt; }
  body { font-family: "Plus Jakarta Sans", sans-serif; font-size: 12pt; text-align: justify; color: #000; }
  p { margin: 0 0 6pt 0; line-height: 1.35; text-align: justify; }
  h1 { font-size: 14pt; font-weight: bold; text-transform: uppercase; text-align: center; margin: 0 0 4pt 0; }
  h2 { font-size: 12pt; font-weight: bold; text-align: center; margin: 0 0 12pt 0; }
  table { width: 100%; border-collapse: collapse; margin-top: 12pt; }
  td, th { border: 1pt solid #000; padding: 6pt 8pt; font-size: 10pt; vertical-align: top; }
  th { font-weight: bold; text-align: center; }
  tr.sig-row { height: 45pt; }
</style></head>
<body>${bodyHtml}</body></html>`;
}

async function downloadWord(filename, bodyHtml) {
  const html = buildWordDoc(filename, bodyHtml);
  await downloadHtmlAsDocx(filename.replace(/\s+/g, '_'), html);
}

// ═══════════════════════════════════════════════════════════════════════
// AGM/EGM STATUTORY DOCUMENT ENGINES — ported verbatim from Annual
// Filing's AGM document formatting work, so shareholder-meeting documents
// look identical whether generated from an Annual Filing case or an
// Event Filing case discussing EGM/AGM matters. Board Meeting documents
// keep Event Filing's existing simpler style throughout — only shown once,
// here, then reused by all five generators below.
// ═══════════════════════════════════════════════════════════════════════
function ordinal(n) { return n === 1 ? 'st' : n === 2 ? 'nd' : n === 3 ? 'rd' : 'th'; }

// ── Shorter Notice Consent letter (Sec 101(1) + SS-2) ──
function buildStatutoryDoc(title, bodyHtml) {
  return `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
<head><meta charset="utf-8"><title>${title}</title>
<style>
  @page { size: A4 portrait; margin: 25mm 20mm 20mm 25mm; }
  body { font-family: "Plus Jakarta Sans", sans-serif; font-size: 11pt; color: #000; }
  p { margin: 0 0 8pt 0; }
  .doc-title { font-weight: bold; font-size: 14pt; text-transform: uppercase; text-align: center; line-height: 1.3; margin: 0 0 4pt 0; }
  .doc-ref { font-size: 10.5pt; text-align: center; margin: 0 0 3pt 0; }
  .doc-subtitle { font-weight: bold; font-size: 14pt; text-transform: uppercase; text-align: center; line-height: 1.3; margin: 10pt 0 16pt 0; }
  .meta { font-size: 10.5pt; margin: 0 0 2pt 0; }
  .subject { font-weight: bold; font-size: 11pt; margin: 12pt 0; text-align: left; }
  .body-text { font-size: 11pt; text-align: justify; line-height: 1.5; margin: 0 0 10pt 0; }
  .sig-table { width: 100%; border-collapse: collapse; margin-top: 28pt; }
  .sig-table td { border: none; font-size: 11pt; padding: 1pt 0; vertical-align: top; }
  .sig-label { color: #000; width: 42%; }
  .letter-page { page-break-after: always; }
  .letter-page:last-child { page-break-after: auto; }
</style></head>
<body>${bodyHtml}</body></html>`;
}
async function downloadStatutoryWord(filename, bodyHtml) {
  const html = buildStatutoryDoc(filename, bodyHtml);
  await downloadHtmlAsDocx(filename.replace(/\s+/g, '_'), html);
}
const NUM_ONES = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
  'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
const NUM_TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
function twoDigitWords(n) { return n < 20 ? NUM_ONES[n] : NUM_TENS[Math.floor(n / 10)] + (n % 10 ? ' ' + NUM_ONES[n % 10] : ''); }
function threeDigitWords(n) {
  const h = Math.floor(n / 100), r = n % 100;
  return (h ? NUM_ONES[h] + ' Hundred' + (r ? ' ' : '') : '') + (r ? twoDigitWords(r) : '');
}
function numberToWordsIndian(num) {
  num = Math.round(Number(num) || 0);
  if (num <= 0) return 'Zero';
  const crore = Math.floor(num / 10000000); num %= 10000000;
  const lakh = Math.floor(num / 100000); num %= 100000;
  const thousand = Math.floor(num / 1000); num %= 1000;
  const rest = num;
  const parts = [];
  if (crore) parts.push(threeDigitWords(crore) + ' Crore');
  if (lakh) parts.push(threeDigitWords(lakh) + ' Lakh');
  if (thousand) parts.push(threeDigitWords(thousand) + ' Thousand');
  if (rest) parts.push(threeDigitWords(rest));
  return parts.join(' ').trim() || 'Zero';
}

// ── Notice of Meeting ──
function buildNoticeStatutoryDoc(title, bodyHtml, headerHtml) {
  return `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
<head><meta charset="utf-8"><title>${title}</title>
<style>
  @page { size: A4 portrait; margin: 25mm 20mm 20mm 25mm;${headerHtml ? ' mso-header: h1; mso-header-margin: 10mm;' : ''} }
  body { font-family: "Plus Jakarta Sans", sans-serif; font-size: 11pt; color: #000000; }
  p { margin: 0; }
  .co-header { font-weight: bold; font-size: 13.5pt; text-align: center; text-transform: uppercase; line-height: 1.3; margin: 0 0 4pt 0; }
  .co-meta { font-weight: normal; font-size: 9.75pt; text-align: center; line-height: 1.3; margin: 0 0 2pt 0; }
  .co-meta.last { margin-bottom: 12pt; }
  .doc-title { font-weight: bold; font-size: 12.75pt; text-align: center; text-transform: uppercase; text-decoration: underline; line-height: 1.3; margin: 10pt 0 14pt 0; }
  .doc-provision { font-style: italic; font-weight: normal; font-size: 9.25pt; text-align: center; line-height: 1.2; margin: 0 0 10pt 0; }
  .section-heading { font-weight: bold; font-size: 11pt; text-align: left; text-transform: uppercase; text-decoration: underline; line-height: 1.4; margin: 14pt 0 6pt 0; }
  .item-title { font-weight: bold; font-size: 11pt; text-align: left; text-transform: uppercase; line-height: 1.4; margin: 10pt 0 4pt 0; }
  .body-narrative { font-weight: normal; font-size: 11pt; text-align: justify; line-height: 1.5; margin: 0 0 6pt 0; }
  .resolution-quote { font-size: 10.5pt; text-align: justify; line-height: 1.4; margin: 0 0 6pt 15pt; page-break-inside: avoid; }
  .resolution-quote b { font-weight: bold; }
  .agenda-list { margin: 0 0 8pt 0; padding-left: 22pt; }
  .agenda-list li { font-size: 11pt; text-align: justify; line-height: 1.5; margin-bottom: 6pt; }
  .statutory-notes { margin: 6pt 0 0 0; }
  .note-item { font-size: 9.75pt; text-align: justify; line-height: 1.35; margin: 0 0 5pt 18pt; text-indent: -18pt; }
  .note-item b { font-weight: bold; }
  .explanatory-statement p { font-weight: normal; font-size: 11pt; text-align: justify; line-height: 1.5; margin: 0 0 6pt 0; }
  .explanatory-statement.new-page { page-break-before: always; }
  .sig-block { font-weight: bold; font-size: 10.75pt; line-height: 1.4; margin-top: 35pt; page-break-inside: avoid; }
  .sig-block .sig-name { text-transform: uppercase; }
  .docx-header { mso-element: header; }
</style></head>
<body>${headerHtml ? `<div class="docx-header" id="h1">${headerHtml}</div>` : ''}${bodyHtml}</body></html>`;
}
async function downloadNoticeStatutoryWord(filename, bodyHtml, headerHtml) {
  const html = buildNoticeStatutoryDoc(filename, bodyHtml, headerHtml);
  await downloadHtmlAsDocx(filename.replace(/\s+/g, '_'), html);
}
// A legal Notice/CTC/Minutes quote must show ONLY the operative clauses
// ("RESOLVED THAT ... RESOLVED FURTHER THAT ...") — never any narrative
// a resolution template happens to carry before that point (e.g. "The
// Chairman informed the members that... After discussion, the members
// passed the following resolution:"). That framing belongs in MINUTES
// only, and even there it should come from what the document engine
// itself generates, not from text baked into Resolution Master — a
// template authored as a Minutes narrative must not leak into a Notice,
// which is drafted and sent BEFORE the meeting even happens. This is the
// one place that guarantee is enforced, so every quote — in every engine,
// in both modules — funnels through it.
function extractOperativeClauses(text) {
  const t = (text || '').trim().replace(/^"+/, '').replace(/"+$/, '');
  const idx = t.search(/RESOLVED\s+THAT/i);
  return idx > 0 ? t.slice(idx).trim() : t;
}
function formatResolutionQuote(text) {
  const t = extractOperativeClauses(text);
  const m = t.match(/^(RESOLVED(?:\s+FURTHER)?\s+THAT)/i);
  const body = m ? `<b>${m[1].toUpperCase()}</b> ${t.slice(m[1].length).trim()}` : t;
  // Every quote block is wrapped in a single <p> when exported (see every
  // call site below), and HTML/Word collapse bare newlines by default —
  // so a carefully (i)/(ii)/(iii)-numbered clause list (e.g. an MOA
  // objects-clause alteration, see NUMBERED_CLAUSE_KEYS above) would
  // otherwise print as one run-on paragraph in Notice/Minutes/CTC no
  // matter how cleanly it was typed. A resolution with no line breaks is
  // untouched — this only ever adds breaks where one was already there.
  return body.replace(/\n/g, '<br/><br/>');
}
// (extractOperativeClauses inside formatResolutionQuote already discards
// any narrative and surrounding quotes, so this is now just an alias —
// kept as its own name since it reads better at each call site.)
function sanitizeQuotedResolution(text) {
  return formatResolutionQuote(text);
}
// Every Notice/Minutes/CTC quote block — single- or multi-clause — should
// always be built by mapping this over formatResolutionQuote(), never by
// quoting a resolution's raw text directly.
function splitResolutionParagraphs(text) {
  const cleaned = extractOperativeClauses(text);
  const parts = cleaned.split(/(?=RESOLVED\s+FURTHER\s+THAT)/i).map(s => s.trim()).filter(Boolean);
  return parts.length ? parts : [cleaned];
}
// A resolution's Master title sometimes carries an internal disambiguation
// suffix the CS added purely to tell similar entries apart while picking
// one from a list — e.g. "(Special Resolution — EGM, authorizes RD
// process)" or "(Board Resolution — recommends & convenes EGM)". That
// suffix must never print in a legal document heading; the picker already
// shows Type/Category as a separate subtitle, so nothing is lost by
// stripping it here. Only strips a TRAILING parenthetical that explicitly
// names a meeting/resolution-type token — a genuine substantive title
// with an unrelated parenthetical (e.g. a section reference) is left alone.
function cleanLegalTitle(title) {
  return (title || '')
    .replace(/\s*\((?:[^()]*\b(?:BM|AGM|EGM|Board Resolution|Ordinary Resolution|Special Resolution|Ordinary Business|Special Business|authorizes|recommends|convenes)\b[^()]*)\)\s*$/i, '')
    .trim();
}
// The company's letterhead — name, CIN, registered office, registered
// e-mail — rendered ONCE per document as a genuine Word page header (see
// .docx-header / mso-element:header above), so it repeats automatically on
// every page of a multi-page document instead of needing to be reapplied
// by hand. Every Notice/Attendance/Minutes/CTC call site should build this
// once and pass it as the header argument, never inline it into the body.
function buildLetterheadHtml(item) {
  return `<p class="co-header">${item.company_name}</p>
<p class="co-meta">CIN: ${item.cin_llpin || '—'}</p>
<p class="co-meta">Regd. Office: ${item.registered_office || '—'}</p>
<p class="co-meta last">E-Mail: ${item.registered_email || '—'}</p>`;
}

// ── Attendance Sheet (Sec 103/105/112/113) ──
function buildAttendanceStatutoryDoc(title, bodyHtml, headerHtml) {
  return `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
<head><meta charset="utf-8"><title>${title}</title>
<style>
  @page { size: A4 portrait; margin: 25mm 20mm 20mm 25mm;${headerHtml ? ' mso-header: h1; mso-header-margin: 10mm;' : ''} }
  body { font-family: "Plus Jakarta Sans", sans-serif; font-size: 10pt; color: #000000; }
  p { margin: 0; }
  .co-header { font-weight: bold; font-size: 13.5pt; text-align: center; text-transform: uppercase; line-height: 1.3; margin: 0 0 4pt 0; }
  .co-meta { font-weight: normal; font-size: 9.25pt; text-align: center; line-height: 1.3; margin: 0 0 2pt 0; }
  .co-meta.last { margin-bottom: 10pt; }
  .doc-title { font-weight: bold; font-size: 12pt; text-align: center; text-transform: uppercase; text-decoration: underline; line-height: 1.3; margin: 8pt 0 12pt 0; }
  .meta-table { width: 100%; border-collapse: collapse; margin: 0 0 14pt 0; }
  .meta-table td { border: 1pt solid #000000; padding: 4pt 6pt; font-size: 10pt; }
  .meta-table td.label { font-weight: bold; width: 30%; background: #F7F7F7; }
  .section-heading { font-weight: bold; font-size: 10.5pt; text-align: left; text-transform: uppercase; margin: 14pt 0 6pt 0; }
  table.att-table { width: 100%; border-collapse: collapse; margin: 0 0 4pt 0; }
  table.att-table th { border: 1pt solid #000000; padding: 4pt 6pt; font-weight: bold; font-size: 9.75pt; text-align: center; background: #F2F2F2; }
  table.att-table td { border: 1pt solid #000000; padding: 4pt 6pt; font-size: 9.75pt; text-align: left; vertical-align: middle; height: 28pt; }
  table.att-table td.num { text-align: center; }
  table.att-table td.sig { width: 16%; }
  table.att-table tr.total-row td { font-weight: bold; background: #FAFAFA; }
  .quorum-block { margin-top: 16pt; border: 1pt solid #000000; padding: 8pt 10pt; page-break-inside: avoid; }
  .quorum-block .qb-title { font-weight: bold; font-size: 10.5pt; text-transform: uppercase; margin-bottom: 6pt; }
  .quorum-block p { font-size: 10pt; line-height: 1.5; margin: 0 0 3pt 0; }
  .sig-block { font-weight: bold; font-size: 10pt; line-height: 1.4; margin-top: 30pt; page-break-inside: avoid; }
  .sig-block .sig-name { text-transform: uppercase; }
  .docx-header { mso-element: header; }
</style></head>
<body>${headerHtml ? `<div class="docx-header" id="h1">${headerHtml}</div>` : ''}${bodyHtml}</body></html>`;
}
async function downloadAttendanceStatutoryWord(filename, bodyHtml, headerHtml) {
  const html = buildAttendanceStatutoryDoc(filename, bodyHtml, headerHtml);
  await downloadHtmlAsDocx(filename.replace(/\s+/g, '_'), html);
}
const ATTENDEE_CATEGORY_LABELS = { self: 'Self', corp_rep: 'Corp. Rep. (Sec 113)', nominee: 'Nominee (Sec 112)' };

// ── Minutes of Meeting ──
function buildMinutesStatutoryDoc(title, bodyHtml, headerHtml) {
  return `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
<head><meta charset="utf-8"><title>${title}</title>
<style>
  @page { size: A4 portrait; margin: 25mm 20mm 20mm 25mm;${headerHtml ? ' mso-header: h1; mso-header-margin: 10mm;' : ''} }
  body { font-family: "Plus Jakarta Sans", sans-serif; font-size: 11pt; color: #000000; }
  p { margin: 0; }
  .co-header { font-weight: bold; font-size: 13.5pt; text-align: center; text-transform: uppercase; line-height: 1.3; margin: 0 0 4pt 0; }
  .co-meta { font-weight: normal; font-size: 9.75pt; text-align: center; line-height: 1.3; margin: 0 0 2pt 0; }
  .co-meta.last { margin-bottom: 12pt; }
  .doc-title { font-weight: bold; font-size: 12.75pt; text-align: center; text-transform: uppercase; text-decoration: underline; line-height: 1.3; margin: 10pt 0 14pt 0; }
  .opening-narrative { font-weight: bold; font-size: 11pt; text-align: justify; line-height: 1.5; margin: 0 0 10pt 0; }
  table.attendance-box { width: 100%; border-collapse: collapse; margin: 0 0 14pt 0; }
  table.attendance-box th { border: 1pt solid #000000; padding: 5pt 8pt; font-weight: bold; font-size: 10pt; text-align: left; text-transform: uppercase; background: #F2F2F2; width: 50%; }
  table.attendance-box td { border: 1pt solid #000000; padding: 5pt 8pt; font-size: 10pt; text-align: left; vertical-align: top; }
  table.attendance-box .entry-name { font-weight: bold; }
  .proc-heading { font-weight: bold; font-size: 11pt; text-align: left; text-transform: uppercase; line-height: 1.4; margin: 12pt 0 4pt 0; }
  .section-heading { font-weight: bold; font-size: 11pt; text-align: left; text-transform: uppercase; text-decoration: underline; line-height: 1.4; margin: 14pt 0 6pt 0; }
  .item-title { font-weight: bold; font-size: 11pt; text-align: left; line-height: 1.4; margin: 10pt 0 4pt 0; }
  .procedural-narrative { font-weight: normal; font-size: 11pt; text-align: justify; line-height: 1.5; margin: 0 0 6pt 0; }
  .resolution-quote { font-size: 10.5pt; text-align: justify; line-height: 1.4; margin: 0 0 6pt 15pt; page-break-inside: avoid; }
  .resolution-quote b { font-weight: bold; }
  .vote-result { font-weight: bold; font-size: 11pt; text-align: justify; line-height: 1.5; margin: 0 0 8pt 0; }
  .minute-entry { font-weight: bold; font-size: 10pt; text-align: left; line-height: 1.35; margin: 20pt 0 15pt 0; }
  .sig-block { font-weight: bold; font-size: 10.75pt; line-height: 1.4; margin-top: 35pt; page-break-inside: avoid; }
  .sig-block .sig-name { text-transform: uppercase; }
  .docx-header { mso-element: header; }
</style></head>
<body>${headerHtml ? `<div class="docx-header" id="h1">${headerHtml}</div>` : ''}${bodyHtml}</body></html>`;
}
async function downloadMinutesStatutoryWord(filename, bodyHtml, headerHtml) {
  const html = buildMinutesStatutoryDoc(filename, bodyHtml, headerHtml);
  await downloadHtmlAsDocx(filename.replace(/\s+/g, '_'), html);
}

// ── Certified True Copy (any meeting type) ──
function buildCtcStatutoryDoc(title, bodyHtml, headerHtml) {
  return `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
<head><meta charset="utf-8"><title>${title}</title>
<style>
  @page { size: A4 portrait; margin: 25mm 20mm 20mm 25mm;${headerHtml ? ' mso-header: h1; mso-header-margin: 10mm;' : ''} }
  body { font-family: "Plus Jakarta Sans", sans-serif; font-size: 11pt; color: #000000; }
  p { margin: 0; }
  .co-header { font-weight: bold; font-size: 13.5pt; text-align: center; text-transform: uppercase; line-height: 1.3; margin: 0 0 3pt 0; }
  .co-meta { font-weight: normal; font-size: 9.75pt; text-align: center; line-height: 1.3; margin: 0 0 2pt 0; }
  .co-meta.last { margin-bottom: 12pt; }
  .doc-title { font-weight: bold; font-size: 12.5pt; text-align: center; text-transform: uppercase; text-decoration: underline; line-height: 1.3; margin: 10pt 0; }
  .preamble { font-weight: normal; font-size: 11pt; text-align: justify; line-height: 1.5; margin: 0 0 12pt 0; }
  .preamble b { font-weight: bold; }
  .item-title { font-weight: bold; font-size: 11pt; text-align: left; text-transform: uppercase; text-decoration: underline; line-height: 1.4; margin: 10pt 0; }
  .resolution-quote { font-size: 10.5pt; text-align: justify; line-height: 1.4; margin: 0 0 6pt 15pt; page-break-inside: avoid; }
  .resolution-quote b { font-weight: bold; }
  .ctc-banner { font-weight: bold; font-size: 11pt; text-align: center; line-height: 1.3; margin: 20pt 0 12pt 0; }
  .sig-block { page-break-inside: avoid; margin-top: 6pt; }
  .sig-for { font-weight: bold; font-size: 11pt; text-align: left; text-transform: uppercase; line-height: 1.3; margin: 0 0 35pt 0; }
  .sig-name-block { font-weight: bold; font-size: 10.5pt; text-align: left; line-height: 1.3; }
  .sig-name-block .sig-name { text-transform: uppercase; }
  .sig-meta { font-weight: bold; font-size: 10pt; text-align: left; line-height: 1.3; margin-top: 10pt; }
  .section-heading { font-weight: bold; font-size: 11pt; text-align: left; text-transform: uppercase; text-decoration: underline; line-height: 1.4; margin: 14pt 0 6pt 0; }
  .docx-header { mso-element: header; }
</style></head>
<body>${headerHtml ? `<div class="docx-header" id="h1">${headerHtml}</div>` : ''}${bodyHtml}</body></html>`;
}
async function downloadCtcStatutoryWord(filename, bodyHtml, headerHtml) {
  const html = buildCtcStatutoryDoc(filename, bodyHtml, headerHtml);
  await downloadHtmlAsDocx(filename.replace(/\s+/g, '_'), html);
}

// ── Share Transfer documents (Request Letter / Application for
// Registration / Form SH-4 / Acknowledgement) — same statutory class
// naming convention as the builders above (.co-header/.co-meta/.doc-title/
// .sig-block etc.), plus a .page-break rule since these four are the only
// documents that repeat one section per transfer row inside a single file
// (matching how the real attachments already batch multiple SH-4s/letters
// into one docx) and a table style for SH-4's grid layout.
function buildTransferStatutoryDoc(title, bodyHtml, headerHtml) {
  return `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
<head><meta charset="utf-8"><title>${title}</title>
<style>
  @page { size: A4 portrait; margin: 25mm 20mm 20mm 25mm;${headerHtml ? ' mso-header: h1; mso-header-margin: 10mm;' : ''} }
  body { font-family: "Plus Jakarta Sans", sans-serif; font-size: 11pt; color: #000000; }
  p { margin: 0; }
  .co-header { font-weight: bold; font-size: 13.5pt; text-align: center; text-transform: uppercase; line-height: 1.3; margin: 0 0 3pt 0; }
  .co-meta { font-weight: normal; font-size: 9.75pt; text-align: center; line-height: 1.3; margin: 0 0 2pt 0; }
  .co-meta.last { margin-bottom: 12pt; }
  .doc-title { font-weight: bold; font-size: 12.5pt; text-align: center; text-transform: uppercase; text-decoration: underline; line-height: 1.3; margin: 10pt 0; }
  .doc-provision { font-style: italic; font-weight: normal; font-size: 9.25pt; text-align: center; line-height: 1.2; margin: 0 0 10pt 0; }
  .body-narrative { font-weight: normal; font-size: 11pt; text-align: justify; line-height: 1.5; margin: 0 0 8pt 0; }
  .field-row { font-size: 11pt; line-height: 1.6; margin: 0 0 3pt 0; }
  .field-row b { font-weight: bold; }
  table { width: 100%; border-collapse: collapse; margin: 10pt 0; }
  td, th { border: 1pt solid #000; padding: 5pt 7pt; font-size: 9.75pt; vertical-align: top; }
  th { font-weight: bold; text-align: center; }
  .sig-block { page-break-inside: avoid; margin-top: 20pt; }
  .sig-for { font-weight: bold; font-size: 11pt; text-align: left; text-transform: uppercase; line-height: 1.3; margin: 0 0 30pt 0; }
  .sig-name-block { font-weight: bold; font-size: 10.5pt; text-align: left; line-height: 1.3; }
  .sig-name-block .sig-name { text-transform: uppercase; }
  .sig-meta { font-weight: bold; font-size: 10pt; text-align: left; line-height: 1.3; margin-top: 8pt; }
  .page-break { page-break-before: always; }
  .docx-header { mso-element: header; }
</style></head>
<body>${headerHtml ? `<div class="docx-header" id="h1">${headerHtml}</div>` : ''}${bodyHtml}</body></html>`;
}
async function downloadTransferStatutoryWord(filename, bodyHtml, headerHtml) {
  const html = buildTransferStatutoryDoc(filename, bodyHtml, headerHtml);
  await downloadHtmlAsDocx(filename.replace(/\s+/g, '_'), html);
}

// ═══════════════════════════════════════════════════════════════════════
// CONDITIONAL BLOCKS — a resolution can now word itself differently based
// on a choice the CS makes (e.g. Rent Agreement vs Authorization Letter).
// Syntax inside template_body:
//   {{#if field=="Option A"}}...text...{{#elif field=="Option B"}}...text...{{/if}}
// `#elif` can repeat for 3+ options. Whatever value the resolver picks
// becomes both the branch that gets kept AND the dropdown label shown to
// the CS — no separate "options list" needs to be configured anywhere.
// ═══════════════════════════════════════════════════════════════════════

// Scans a template for every conditional field and the option labels used
// in its branches — e.g. { proof_type: ['Rent Agreement', 'Authorization Letter'] }
function extractChoiceFields(templateBody) {
  const regex = /\{\{#(?:if|elif)\s+(\w+)\s*==\s*"([^"]+)"\}\}/g;
  const fields = {};
  let m;
  while ((m = regex.exec(templateBody || '')) !== null) {
    const [, field, value] = m;
    if (!fields[field]) fields[field] = [];
    if (!fields[field].includes(value)) fields[field].push(value);
  }
  return fields;
}

// Resolves every {{#if field=="A"}}...{{#elif field=="B"}}...{{/if}} block
// down to whichever branch matches the chosen value — before the plain
// {{token}} substitution runs. If nothing's been chosen yet, the block
// resolves to empty text (rather than showing raw template syntax).
function resolveConditionals(templateBody, values) {
  return (templateBody || '').replace(/\{\{#if\b[\s\S]*?\{\{\/if\}\}/g, (block) => {
    const parts = block.split(/\{\{#(?:if|elif)\s+(\w+)\s*==\s*"([^"]+)"\}\}|\{\{\/if\}\}/g);
    for (let i = 1; i < parts.length; i += 3) {
      const field = parts[i], val = parts[i + 1], branch = parts[i + 2] || '';
      if (field && values?.[field] === val) return branch;
    }
    return '';
  });
}

function extractPlaceholders(templateBody) {
  const matches = (templateBody || '').match(/\{\{(\w+)\}\}/g) || [];
  const plain = matches.map(m => m.replace(/[{}]/g, ''));
  const choiceFieldNames = Object.keys(extractChoiceFields(templateBody));
  return [...new Set([...plain, ...choiceFieldNames])];
}
function humanizeKey(key) {
  return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}
// Any placeholder whose name contains "date" is treated as a date field
// everywhere — the Event Details tab renders it as a real date picker
// instead of plain text, and fillTemplate formats it DD/MM/YYYY instead
// of the raw ISO value when substituting it into resolution text. Purely
// name-based, so this applies to every current and future resolution
// automatically — nothing hardcoded per event type.
function isDateKey(key) {
  return /date|dob|birth/i.test(key);
}
function formatDateForDoc(iso) {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}
// A native <input type="date"> fires onChange on every keystroke while
// the year is still being typed digit-by-digit — typing just the first
// "2" of "2026" already fires with year "0002". Filing & Tracking's
// auto-transition (below) must never treat that half-typed value as a
// real Approved/Filed date; only a plausible, fully-typed one counts.
function isPlausibleDate(dateStr) {
  const m = /^(\d{4})-\d{2}-\d{2}$/.exec(dateStr || '');
  if (!m) return false;
  const year = Number(m[1]);
  return year >= 2000 && year <= new Date().getFullYear() + 5;
}
// Converts a native <input type="time"> value ("HH:MM", 24-hour) into a
// human 12-hour label ("11:00 AM") for use in generated documents.
function formatTime12h(hhmm) {
  if (!hhmm) return '—';
  const [h, m] = hhmm.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return hhmm;
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${String(m).padStart(2, '0')} ${period}`;
}
function fillTemplate(templateBody, values) {
  const resolved = resolveConditionals(templateBody, values);
  return resolved.replace(/\{\{(\w+)\}\}/g, (match, key) => {
    const v = values?.[key];
    if (!v || !String(v).trim()) return match;
    return isDateKey(key) ? formatDateForDoc(v) : v;
  });
}
// Every {{token}} still literally present in a resolved/filled string —
// used to block "Mark Passed" (and warn before CTC download) with a
// precise list of what's still missing, instead of a vague message.
function findUnresolvedTokens(text) {
  return [...new Set((text.match(/\{\{(\w+)\}\}/g) || []).map(m => m.replace(/[{}]/g, '')))];
}
// Resolution Master stores each explanatory statement with its own leading
// "ITEM NO. [•]: <TITLE>" heading line (for standalone reference/printing),
// but the Notice document already renders "Item No. X: <Title>" above this
// block — so that stored heading line is dropped here to avoid a duplicate,
// unfilled-placeholder heading. {{tokens}} in the body are filled the same
// way resolution text is, and any leftover "Item No. [•]" self-reference in
// the body is stitched to the item's real serial number.
function renderExplanatoryStatement(rawText, itemNo, context) {
  if (!rawText) return '';
  const withoutHeading = rawText.replace(/^\s*ITEM NO\.\s*\[[^\]]*\]\s*:[^\n]*\n+/i, '');
  return fillTemplate(withoutHeading, context).replace(/\[•\]/g, String(itemNo));
}
// Picks the Statutory Auditor Master row this company's next auditor
// resolution should reference — the active one if there is one, else
// whoever ceased most recently. Mirrors AnnualFilings.js's identical helper.
function activeAuditorFor(auditors) {
  const list = auditors || [];
  const active = list.find(a => a.is_active);
  if (active) return active;
  return [...list].sort((a, b) => (b.cessation_date || '').localeCompare(a.cessation_date || ''))[0] || null;
}
// "Take Data" half of the Auditors-tab wiring — when one of the auditor-
// lifecycle resolutions is added to a case, pre-fill its field_values from
// whichever auditor is already on file, instead of a blank form every
// time. Which field gets filled depends on what that specific resolution
// is FOR: RES-AGM-006 (re-appointment) prefills the auditor being
// continued; RES-AUD-002/004 (casual vacancy) and RES-AUD-005/006
// (removal) prefill the OUTGOING auditor being replaced/removed — in both
// cases that's simply whoever the master's active row currently is (for
// RES-AUD-003, added only after RES-AUD-002 has already synced the
// incoming firm in as the new active-but-pending row, so "active" by then
// correctly means the incoming one being confirmed, not the outgoing one).
function auditorResolutionPrefill(resolutionCode, auditors) {
  const a = activeAuditorFor(auditors);
  if (!a) return {};
  switch (resolutionCode) {
    case 'RES-AGM-006':
      return { auditor_firm_name: a.firm_name || '', auditor_frn: a.frn || '' };
    case 'RES-AUD-002':
    case 'RES-AUD-004':
      return { outgoing_auditor_name: a.firm_name || '', outgoing_frn: a.frn || '' };
    case 'RES-AUD-003':
      return { incoming_auditor_name: a.firm_name || '', incoming_frn: a.frn || '' };
    case 'RES-AUD-005':
    case 'RES-AUD-006':
      return { auditor_to_be_removed_name: a.firm_name || '', auditor_frn: a.frn || '' };
    default:
      return {};
  }
}

// ═══════════════════════════════════════════════════════════════════════
// SHARE TRANSFER ROWS — the one event type whose data isn't a flat set of
// fields but a repeating line-item table (N transferors, each possibly to
// a different transferee). Rather than teaching the {{token}} engine a
// new loop/block syntax (touching every call site that already uses
// fillTemplate/extractPlaceholders across this file and ResolutionMaster),
// three PLAIN placeholders are kept auto-computed as regular eventData
// keys whenever eventData.transfers changes — every existing fillTemplate
// call site picks them up for free, no engine change needed:
//   {{transfers_table}}          — one line per row, plain text
//   {{total_shares_transferred}} — sum across all rows
//   {{transferee_names_list}}    — distinct transferee names, comma-joined
// ═══════════════════════════════════════════════════════════════════════
const TRANSFER_COMPUTED_KEYS = ['transfers_table', 'total_shares_transferred', 'transferee_names_list'];

function formatTransfersTable(transfers) {
  return (transfers || []).map(t => {
    const range = (t.distinctive_from && t.distinctive_to) ? `, Distinctive Nos. ${t.distinctive_from} to ${t.distinctive_to}` : '';
    const cert = t.certificate_no ? `, Certificate No. ${t.certificate_no}` : '';
    return `${t.transferor_name || 'Transferor'} (Folio No. ${t.transferor_folio || '—'}) — ${t.no_of_shares || 0} shares to ${t.transferee_name || 'Transferee'} (Folio No. ${t.transferee_folio || 'New'})${range}${cert}`;
  }).join('\n');
}
function computeTransferFields(transfers) {
  const list = transfers || [];
  const total = list.reduce((sum, t) => sum + (Number(t.no_of_shares) || 0), 0);
  const names = [...new Set(list.map(t => t.transferee_name).filter(Boolean))];
  return {
    transfers_table: formatTransfersTable(list),
    total_shares_transferred: total ? String(total) : '',
    transferee_names_list: names.join(', '),
  };
}

// ═══════════════════════════════════════════════════════════════════════
// NUMBERED-CLAUSE PLACEHOLDERS — a placeholder key listed here (e.g.
// {{altered_objects_clause_text}}, for an MOA objects-clause alteration)
// renders as a repeating "(i) / (ii) / (iii) …" clause list instead of a
// single flat text box, and its value gets auto-numbered on every edit —
// legal drafting for a multi-clause MOA amendment needs each clause on
// its own line, not typed by hand into one run-on paragraph. The raw
// rows live at eventData[`${key}__clauses`] (an array); the placeholder
// itself (eventData[key], what fillTemplate actually reads) is kept in
// sync with the formatted join on every change — no engine change
// needed, same trick TRANSFER_COMPUTED_KEYS uses above, just computed
// inline on edit instead of via a separate effect.
const NUMBERED_CLAUSE_KEYS = ['altered_objects_clause_text'];
const LOWER_ROMAN = ['', 'i', 'ii', 'iii', 'iv', 'v', 'vi', 'vii', 'viii', 'ix', 'x',
  'xi', 'xii', 'xiii', 'xiv', 'xv', 'xvi', 'xvii', 'xviii', 'xix', 'xx', 'xxi', 'xxii', 'xxiii', 'xxiv', 'xxv'];
function toLowerRoman(n) { return LOWER_ROMAN[n] || String(n); }
function formatNumberedClauses(clauses) {
  return (clauses || []).map(c => (c || '').trim()).filter(Boolean).map((c, i) => `(${toLowerRoman(i + 1)}) ${c}`).join('\n');
}
function NumberedClauseEditor({ clauses, onChange }) {
  const list = clauses && clauses.length ? clauses : [''];
  const update = (i, val) => onChange(list.map((c, idx) => idx === i ? val : c));
  const addClause = () => onChange([...list, '']);
  const removeClause = (i) => onChange(list.length > 1 ? list.filter((_, idx) => idx !== i) : ['']);
  return (
    <div className="space-y-2">
      {list.map((c, i) => (
        <div key={i} className="flex items-start gap-2">
          <span className="text-xs font-semibold text-gray-400 pt-2.5 w-9 flex-shrink-0 text-right">({toLowerRoman(i + 1)})</span>
          <textarea rows={2} value={c} onChange={e => update(i, e.target.value)} placeholder={`Clause (${toLowerRoman(i + 1)})…`}
            className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500" />
          {list.length > 1 && (
            <button type="button" onClick={() => removeClause(i)} title="Remove clause" className="text-gray-300 hover:text-red-500 pt-2.5">{Icon.trash}</button>
          )}
        </div>
      ))}
      <button type="button" onClick={addClause} className="text-xs font-semibold text-blue-600 hover:underline">+ Add Clause</button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// ICONS — minimal monoline set, matching the app's existing nav icon style
// ═══════════════════════════════════════════════════════════════════════
const Icon = {
  meeting: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
  form: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
  checklist: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>,
  event: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>,
  kebab: <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="1.8"/><circle cx="12" cy="12" r="1.8"/><circle cx="12" cy="19" r="1.8"/></svg>,
  eye: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>,
  trash: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>,
  back: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>,
};

function IconBadge({ accent, children }) {
  return (
    <span className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${accent.bg} ${accent.text}`}>
      {children}
    </span>
  );
}

// ── KEBAB MENU — a single ⋮ trigger opening a small dropdown of row
// actions. Rendered via a portal straight into document.body — the
// table card has overflow-hidden for its rounded corners, which would
// otherwise silently clip an absolutely-positioned dropdown before it's
// ever visible. Closes on outside click or after an action is chosen.
function KebabMenu({ actions }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  const toggleOpen = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 6, left: Math.max(8, rect.right - 150) });
    }
    setOpen(o => !o);
  };

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <>
      <button ref={btnRef} onClick={(e) => { e.stopPropagation(); toggleOpen(); }}
        style={{ width: 28, height: 28, borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
        {Icon.kebab}
      </button>
      {open && ReactDOM.createPortal(
        <div ref={menuRef} style={{ position: 'fixed', top: pos.top, left: pos.left, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.15)', minWidth: 150, zIndex: 9999, overflow: 'hidden' }}>
          {actions.map((a, i) => (
            <button key={i} onClick={(e) => { e.stopPropagation(); setOpen(false); a.onClick(); }}
              style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', border: 'none', background: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', color: a.danger ? '#b91c1c' : '#0f1f45', textAlign: 'left' }}
              onMouseEnter={e => e.currentTarget.style.background = a.danger ? '#fef2f2' : '#f8fafc'}
              onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
              {a.icon}{a.label}
            </button>
          ))}
        </div>,
        document.body
      )}
    </>
  );
}

// Colored circular step number, used in the wizard (1 → 2 → 3), with a
// short connecting line so it reads as a sequence rather than a list.
const STEP_ACCENTS = [ACCENTS.navy, ACCENTS.purple, ACCENTS.emerald];
function StepBadge({ n }) {
  const accent = STEP_ACCENTS[(n - 1) % STEP_ACCENTS.length];
  return (
    <span className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-bold ${accent.strong} ${accent.text}`}>
      {n}
    </span>
  );
}

function AlertModal({ message, title, variant = 'error', onClose }) {
  const colors = variant === 'error' ? { bg: '#fde8e8', text: '#b91c1c', btn: '#b91c1c' } : { bg: '#e6f4ea', text: '#166534', btn: '#166534' };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,31,69,0.35)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div className="modal-surface" style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 380, padding: 24, boxShadow: '0 24px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ width: 44, height: 44, borderRadius: 12, background: colors.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
          <span style={{ color: colors.text, fontWeight: 700, fontSize: 18 }}>{variant === 'error' ? '!' : '✓'}</span>
        </div>
        {title && <p className="modal-title" style={{ fontWeight: 700, fontSize: 15, color: '#0f1f45', margin: '0 0 6px' }}>{title}</p>}
        <p className="modal-body" style={{ fontSize: 13.5, color: '#475569', margin: 0, lineHeight: 1.5 }}>{message}</p>
        <button onClick={onClose} style={{ marginTop: 18, width: '100%', padding: '10px 0', borderRadius: 9, border: 'none', background: colors.btn, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>OK</button>
      </div>
    </div>
  );
}

function ConfirmModal({ message, title, confirmLabel = 'Confirm', onConfirm, onCancel }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,31,69,0.35)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div className="modal-surface" style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 400, padding: 24, boxShadow: '0 24px 60px rgba(0,0,0,0.2)' }}>
        {title && <p className="modal-title" style={{ fontWeight: 700, fontSize: 15, color: '#0f1f45', margin: '0 0 8px' }}>{title}</p>}
        <p className="modal-body" style={{ fontSize: 13.5, color: '#475569', margin: 0, lineHeight: 1.5 }}>{message}</p>
        <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
          <button onClick={onCancel} style={{ flex: 1, padding: '10px 0', borderRadius: 9, border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
          <button onClick={onConfirm} style={{ flex: 1, padding: '10px 0', borderRadius: 9, border: 'none', background: '#0f1f45', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

// A register entry already exists for this company/date — ask which way
// to resolve it, rather than silently picking one.
function RegisterSyncChoiceModal({ message, title, onUpdate, onCreateNew, onCancel }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,31,69,0.35)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div className="modal-surface" style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 420, padding: 24, boxShadow: '0 24px 60px rgba(0,0,0,0.2)' }}>
        {title && <p className="modal-title" style={{ fontWeight: 700, fontSize: 15, color: '#0f1f45', margin: '0 0 8px' }}>{title}</p>}
        <p className="modal-body" style={{ fontSize: 13.5, color: '#475569', margin: 0, lineHeight: 1.5 }}>{message}</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 20 }}>
          <button onClick={onUpdate} style={{ padding: '10px 0', borderRadius: 9, border: 'none', background: '#0f1f45', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Update Existing Record</button>
          <button onClick={onCreateNew} style={{ padding: '10px 0', borderRadius: 9, border: '1px solid #e2e8f0', background: '#fff', color: '#0f1f45', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Record New Row</button>
          <button onClick={onCancel} style={{ padding: '8px 0', borderRadius: 9, border: 'none', background: 'transparent', color: '#94a3b8', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

const STAGE_ORDER = ['Draft', 'In Progress', 'Filed', 'Approved', 'Synced'];
const STATUS_COLORS = {
  'Draft':       'bg-gray-100 text-gray-600',
  'In Progress': 'bg-blue-100 text-blue-700',
  'Filed':       'bg-orange-100 text-orange-700',
  'Approved':    'bg-green-100 text-green-700',
  'Synced':      'bg-emerald-100 text-emerald-700',
  'Rejected':    'bg-red-100 text-red-700',
};
const ATTACHMENT_STATUS_COLORS = {
  'Pending':        'bg-amber-100 text-amber-700',
  'Collected':      'bg-blue-100 text-blue-700',
  'Completed':      'bg-green-100 text-green-700',
  'Not Applicable': 'bg-gray-100 text-gray-500',
};

function computeCaseStatus(resolutions, filings, syncedAt) {
  const filingsApproved = filings.length > 0 && filings.every(f => f.status === 'Approved');
  const filingsFiled = filings.length > 0 && filings.every(f => ['Filed', 'Approved'].includes(f.status));
  const anyRejected = filings.some(f => f.status === 'Rejected');
  const anyResolutionPassed = resolutions.some(r => r.status === 'Passed');

  if (syncedAt) return 'Synced';
  if (filingsApproved) return 'Approved';
  if (anyRejected) return 'Rejected';
  if (filingsFiled) return 'Filed';
  if (anyResolutionPassed) return 'In Progress';
  return 'Draft';
}

// ═══════════════════════════════════════════════════════════════════════
// QUORUM — Board Meeting: 1/3 of total directors, or 2, whichever is
// higher (Section 174(1)). General Meeting: 2 members for a private
// company; a public company scales by total member count (Section 103).
// ═══════════════════════════════════════════════════════════════════════
function boardQuorum(totalDirectors) {
  if (!totalDirectors) return 2;
  return Math.max(2, Math.ceil(totalDirectors / 3));
}
function generalMeetingQuorum(companyType, totalMembers) {
  const isPublic = /public/i.test(companyType || '');
  if (!isPublic) return 2;
  if (totalMembers <= 1000) return 5;
  if (totalMembers <= 5000) return 15;
  return 30;
}

// ═══════════════════════════════════════════════════════════════════════
// VOTING (Section 114) — evaluated only from votes actually cast (for +
// against); abstentions never enter the denominator. Board and General
// Meeting resolutions use the same for-vs-against math — only Special
// Resolutions need the 3x supermajority. Voting weight differs by type:
// one vote per director for Board Resolutions, but share-weighted (per
// Section 47) for Ordinary/Special — a member's vote counts as their
// paid-up shareholding, not a flat headcount.
// ═══════════════════════════════════════════════════════════════════════
function voteWeight(isBM, personName, shareholders) {
  if (isBM) return 1;
  const sh = shareholders.find(s => s.member_name === personName);
  return sh ? Number(sh.no_of_shares) || 0 : 0;
}
function tallyVotes(votes, presentRoster, isBM, shareholders) {
  let forWeight = 0, againstWeight = 0, abstainWeight = 0;
  presentRoster.forEach(name => {
    const w = voteWeight(isBM, name, shareholders);
    const v = votes[name];
    if (v === 'for') forWeight += w;
    else if (v === 'against') againstWeight += w;
    else if (v === 'abstain') abstainWeight += w;
  });
  return { forWeight, againstWeight, abstainWeight };
}
// null = not enough votes cast yet to determine an outcome; otherwise
// true (passes) / false (fails) against the threshold for this type.
function evaluateResolution(resolutionType, forWeight, againstWeight) {
  if (forWeight + againstWeight <= 0) return null;
  if (resolutionType === 'SPECIAL') return forWeight >= 3 * againstWeight;
  return forWeight > againstWeight;
}

// ═══════════════════════════════════════════════════════════════════════
// NOTICE PERIOD — "clear days" excludes BOTH the day the notice was sent
// and the day of the meeting itself (the count is of the days strictly
// between them). BM needs 7, GM needs 21 (Sec 173(3) / Sec 101(1)).
// Known simplification: doesn't add the extra 48 hours General Clauses
// Act practice allows for notice sent by post — we don't currently track
// notice mode (email vs post) to know when that applies.
// ═══════════════════════════════════════════════════════════════════════
// BM (Sec 173(3)) is a flat minimum — plain day count, no exclusion.
// GM (Sec 101(1)) is "21 CLEAR days" — excludes both the day notice was
// sent and the day of the meeting itself, hence the extra -1.
function noticeGapDays(type, noticeDate, meetingDate) {
  if (!noticeDate || !meetingDate) return null;
  const raw = Math.round((new Date(meetingDate) - new Date(noticeDate)) / 86400000);
  return type === 'BM' ? raw : raw - 1;
}
function requiredNoticeDays(type) { return type === 'BM' ? 7 : 21; }

// ═══════════════════════════════════════════════════════════════════════
// DARK MODE — full coverage: surfaces, text ladder, borders, every accent
// tint used above, and — critically — raw form controls (input/select/
// textarea don't inherit bg-white from a parent, so they need their own
// rule or they'd stay white-on-black-text inside a dark card).
// ═══════════════════════════════════════════════════════════════════════
const DARK_STYLE_OVERRIDES = `
  .dark [class~="bg-white"] { background-color: #1e293b !important; }
  .dark [class~="bg-gray-50"] { background-color: #0f172a !important; }
  .dark [class~="bg-gray-50/50"] { background-color: rgba(15,23,42,0.6) !important; }
  .dark [class~="bg-gray-50/60"] { background-color: rgba(15,23,42,0.6) !important; }
  .dark [class~="bg-gray-100"] { background-color: #334155 !important; }
  .dark [class~="hover:bg-gray-50"]:hover { background-color: #334155 !important; }
  .dark [class~="hover:bg-gray-50/60"]:hover { background-color: #334155 !important; }
  .dark [class~="hover:text-gray-600"]:hover { color: #e2e8f0 !important; }

  .dark [class~="text-gray-300"] { color: #475569 !important; }
  .dark [class~="text-gray-400"] { color: #64748b !important; }
  .dark [class~="text-gray-500"] { color: #94a3b8 !important; }
  .dark [class~="text-gray-600"] { color: #cbd5e1 !important; }
  .dark [class~="text-gray-700"] { color: #e2e8f0 !important; }
  .dark [class~="text-gray-800"] { color: #f1f5f9 !important; }
  .dark [class*="text-[#0f1f45]"] { color: #f1f5f9 !important; }

  .dark [class~="border-gray-50"] { border-color: #334155 !important; }
  .dark [class~="border-gray-100"] { border-color: #334155 !important; }
  .dark [class~="border-gray-200"] { border-color: #475569 !important; }
  .dark [class~="divide-gray-50"] > * + * { border-color: #334155 !important; }

  .dark [class~="bg-blue-50"] { background-color: rgba(30,58,138,0.3) !important; }
  .dark [class~="bg-blue-100"] { background-color: rgba(30,64,175,0.4) !important; }
  .dark [class~="text-blue-600"] { color: #60a5fa !important; }
  .dark [class~="text-blue-700"] { color: #93c5fd !important; }
  .dark [class~="border-l-blue-400"] { border-color: #3b82f6 !important; }
  .dark [class~="border-blue-200"] { border-color: rgba(96,165,250,0.4) !important; }

  .dark [class~="bg-purple-50"] { background-color: rgba(88,28,135,0.3) !important; }
  .dark [class~="bg-purple-100"] { background-color: rgba(107,33,168,0.4) !important; }
  .dark [class~="text-purple-600"] { color: #c084fc !important; }
  .dark [class~="text-purple-700"] { color: #d8b4fe !important; }
  .dark [class~="border-l-purple-400"] { border-color: #a78bfa !important; }

  .dark [class~="bg-cyan-50"] { background-color: rgba(22,78,99,0.3) !important; }
  .dark [class~="bg-cyan-100"] { background-color: rgba(14,116,144,0.4) !important; }
  .dark [class~="text-cyan-600"] { color: #22d3ee !important; }
  .dark [class~="text-cyan-700"] { color: #67e8f9 !important; }
  .dark [class~="border-l-cyan-400"] { border-color: #22d3ee !important; }

  .dark [class~="bg-emerald-50"] { background-color: rgba(6,78,59,0.3) !important; }
  .dark [class~="bg-emerald-100"] { background-color: rgba(6,95,70,0.4) !important; }
  .dark [class~="text-emerald-600"] { color: #34d399 !important; }
  .dark [class~="text-emerald-700"] { color: #6ee7b7 !important; }
  .dark [class~="border-l-emerald-400"] { border-color: #34d399 !important; }
  .dark [class~="bg-green-100"] { background-color: rgba(6,78,59,0.4) !important; }
  .dark [class~="text-green-600"] { color: #34d399 !important; }
  .dark [class~="text-green-700"] { color: #6ee7b7 !important; }

  .dark [class~="bg-amber-50"] { background-color: rgba(120,53,15,0.3) !important; }
  .dark [class~="bg-amber-100"] { background-color: rgba(146,64,14,0.4) !important; }
  .dark [class~="text-amber-600"] { color: #fbbf24 !important; }
  .dark [class~="text-amber-700"] { color: #fcd34d !important; }
  .dark [class~="border-l-amber-400"] { border-color: #fbbf24 !important; }

  .dark [class~="bg-orange-50"] { background-color: rgba(124,45,18,0.3) !important; }
  .dark [class~="bg-orange-100"] { background-color: rgba(154,52,18,0.4) !important; }
  .dark [class~="text-orange-600"] { color: #fb923c !important; }
  .dark [class~="text-orange-700"] { color: #fdba74 !important; }
  .dark [class~="border-l-orange-400"] { border-color: #fb923c !important; }

  .dark [class~="bg-red-50"] { background-color: rgba(127,29,29,0.3) !important; }
  .dark [class~="bg-red-100"] { background-color: rgba(153,27,27,0.4) !important; }
  .dark [class~="text-red-500"] { color: #f87171 !important; }
  .dark [class~="text-red-600"] { color: #f87171 !important; }
  .dark [class~="text-red-700"] { color: #fca5a5 !important; }
  .dark [class~="border-l-red-400"] { border-color: #f87171 !important; }
  .dark [class~="border-red-100"] { border-color: rgba(127,29,29,0.5) !important; }
  .dark [class~="border-red-200"] { border-color: rgba(153,27,27,0.5) !important; }

  .dark [class~="border-l-gray-300"] { border-color: #64748b !important; }

  /* Raw form controls — these don't inherit a card's bg-white, so without
     this they stay white-on-black-text even inside a dark-themed card. */
  .dark input, .dark select, .dark textarea {
    background-color: #0f172a !important;
    color: #f1f5f9 !important;
    border-color: #475569 !important;
  }
  .dark input::placeholder, .dark textarea::placeholder { color: #64748b !important; }
  .dark input:focus, .dark select:focus, .dark textarea:focus { border-color: #60a5fa !important; }
  .dark select option { background-color: #1e293b; color: #f1f5f9; }

  /* Tables */
  .dark table thead tr { background-color: #0f172a !important; }

  /* Modal surfaces forced explicitly via a class rather than relying on
     descendant scoping, since modals can render outside the themed wrapper. */
  .dark .modal-surface { background-color: #1e293b !important; box-shadow: 0 24px 60px rgba(0,0,0,0.5) !important; }
  .dark .modal-title { color: #f1f5f9 !important; }
  .dark .modal-body { color: #cbd5e1 !important; }
`;

function fetchJson(url, opts) {
  const token = localStorage.getItem('token');
  return fetch(`${BASE}${url}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(opts?.headers || {}) },
  }).then(async r => {
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(data.message || 'Request failed');
    return data;
  });
}

// ═══════════════════════════════════════════════════════════════════════
// NEW EVENT FILING WIZARD
// Company → every Event this meeting round triggers (multi-select, each
// event's resolutions optional/uncheck-able) → fill in the meeting
// details each needs, once → all cases get created together, all linked
// to the same shared meeting record(s).
// ═══════════════════════════════════════════════════════════════════════
function NewEventFilingWizard({ onClose, onCreated, onBackToList, onSwitchToEvents }) {
  const [companies, setCompanies] = useState([]);
  const [events, setEvents] = useState([]);
  const [resolutionsById, setResolutionsById] = useState({}); // for showing real titles/codes instead of "Resolution #id"
  const [loading, setLoading] = useState(true);
  const [companyId, setCompanyId] = useState('');
  const [eventSearch, setEventSearch] = useState('');

  // { [eventId]: { checked: bool, resolutions: { [resolutionId]: bool } } }
  const [selections, setSelections] = useState({});
  // Notice-of-meeting data only — chairperson/attendance is recorded after
  // the meeting is actually held, on the case detail screen, not here.
  // { BM: {meeting_date, meeting_time, venue, venue_mode}, EGM: {...}, AGM: {...} }
  const [meetingsData, setMeetingsData] = useState({});
  // 'new' (default) or 'existing' per meeting type — lets this case share
  // an already-recorded meeting (e.g. the Annual Filing's Board Meeting)
  // instead of always minting a fresh one, which is what caused two
  // separate Board Meetings for one physical meeting in the first place.
  // Same "Use Existing Meeting" concept AnnualFilings.js's wizard already
  // has — offers only Statutory-Register-recorded meetings (rows with
  // event_filing_meeting_id set), not every draft mca_meetings row.
  const [meetingModeByType, setMeetingModeByType] = useState({});
  const [existingMeetingsByType, setExistingMeetingsByType] = useState({ BM: [], AGM: [], EGM: [] });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // Holds the batch-create result — the modal doesn't close immediately,
  // it first offers the consolidated documents to download.
  const [batchResult, setBatchResult] = useState(null);

  useEffect(() => {
    Promise.all([
      fetchJson('/api/mca/companies'),
      fetchJson('/api/mca/events?active=true'),
      fetchJson('/api/mca/resolutions'),
    ]).then(([c, e, r]) => {
      setCompanies(Array.isArray(c) ? c : []);
      setEvents(Array.isArray(e) ? e : []);
      setResolutionsById(Object.fromEntries((Array.isArray(r) ? r : []).map(res => [res.id, res])));
    })
      .catch(() => { setCompanies([]); setEvents([]); })
      .finally(() => setLoading(false));
  }, []);

  const filteredEvents = events.filter(e => {
    const q = eventSearch.toLowerCase();
    return !q || e.event_name?.toLowerCase().includes(q) || e.category?.toLowerCase().includes(q) || e.event_tag?.toLowerCase().includes(q);
  });

  const toggleEvent = (event) => {
    setSelections(prev => {
      const current = prev[event.id];
      if (current?.checked) {
        const { [event.id]: _drop, ...rest } = prev;
        return rest;
      }
      // Newly ticked event — default all its resolutions to checked.
      const resolutions = {};
      (event.resolution_refs || []).forEach(r => { resolutions[r.resolution_id] = true; });
      return { ...prev, [event.id]: { checked: true, resolutions } };
    });
  };

  const toggleResolution = (eventId, resolutionId) => {
    setSelections(prev => ({
      ...prev,
      [eventId]: { ...prev[eventId], resolutions: { ...prev[eventId].resolutions, [resolutionId]: !prev[eventId].resolutions[resolutionId] } },
    }));
  };

  // Whichever events + resolutions are currently ticked tells us which
  // meeting types (BM/AGM/EGM) are actually needed.
  const neededMeetingTypes = [...new Set(
    Object.entries(selections).flatMap(([eventId, sel]) => {
      if (!sel.checked) return [];
      const event = events.find(e => String(e.id) === String(eventId));
      return (event?.resolution_refs || [])
        .filter(r => sel.resolutions[r.resolution_id])
        .map(r => r.meeting_type);
    })
  )];

  const updateMeeting = (type, field, value) => setMeetingsData(prev => ({ ...prev, [type]: { ...prev[type], [field]: value } }));

  const selectedCompany = companies.find(c => String(c.id) === String(companyId));

  // Existing-meeting options for this company, pulled from the Statutory
  // Register (same source/filter AnnualFilings.js's wizard uses) — only
  // meetings someone actually synced to the register, keyed by type.
  useEffect(() => {
    if (!companyId) { setExistingMeetingsByType({ BM: [], AGM: [], EGM: [] }); return; }
    Promise.all([
      fetchJson(`/api/mca/companies/${companyId}/board-meetings`),
      fetchJson(`/api/mca/companies/${companyId}/general-meetings`),
    ]).then(([bmRows, gmRows]) => {
      const toOption = m => ({ id: m.event_filing_meeting_id, meeting_date: m.meeting_date, venue: m.venue });
      setExistingMeetingsByType({
        BM: (bmRows || []).filter(m => m.event_filing_meeting_id).map(toOption),
        AGM: (gmRows || []).filter(m => m.event_filing_meeting_id && m.meeting_type === 'AGM').map(toOption),
        EGM: (gmRows || []).filter(m => m.event_filing_meeting_id && m.meeting_type === 'EGM').map(toOption),
      });
    }).catch(() => setExistingMeetingsByType({ BM: [], AGM: [], EGM: [] }));
  }, [companyId]);

  // Whenever a meeting type becomes newly needed, pre-fill its venue
  // with the company's registered office — still fully editable if the
  // meeting is actually happening somewhere else (e.g. video conference).
  const neededMeetingTypesKey = neededMeetingTypes.join(',');
  useEffect(() => {
    if (!selectedCompany) return;
    setMeetingsData(prev => {
      let changed = false;
      const next = { ...prev };
      neededMeetingTypes.forEach(type => {
        if (!next[type]) {
          next[type] = { venue: selectedCompany.registered_office || '', venue_mode: 'physical' };
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [neededMeetingTypesKey, selectedCompany?.id]);

  const selectedEventCount = Object.values(selections).filter(s => s.checked).length;

  const handleCreate = async () => {
    if (!companyId) { setError('Select a company'); return; }
    if (selectedEventCount === 0) { setError('Select at least one event'); return; }
    for (const type of neededMeetingTypes) {
      if (meetingModeByType[type] === 'existing') {
        if (!meetingsData[type]?.meeting_id) { setError(`Pick an existing ${type} meeting, or switch to "Create New"`); return; }
      } else if (!meetingsData[type]?.meeting_date) {
        setError(`${type} meeting date is required`); return;
      } else {
        const gap = noticeGapDays(type, meetingsData[type]?.notice_date, meetingsData[type].meeting_date);
        if (gap !== null && gap < 0) { setError(`${type} Meeting Date can't be before Notice Sent date`); return; }
      }
    }

    setSaving(true);
    setError('');
    try {
      const payload = {
        company_id: companyId,
        meetings: neededMeetingTypes.map(type => meetingModeByType[type] === 'existing'
          ? { meeting_type: type, meeting_id: meetingsData[type]?.meeting_id }
          : { meeting_type: type, ...meetingsData[type] }),
        selections: Object.entries(selections).filter(([, s]) => s.checked).map(([eventId, s]) => ({
          event_id: Number(eventId),
          applicable_resolution_ids: Object.entries(s.resolutions).filter(([, v]) => v).map(([id]) => Number(id)),
        })),
      };
      const result = await fetchJson('/api/mca/event-filings/batch', { method: 'POST', body: JSON.stringify(payload) });
      setBatchResult(result); // show the success/download screen, don't call onCreated yet
    } catch (e) {
      setError(e.message || 'Could not create case');
    } finally {
      setSaving(false);
    }
  };

  // ── SUCCESS SCREEN — consolidated Notice/Agenda + Master Minutes,
  // one document PER MEETING (BM and EGM are separate documents, since
  // they're legally distinct meetings — but every agenda item within the
  // same meeting is consolidated into one document). ──────────────────
  if (batchResult) {
    const cases = batchResult.cases || [];
    const meetings = batchResult.meetings || [];

    const agendaFor = (meetingId) => cases.flatMap(c => (c.resolutions || []).filter(r => r.meeting_id === meetingId).map(r => ({ caseTitle: c.title, resolutionTitle: r.resolution_title, resolutionType: r.resolution_type })));

    const downloadConsolidatedNotice = (meeting) => {
      const items = agendaFor(meeting.id);
      const html = `
        <h1>${selectedCompany?.company_name || ''}</h1>
        <h2>Notice of ${meeting.meeting_type === 'BM' ? 'Board Meeting' : meeting.meeting_type}</h2>
        <table><tbody>
          <tr><td>Date</td><td>${meeting.meeting_date ? new Date(meeting.meeting_date).toLocaleDateString('en-IN') : '—'}</td></tr>
          <tr><td>Time</td><td>${formatTime12h(meeting.meeting_time)}</td></tr>
          <tr><td>Venue</td><td>${meeting.venue || '—'}</td></tr>
          <tr><td>Chairperson</td><td>${meeting.chairperson || '—'}</td></tr>
        </tbody></table>
        <p class="clause-heading" style="margin-top:14pt;">Agenda</p>
        <ol>${items.map(i => `<li>${i.resolutionTitle}</li>`).join('')}</ol>
        <p style="margin-top:24pt;">By Order of the Board<br/>For ${selectedCompany?.company_name || ''}</p>
      `;
      downloadWord(`Notice ${meeting.meeting_type} - ${selectedCompany?.company_name}`, html);
    };

    // Minutes were removed from this screen — at case-creation time the
    // meeting hasn't actually been held yet, so there's nothing to record
    // minutes of. Only the Notice belongs here; Minutes live on the
    // Documents tab once the meeting's actually happened and been voted on.

    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,31,69,0.35)', zIndex: 200, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '24px 16px', overflowY: 'auto' }}>
        <div className="bg-white modal-surface rounded-2xl w-full shadow-2xl" style={{ maxWidth: 640, marginBottom: 24 }}>
          <div className="px-6 py-5 border-b border-gray-100 relative overflow-hidden">
            <p className="text-base font-bold text-[#0f1f45]">✓ {cases.length} Case{cases.length !== 1 ? 's' : ''} Created</p>
            <p className="text-xs text-gray-400 mt-0.5">Download consolidated documents for each meeting below.</p>
            <div className="absolute bottom-0 left-0 right-0 h-1" style={{ background: 'linear-gradient(90deg, #0f1f45, #7c3aed, #0891b2, #059669, #FF8400)' }} />
          </div>
          <div className="px-6 py-5 space-y-4">
            {meetings.map(m => {
              const accent = m.meeting_type === 'BM' ? ACCENTS.navy : m.meeting_type === 'AGM' ? ACCENTS.amber : ACCENTS.purple;
              return (
                <div key={m.id} className={`rounded-lg border border-gray-200 p-4 border-l-4 ${accent.border}`}>
                  <p className={`text-sm font-semibold mb-2 ${accent.text}`}>{m.meeting_type} — {m.meeting_date ? new Date(m.meeting_date).toLocaleDateString('en-IN') : ''}</p>
                  <p className="text-xs text-gray-500 mb-3">{agendaFor(m.id).length} agenda item{agendaFor(m.id).length !== 1 ? 's' : ''}</p>
                  <div className="flex gap-2">
                    <button onClick={() => downloadConsolidatedNotice(m)} className="px-3 py-2 rounded-lg border border-gray-200 text-gray-600 text-xs font-semibold">📄 Notice & Agenda (Word)</button>
                  </div>
                </div>
              );
            })}
            <div className="pt-2">
              <p className="text-xs font-semibold text-gray-600 mb-2">Cases created:</p>
              <div className="space-y-1">
                {cases.map(c => <p key={c.id} className="text-xs text-gray-500 flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />{c.title}</p>)}
              </div>
            </div>
          </div>
          <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
            <button onClick={() => onBackToList()}
              className="group pl-2 pr-5 py-2 rounded-full bg-blue-50 hover:bg-blue-100 text-[#0f1f45] text-sm font-semibold flex items-center gap-2.5 transition-colors">
              <span className="w-7 h-7 rounded-full bg-white shadow-sm flex items-center justify-center text-blue-600 transition-transform duration-200 group-hover:-translate-x-1">
                {Icon.back}
              </span>
              Back to Event Filings
            </button>
            {cases.length === 1 && (
              <button onClick={() => onCreated(cases)} className="px-5 py-2.5 rounded-lg bg-[#0f1f45] text-white text-sm font-semibold">View Case</button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,31,69,0.35)', zIndex: 200, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '24px 16px', overflowY: 'auto' }}>
      <div className="bg-white modal-surface rounded-2xl w-full shadow-2xl" style={{ maxWidth: 780, marginBottom: 24 }}>
        <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
          <div>
            <p className="text-base font-bold text-[#0f1f45]">New Event Filing</p>
            <p className="text-xs text-gray-400 mt-0.5">One meeting round can trigger one or more events — tick everything that applies.</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">✕</button>
        </div>

        <div className="px-6 py-5 space-y-6 max-h-[70vh] overflow-y-auto">
          {error && <div className="bg-red-50 border border-red-100 text-red-600 text-xs rounded-lg px-3 py-2">{error}</div>}

          <div>
            <label className="flex items-center gap-2 text-xs font-semibold text-gray-600 mb-1.5"><StepBadge n={1} />Company *</label>
            <select value={companyId} onChange={e => setCompanyId(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-blue-500">
              <option value="">{loading ? 'Loading…' : 'Select company'}</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.company_name} {c.cin_llpin ? `(${c.cin_llpin})` : ''}</option>)}
            </select>
          </div>

          <div>
            <label className="flex items-center gap-2 text-xs font-semibold text-gray-600 mb-1.5">
              <StepBadge n={2} />Events <span className="font-normal text-gray-400">— tick every one that applies to this round</span>
            </label>
            <input value={eventSearch} onChange={e => setEventSearch(e.target.value)} placeholder="Search events…"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm mb-2 focus:outline-none focus:border-blue-500" />
            {!loading && events.length === 0 ? (
              <div className="border border-dashed border-gray-200 rounded-lg p-6 text-center">
                <p className="text-sm text-gray-500 mb-2">No events created yet.</p>
                <button onClick={() => { onClose(); onSwitchToEvents(); }} className="px-4 py-2 rounded-lg bg-[#0f1f45] text-white text-xs font-semibold">
                  Create an Event →
                </button>
              </div>
            ) : (
              <div className="border border-gray-200 rounded-lg divide-y divide-gray-50 max-h-72 overflow-y-auto">
                {filteredEvents.map(e => {
                  const sel = selections[e.id];
                  return (
                    <div key={e.id}>
                      <button onClick={() => toggleEvent(e)} className={`w-full text-left px-3 py-2.5 hover:bg-gray-50 flex items-start gap-2.5 ${sel?.checked ? 'bg-blue-50' : ''}`}>
                        <span className={`mt-0.5 w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center ${sel?.checked ? 'bg-[#0f1f45] border-[#0f1f45] text-white' : 'border-gray-300'}`}>
                          {sel?.checked && '✓'}
                        </span>
                        <span>
                          <p className="text-sm font-semibold text-gray-800">{e.event_name}</p>
                          <p className="text-[11px] text-gray-400 mt-0.5">{e.category} · Forms: {(e.form_sequence || []).map(f => f.form).join(', ') || '—'}</p>
                        </span>
                      </button>
                      {sel?.checked && (e.resolution_refs || []).length > 0 && (
                        <div className="pl-9 pr-3 pb-3 space-y-1.5">
                          <p className="text-[11px] text-gray-400">Applicable resolutions (uncheck any that don't apply):</p>
                          {(e.resolution_refs || []).map(r => {
                            const accent = r.meeting_type === 'BM' ? ACCENTS.navy : r.meeting_type === 'AGM' ? ACCENTS.amber : ACCENTS.purple;
                            const resolution = resolutionsById[r.resolution_id];
                            return (
                              <label key={r.resolution_id} className="flex items-center gap-2 text-xs">
                                <input type="checkbox" checked={!!sel.resolutions[r.resolution_id]} onChange={() => toggleResolution(e.id, r.resolution_id)} />
                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${accent.bg} ${accent.text}`}>{r.meeting_type}</span>
                                <span className="text-gray-600">
                                  {resolution ? resolution.resolution_title : `Resolution #${r.resolution_id} (not found — may have been deleted)`}
                                  {resolution && <span className="text-gray-400 ml-1 font-mono text-[10px]">{resolution.resolution_code}</span>}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
                {filteredEvents.length === 0 && <div className="px-3 py-6 text-center text-xs text-gray-400">No events match your search</div>}
              </div>
            )}
          </div>

          {neededMeetingTypes.length > 0 && (
            <div>
              <label className="flex items-center gap-2 text-xs font-semibold text-gray-600 mb-1.5">
                <StepBadge n={3} />Notice of Meeting <span className="font-normal text-gray-400">— the meeting hasn't happened yet, so only what goes into the notice is needed here</span>
              </label>
              <div className="space-y-3">
                {neededMeetingTypes.map(type => {
                  const accent = type === 'BM' ? ACCENTS.navy : type === 'AGM' ? ACCENTS.amber : ACCENTS.purple;
                  const isVC = meetingsData[type]?.venue_mode === 'vc';
                  const noticeDate = meetingsData[type]?.notice_date;
                  const meetingDate = meetingsData[type]?.meeting_date;
                  const requiredDays = requiredNoticeDays(type);
                  const gapDays = noticeGapDays(type, noticeDate, meetingDate);
                  const isShort = gapDays !== null && gapDays < requiredDays;
                  const mode = meetingModeByType[type] || 'new';
                  const existingOptions = existingMeetingsByType[type] || [];
                  const typeLabel = type === 'BM' ? 'Board Meeting' : type === 'AGM' ? 'Annual General Meeting' : 'Extraordinary General Meeting';
                  return (
                    <div key={type} className={`rounded-lg border border-gray-200 p-3 border-l-4 ${accent.border}`}>
                      <p className={`text-xs font-semibold mb-2 ${accent.text}`}>{typeLabel}</p>
                      <div className="flex gap-1.5 mb-2.5">
                        {['new', 'existing'].map(m => (
                          <button key={m} type="button"
                            onClick={() => setMeetingModeByType(prev => ({ ...prev, [type]: m }))}
                            className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border ${mode === m ? 'bg-[#0f1f45] text-white border-[#0f1f45]' : 'bg-white text-gray-500 border-gray-200'}`}>
                            {m === 'existing' ? `Use Existing ${type}` : `Create New ${type}`}
                          </button>
                        ))}
                      </div>
                      {mode === 'existing' ? (
                        <div>
                          <label className="block text-[10px] text-gray-400 mb-0.5">Select meeting — same physical meeting another case (Event or Annual Filing) already recorded</label>
                          <select value={meetingsData[type]?.meeting_id || ''} onChange={ev => updateMeeting(type, 'meeting_id', ev.target.value ? Number(ev.target.value) : null)}
                            className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-blue-500">
                            <option value="">Select meeting…</option>
                            {existingOptions.map(m => (
                              <option key={m.id} value={m.id}>{m.meeting_date ? new Date(m.meeting_date).toLocaleDateString('en-IN') : '—'} — {m.venue || 'Venue TBD'}</option>
                            ))}
                          </select>
                          {existingOptions.length === 0 && (
                            <p className="text-[10px] text-gray-400 mt-1">No {typeLabel} recorded in the Statutory Register for this company yet — sync one from an existing case's Documents tab first, or switch to "Create New {type}".</p>
                          )}
                        </div>
                      ) : (
                        <>
                          <div className="grid grid-cols-3 gap-2 mb-1">
                            <div>
                              <label className="block text-[10px] text-gray-400 mb-0.5">Notice Sent</label>
                              <input type="date" value={noticeDate || ''} onChange={ev => updateMeeting(type, 'notice_date', ev.target.value)}
                                className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-blue-500" />
                            </div>
                            <div>
                              <label className="block text-[10px] text-gray-400 mb-0.5">Meeting Date</label>
                              <input type="date" min={noticeDate || undefined} value={meetingDate || ''} onChange={ev => updateMeeting(type, 'meeting_date', ev.target.value)}
                                className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-blue-500" />
                            </div>
                            <div>
                              <label className="block text-[10px] text-gray-400 mb-0.5">Time</label>
                              <input type="time" value={meetingsData[type]?.meeting_time || ''} onChange={ev => updateMeeting(type, 'meeting_time', ev.target.value)}
                                className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-blue-500" />
                            </div>
                          </div>
                          {/* A negative gap means the meeting date is
                              before the notice was sent — impossible, not
                              a "shorter notice" scenario (that only
                              covers notice given late but still before
                              the meeting) — so it gets its own hard error. */}
                          {gapDays !== null && gapDays < 0 && (
                            <p className="text-[10px] mb-2 text-red-600 font-semibold">⚠ Meeting Date can't be before Notice Sent date — check the dates above.</p>
                          )}
                          {gapDays !== null && gapDays >= 0 && (
                            <p className={`text-[10px] mb-2 ${isShort ? 'text-amber-600 font-semibold' : 'text-gray-400'}`}>
                              {gapDays} day{gapDays !== 1 ? 's' : ''}{type !== 'BM' ? ' clear' : ''} notice {isShort ? `— short of the ${requiredDays}-day minimum. Shorter notice consent will be required (available on the Documents tab once the case is created).` : `— meets the ${requiredDays}-day minimum.`}
                            </p>
                          )}
                          <div className="flex items-center gap-1.5 mb-2">
                            <button type="button" onClick={() => { updateMeeting(type, 'venue_mode', 'physical'); updateMeeting(type, 'venue', selectedCompany?.registered_office || ''); }}
                              className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border ${!isVC ? 'bg-[#0f1f45] text-white border-[#0f1f45]' : 'bg-white text-gray-500 border-gray-200'}`}>Registered Office / Physical</button>
                            <button type="button" onClick={() => { updateMeeting(type, 'venue_mode', 'vc'); updateMeeting(type, 'venue', 'Through Video Conferencing (VC) / Other Audio-Visual Means'); }}
                              className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border ${isVC ? 'bg-[#0f1f45] text-white border-[#0f1f45]' : 'bg-white text-gray-500 border-gray-200'}`}>Audio/Video Mode</button>
                          </div>
                          <input value={meetingsData[type]?.venue || ''} onChange={ev => updateMeeting(type, 'venue', ev.target.value)} placeholder="Venue"
                            className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-blue-500" />
                          {!isVC && meetingsData[type]?.venue && meetingsData[type].venue === selectedCompany?.registered_office && (
                            <p className="text-[10px] text-gray-400 mt-1">Defaulted to registered office — edit if this meeting is happening elsewhere.</p>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
          <p className="text-xs text-gray-400">{selectedEventCount} event{selectedEventCount !== 1 ? 's' : ''} selected → {selectedEventCount} case{selectedEventCount !== 1 ? 's' : ''} will be created</p>
          <div className="flex gap-3">
            <button onClick={onClose} className="px-4 py-2.5 rounded-lg border border-gray-200 text-gray-600 text-sm font-semibold">Cancel</button>
            <button onClick={handleCreate} disabled={saving || !companyId || selectedEventCount === 0}
              className="px-5 py-2.5 rounded-lg bg-[#0f1f45] text-white text-sm font-semibold disabled:opacity-50">
              {saving ? 'Creating…' : `Create ${selectedEventCount || ''} Case${selectedEventCount !== 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// LIST VIEW
// ═══════════════════════════════════════════════════════════════════════
function EventFilings({ onNavigate, initialCaseId }) {
  const [view, setView] = useState('cases');
  const [cases, setCases] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCompany, setFilterCompany] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');
  const [showNewModal, setShowNewModal] = useState(false);
  // Jumped to directly (e.g. from Company Master's Filings tab) — read
  // once on mount, same convention Registers.js uses for its deep-link.
  const [selected, setSelected] = useState(() => initialCaseId ? { id: initialCaseId } : null);
  const [confirm, setConfirm] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [c, comp] = await Promise.all([fetchJson('/api/mca/event-filings'), fetchJson('/api/mca/companies')]);
      setCases(Array.isArray(c) ? c : []);
      setCompanies(Array.isArray(comp) ? comp : []);
    } catch { setCases([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const handleDeleteCase = (c) => {
    setConfirm({
      title: 'Delete this case?',
      message: `"${c.title}" will be permanently deleted. This cannot be undone.`,
      confirmLabel: 'Delete',
      onCancel: () => setConfirm(null),
      onConfirm: async () => {
        setConfirm(null);
        try { await fetchJson(`/api/mca/event-filings/${c.id}`, { method: 'DELETE' }); load(); } catch { /* silently ignore, row stays visible */ }
      },
    });
  };

  if (selected) {
    return <EventFilingCaseDetail item={selected} onBack={() => { setSelected(null); load(); }} onNavigate={onNavigate} onOpenSibling={setSelected} />;
  }

  const filtered = cases.filter(c => {
    const q = search.toLowerCase();
    const matchSearch = !q || c.title?.toLowerCase().includes(q) || c.company_name?.toLowerCase().includes(q) || c.event_tag?.toLowerCase().includes(q);
    const matchCompany = filterCompany === 'All' || String(c.company_id) === String(filterCompany);
    const matchStatus = filterStatus === 'All' || c.status === filterStatus;
    return matchSearch && matchCompany && matchStatus;
  });

  const stats = {
    total: cases.length,
    draft: cases.filter(c => c.status === 'Draft').length,
    inProgress: cases.filter(c => ['In Progress', 'Filed'].includes(c.status)).length,
    approved: cases.filter(c => ['Approved', 'Synced'].includes(c.status)).length,
    rejected: cases.filter(c => c.status === 'Rejected').length,
  };

  const statCards = [
    { label: 'Total', value: stats.total, accent: ACCENTS.navy, icon: Icon.event },
    { label: 'Draft', value: stats.draft, accent: ACCENTS.slate, icon: Icon.form },
    { label: 'In Progress', value: stats.inProgress, accent: ACCENTS.amber, icon: Icon.meeting },
    { label: 'Approved / Synced', value: stats.approved, accent: ACCENTS.emerald, icon: Icon.checklist },
    { label: 'Rejected', value: stats.rejected, accent: ACCENTS.red, icon: Icon.form },
  ];

  return (
    <div className="space-y-5">
      <style>{DARK_STYLE_OVERRIDES}</style>
      {confirm && <ConfirmModal {...confirm} />}
      {showNewModal && (
        <NewEventFilingWizard
          onClose={() => setShowNewModal(false)}
          onSwitchToEvents={() => setView('events')}
          onCreated={(createdCases) => {
            setShowNewModal(false);
            load();
            setSelected(createdCases[0]);
          }}
          onBackToList={() => { setShowNewModal(false); load(); }}
        />
      )}

      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-bold text-[#0f1f45]">Event Filings</h3>
          <p className="text-sm text-gray-500 mt-0.5">Registered office change, director changes, allotment, charges, MGT-14, strike-off and other event-based ROC filings.</p>
        </div>
        {view === 'cases' && (
          <button onClick={() => setShowNewModal(true)}
            className="px-4 py-2.5 rounded-xl text-white text-sm font-semibold shadow-sm flex items-center gap-2 transition-shadow hover:shadow-md"
            style={{ background: 'linear-gradient(135deg, #0f1f45 0%, #1d3a72 100%)' }}>
            <span className="w-5 h-5 rounded-full bg-white/15 flex items-center justify-center text-xs">+</span>
            New Event Filing
          </button>
        )}
      </div>

      <div className="inline-flex rounded-full border border-gray-200 bg-gray-50 p-1 w-fit">
        <button onClick={() => setView('cases')}
          className={`px-4 py-1.5 rounded-full text-sm font-medium flex items-center gap-1.5 transition-colors ${view === 'cases' ? 'bg-[#0f1f45] text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
          <span className="w-4 h-4">{Icon.event}</span>Cases
        </button>
        <button onClick={() => setView('events')}
          className={`px-4 py-1.5 rounded-full text-sm font-medium flex items-center gap-1.5 transition-colors ${view === 'events' ? 'bg-[#0f1f45] text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
          <span className="w-4 h-4">{Icon.checklist}</span>Event Types
        </button>
      </div>

      {view === 'events' ? <EventsMaster /> : (
        <>
          <div className="grid grid-cols-5 gap-4">
            {statCards.map(s => (
              <div key={s.label} className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex items-center gap-3">
                <IconBadge accent={s.accent}>{s.icon}</IconBadge>
                <div>
                  <p className="text-xs text-gray-400">{s.label}</p>
                  <p className={`text-xl font-bold ${s.accent.text}`}>{s.value}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="p-4 border-b border-gray-100 flex flex-wrap items-center gap-3">
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search title, company, event type…"
                className="flex-1 min-w-[200px] border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500" />
              <select value={filterCompany} onChange={e => setFilterCompany(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none">
                <option value="All">All Companies</option>
                {companies.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
              </select>
              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none">
                <option value="All">All Statuses</option>
                {[...STAGE_ORDER, 'Rejected'].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            {loading ? (
              <div className="py-16 text-center text-gray-400 text-sm">Loading…</div>
            ) : filtered.length === 0 ? (
              <div className="py-16 text-center text-gray-400 text-sm">
                {cases.length === 0 ? 'No event filings yet — click "+ New Event Filing" to start one.' : 'No cases match your filters.'}
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/60">
                    {['Case', 'Company', 'Event', 'Forms', 'Status', 'Created', ''].map(h => (
                      <th key={h} className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(c => {
                    const rowAccent = filingAccent(c.status === 'Draft' ? 'Pending' : c.status === 'Approved' || c.status === 'Synced' ? 'Approved' : c.status === 'Rejected' ? 'Rejected' : 'Filed');
                    return (
                      <tr key={c.id} onClick={() => setSelected(c)} className={`border-b border-gray-50 hover:bg-gray-50/60 cursor-pointer border-l-4 ${rowAccent.border}`}>
                        <td className="px-4 py-3">
                          <p className="text-sm font-semibold text-gray-800">{c.title}</p>
                          <p className="text-[11px] text-gray-400 mt-0.5">{(c.resolutions || []).length} resolution{(c.resolutions || []).length !== 1 ? 's' : ''}</p>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">{c.company_name}</td>
                        <td className="px-4 py-3 text-xs text-gray-500">{c.event_name}</td>
                        <td className="px-4 py-3 text-xs text-gray-500 font-mono">{(c.filings || []).map(f => f.form).join(', ') || '—'}</td>
                        <td className="px-4 py-3"><span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${STATUS_COLORS[c.status] || 'bg-gray-100 text-gray-600'}`}>{c.status}</span></td>
                        <td className="px-4 py-3 text-xs text-gray-400">{c.created_at ? new Date(c.created_at).toLocaleDateString('en-IN') : '—'}</td>
                        <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                          <div className="flex items-center justify-end">
                            <KebabMenu actions={[
                              { label: 'View', icon: Icon.eye, onClick: () => setSelected(c) },
                              { label: 'Delete', icon: Icon.trash, danger: true, onClick: () => handleDeleteCase(c) },
                            ]} />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// CASE DETAIL
// ═══════════════════════════════════════════════════════════════════════
function EventFilingCaseDetail({ item: initialItem, onBack, onOpenSibling, onNavigate }) {
  const [item, setItem] = useState(initialItem);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);

  const [eventData, setEventData] = useState({});
  const [resolutions, setResolutions] = useState([]);
  const [filings, setFilings] = useState([]);
  const [checklist, setChecklist] = useState([]);
  const [eventDate, setEventDate] = useState('');
  const [notes, setNotes] = useState('');

  const [alert, setAlert] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [saveStatus, setSaveStatus] = useState('idle');
  const [syncing, setSyncing] = useState(false);
  const [meetingSiblings, setMeetingSiblings] = useState({}); // meeting_id -> [{id, title, event_name, status}] (isi case samet)
  const [directors, setDirectors] = useState([]);
  const [shareholders, setShareholders] = useState([]);
  const [auditors, setAuditors] = useState([]);
  const [companyType, setCompanyType] = useState('');
  // Document-generation options only — not case data, so kept local and
  // not persisted: whether each meeting's Notice should read as issued on
  // shorter notice, and which agenda items are ticked for a partial CTC
  // export. Keyed by meeting_id.
  const [shorterNoticeByMeeting, setShorterNoticeByMeeting] = useState({});
  const [ctcSelectionByMeeting, setCtcSelectionByMeeting] = useState({});
  // Who actually consented to shorter notice — defaults to the Present
  // list but editable, since consent and attendance aren't always the
  // same set. Keyed by meeting_id.
  const [shorterConsentByMeeting, setShorterConsentByMeeting] = useState({});
  // Free-text narrative fields Minutes/CTC need but nothing upstream
  // captures structurally — interest disclosure (Sec 184, BM), Chairman's
  // remarks and scrutinizer name (GM), and who's certifying the CTC.
  const [minutesExtrasByMeeting, setMinutesExtrasByMeeting] = useState({});
  const [registerSyncingId, setRegisterSyncingId] = useState(null);
  const [registerSyncChoice, setRegisterSyncChoice] = useState(null); // { meetingId, type, existing } while awaiting Update/New choice
  // "Add Resolution" escape hatch — links a Resolution Master template
  // into this case after it already exists (forgotten at creation, or
  // added to Master since), without deleting/recreating the whole case.
  // Only offered for meeting types already present in this case, since
  // adding a brand-new meeting type needs its own meeting particulars
  // (use the full wizard / a new case for that).
  const [addResOptionsByType, setAddResOptionsByType] = useState({});
  const [addResOpen, setAddResOpen] = useState(false);
  const [addResType, setAddResType] = useState('');
  const [addResSearch, setAddResSearch] = useState('');
  const hasMountedRef = useRef(false);
  // Single-flight autosave guards — only one PATCH in flight at a time,
  // always built from the LATEST state via this ref (never a stale
  // closure), and re-run once more if something changed while it was
  // saving. Without this, two overlapping saves can race and the older
  // one (still in flight) can land after the newer one and silently wipe
  // whatever the newer save had just written.
  const savingRef = useRef(false);
  const dirtyRef = useRef(false);
  const latestRef = useRef();

  useEffect(() => {
    fetchJson(`/api/mca/event-filings/${initialItem.id}`).then(full => {
      setItem(full);
      setEventData(full.event_data || {});
      setResolutions(full.resolutions || []);
      setFilings(full.filings || []);
      setChecklist(full.checklist || []);
      setEventDate(full.event_date ? String(full.event_date).slice(0, 10) : '');
      setNotes(full.notes || '');

      if (full.company_id) {
        fetchJson(`/api/mca/companies/${full.company_id}/directors`).then(setDirectors).catch(() => setDirectors([]));
        fetchJson(`/api/mca/companies/${full.company_id}/shareholders`).then(setShareholders).catch(() => setShareholders([]));
        fetchJson(`/api/mca/companies/${full.company_id}/auditors`).then(setAuditors).catch(() => setAuditors([]));
        fetchJson(`/api/mca/companies/${full.company_id}`).then(c => setCompanyType(c.company_type || '')).catch(() => {});
      }
      Promise.all(['BM', 'AGM', 'EGM'].map(t => fetchJson(`/api/mca/resolutions?meeting=${t}&active=true`).then(list => [t, list]).catch(() => [t, []])))
        .then(pairs => setAddResOptionsByType(Object.fromEntries(pairs)));

      // For every meeting used in this case, check which other cases
      // share it — powers both the "Meeting Round" panel and the
      // edit-warning shown next to the meeting fields.
      const meetingIds = [...new Set((full.resolutions || []).map(r => r.meeting_id).filter(Boolean))];
      Promise.all(meetingIds.map(id =>
        fetchJson(`/api/mca/meetings/${id}/usage`).then(list => [id, list]).catch(() => [id, []])
      )).then(pairs => setMeetingSiblings(Object.fromEntries(pairs)));
    }).catch(() => setAlert({ message: 'Could not load this case.', title: 'Load failed' }))
      .finally(() => setLoading(false));
  }, [initialItem.id]);

  // Keep each resolution's `rendered_resolution` live-synced to the
  // current Event Details / template — UNTIL the user actually types
  // into the textarea themselves (tracked via `manually_edited`, not by
  // checking whether the field happens to be non-empty). Checking
  // non-emptiness was the actual bug: stale auto-generated text (e.g.
  // left over from before Event Details were filled, or from a Revise)
  // looked "already real" and blocked re-syncing forever, even though
  // nobody had ever actually hand-edited it.
  useEffect(() => {
    setResolutions(prev => {
      let changed = false;
      const next = prev.map(r => {
        if ((r.manually_edited || r.status === 'Passed')) return r;
        const filled = fillTemplate(r.template_body, fieldContextFor(r));
        if (filled !== r.rendered_resolution) { changed = true; return { ...r, rendered_resolution: filled }; }
        return r;
      });
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(eventData), item.company_name]);

  // Keeps {{transfers_table}}/{{total_shares_transferred}}/
  // {{transferee_names_list}} live-computed as ordinary eventData keys —
  // see TRANSFER_COMPUTED_KEYS above for why this is a plain sync rather
  // than a templating-engine change.
  useEffect(() => {
    if (!eventData.transfers) return;
    const computed = computeTransferFields(eventData.transfers);
    setEventData(prev => {
      const unchanged = TRANSFER_COMPUTED_KEYS.every(k => prev[k] === computed[k]);
      return unchanged ? prev : { ...prev, ...computed };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(eventData.transfers)]);

  const status = computeCaseStatus(resolutions, filings, item.synced_at);

  // Combine every meeting used in this case into one list of sibling
  // cases — for the "Meeting Round" banner (excluding this case itself).
  const allSiblingCases = Object.values(meetingSiblings).flat()
    .filter(c => !((c.module || 'event') === 'event' && c.id === item.id))
    .filter((c, i, arr) => arr.findIndex(x => (x.module || 'event') === (c.module || 'event') && x.id === c.id) === i);

  // Keep the ref current on every render so a save that's mid-flight (or
  // about to start) always sees the truly latest state, not whatever was
  // in scope when it was scheduled.
  latestRef.current = {
    item, eventData, resolutions, filings, checklist, eventDate, notes,
    shorterNoticeByMeeting, shorterConsentByMeeting, minutesExtrasByMeeting,
  };

  const saveCase = async () => {
    if (savingRef.current) { dirtyRef.current = true; return; }
    savingRef.current = true;
    do {
      dirtyRef.current = false;
      const snap = latestRef.current;
      setSaveStatus('saving');
      try {
        const updated = await fetchJson(`/api/mca/event-filings/${snap.item.id}/details`, {
          method: 'PATCH',
          body: JSON.stringify({
            event_data: snap.eventData, resolutions: snap.resolutions, filings: snap.filings, checklist: snap.checklist,
            status: computeCaseStatus(snap.resolutions, snap.filings, snap.item.synced_at),
            event_date: snap.eventDate || null, notes: snap.notes,
          }),
        });

        // Sync each resolution's meeting_data back to its shared
        // mca_meetings record, so any other case using the same meeting
        // sees the latest date/venue too. document_extras rides along in
        // the same call — this is what lets the Statutory Register's
        // Documents tab regenerate the SAME Notice/Minutes/CTC content
        // (chairman's remarks, shorter-notice consent, etc.) instead of
        // showing them blank when it wasn't the Event Filing side that
        // last generated a document for this meeting.
        const meetingUpdates = {};
        snap.resolutions.forEach(r => { if (r.meeting_id && !meetingUpdates[r.meeting_id]) meetingUpdates[r.meeting_id] = r.meeting_data; });
        await Promise.all(Object.entries(meetingUpdates).map(([mid, data]) =>
          fetchJson(`/api/mca/meetings/${mid}`, {
            method: 'PUT',
            body: JSON.stringify({
              ...data,
              document_extras: {
                notice_date: data.notice_date || null,
                venue_mode: data.venue_mode || 'physical',
                shorter_notice: !!snap.shorterNoticeByMeeting[mid],
                shorter_consent: snap.shorterConsentByMeeting[mid] || null,
                ...(snap.minutesExtrasByMeeting[mid] || {}),
              },
            }),
          }).catch(() => {})
        ));

        setItem(prev => ({ ...prev, ...updated }));
        setSaveStatus('saved');
      } catch { setSaveStatus('error'); }
    } while (dirtyRef.current);
    savingRef.current = false;
  };

  useEffect(() => {
    if (loading) return;
    if (!hasMountedRef.current) { hasMountedRef.current = true; return; }
    const timer = setTimeout(() => { saveCase(); }, 1500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventData, resolutions, filings, checklist, eventDate, notes]);

  const handleBack = async () => { await saveCase(); onBack(); };

  const handleDeleteCase = () => {
    setConfirm({
      title: 'Delete this case?',
      message: `"${item.title}" will be permanently deleted. This cannot be undone.`,
      confirmLabel: 'Delete',
      onCancel: () => setConfirm(null),
      onConfirm: async () => {
        setConfirm(null);
        try {
          await fetchJson(`/api/mca/event-filings/${item.id}`, { method: 'DELETE' });
          onBack();
        } catch {
          setAlert({ message: 'Could not delete this case. Try again.', title: 'Delete failed' });
        }
      },
    });
  };

  // The SAME resolution can be linked to a case more than once (e.g. a
  // separate "Regularization of Additional Director" resolution per
  // director) — each instance needs its OWN placeholder values, not one
  // shared eventData[key] every copy would fight over. Instance values
  // live on the resolution itself (r.field_values); a key not yet set
  // there falls back to eventData, so a case created before this existed
  // (single instance, value in eventData) keeps working unchanged until
  // edited. transfers_table/eventData.transfers stays case-level — a
  // transfer batch isn't something one resolution instance owns.
  const fieldContextFor = (r) => ({ company_name: item.company_name, ...eventData, ...(r.field_values || {}) });
  const resolutionFieldValue = (r, key) =>
    (r.field_values && key in r.field_values) ? r.field_values[key]
      : eventData[key] != null ? eventData[key]
      : (key === 'company_name' ? item.company_name : '');
  const updateResolutionField = (resIdx, patch) => {
    const r = resolutions[resIdx];
    updateResolution(resIdx, { field_values: { ...(r?.field_values || {}), ...patch } });
  };

  const rawAllPlaceholders = [...new Set(resolutions.flatMap(r => extractPlaceholders(r.template_body)))];
  // {{transfers_table}} etc. are auto-computed (see TRANSFER_COMPUTED_KEYS
  // above), not user-filled flat fields — excluded from the "fill these
  // in" list; completeness instead checks eventData.transfers has rows.
  const needsTransferRows = rawAllPlaceholders.includes('transfers_table');
  // Merge choice-field options across every linked resolution, so if two
  // resolutions both use e.g. `proof_type` the dropdown offers every
  // option either of them defines.
  const allChoiceFields = resolutions.reduce((acc, r) => {
    Object.entries(extractChoiceFields(r.template_body)).forEach(([field, opts]) => {
      if (!acc[field]) acc[field] = [];
      opts.forEach(o => { if (!acc[field].includes(o)) acc[field].push(o); });
    });
    return acc;
  }, {});
  // Grouped per-resolution INSTANCE for the Event Details tab — one block
  // per resolution actually linked to this case, each showing only that
  // resolution's own placeholders (+ its own choice-field options),
  // instead of one flat dumped, unattributed list. instanceLabel
  // disambiguates two copies of the same resolution (" #1", " #2", …),
  // blank when there's only one so the common case reads as before.
  const resolutionsWithPlaceholders = (() => {
    const idCounts = {};
    resolutions.forEach(r => { idCounts[r.resolution_id] = (idCounts[r.resolution_id] || 0) + 1; });
    const seen = {};
    return resolutions
      .map((r, resIdx) => {
        seen[r.resolution_id] = (seen[r.resolution_id] || 0) + 1;
        return {
          resolution: r, resIdx,
          instanceLabel: idCounts[r.resolution_id] > 1 ? ` #${seen[r.resolution_id]}` : '',
          keys: extractPlaceholders(r.template_body),
          choiceFields: extractChoiceFields(r.template_body),
        };
      })
      .filter(({ keys }) => keys.length > 0);
  })();
  // Total field-SLOTS across every resolution instance (not deduped by
  // key name) — two instances of the same resolution each need their own
  // value filled in, so the completeness count must reflect both.
  const totalRequired = resolutionsWithPlaceholders.reduce((sum, { keys }) => sum + keys.filter(k => !TRANSFER_COMPUTED_KEYS.includes(k)).length, 0)
    + (needsTransferRows ? 1 : 0);
  const filledCount = resolutionsWithPlaceholders.reduce((sum, { resolution: r, keys }) =>
    sum + keys.filter(k => !TRANSFER_COMPUTED_KEYS.includes(k) && String(resolutionFieldValue(r, k) || '').trim()).length, 0)
    + (needsTransferRows && eventData.transfers?.length > 0 ? 1 : 0);
  const eventDetailsOk = totalRequired === 0 || filledCount === totalRequired;

  const resolutionsPassedCount = resolutions.filter(r => r.status === 'Passed').length;
  const resolutionsOk = resolutions.length > 0 && resolutionsPassedCount === resolutions.length;

  const checklistDone = checklist.filter(c => c.status === 'Completed' || c.status === 'Not Applicable').length;
  const attachmentsOk = checklist.length === 0 || checklistDone === checklist.length;

  const filingsApprovedCount = filings.filter(f => f.status === 'Approved').length;
  const filingsOk = filings.length > 0 && filingsApprovedCount === filings.length;
  const syncedOk = !!item.synced_at;

  const statusRows = [
    { label: 'Event Details', ok: eventDetailsOk, detail: totalRequired ? `${filledCount}/${totalRequired} fields filled` : 'No fields required', tab: 'details' },
    { label: 'Resolutions', ok: resolutionsOk, detail: `${resolutionsPassedCount}/${resolutions.length} passed`, tab: 'resolutions' },
    { label: 'Attachments', ok: attachmentsOk, detail: checklist.length ? `${checklistDone}/${checklist.length} done` : 'None required', tab: 'attachments' },
    { label: 'Filings', ok: filingsOk, detail: `${filingsApprovedCount}/${filings.length} approved`, tab: 'filing' },
    { label: 'Register Sync', ok: syncedOk, detail: syncedOk ? `Synced ${new Date(item.synced_at).toLocaleDateString('en-IN')}` : 'Pending all filings approved', tab: 'filing' },
  ];
  const completeCount = statusRows.filter(r => r.ok).length;

  const updateResolution = (idx, patch) => setResolutions(prev => prev.map((r, i) => i === idx ? { ...r, ...patch } : r));

  // Links a Resolution Master template into this case — same shape the
  // backend's snapshot-builder produces, built client-side here since the
  // case's `resolutions` array is already fully editable via the existing
  // autosave (no new endpoint needed). Reuses whichever meeting_id/
  // meeting_data this case already has for that meeting type; only meeting
  // types already present in the case can be picked (see addResType UI).
  // Linking the SAME resolution twice is legitimate — e.g. a separate
  // "Regularization of Additional Director" resolution is needed per
  // director, all off the one Resolution Master template. So this asks
  // for confirmation instead of refusing outright; each instance gets its
  // own placeholder values via field_values (see fieldContextFor/
  // resolutionFieldValue above), so a second copy never overwrites the
  // first's Director Name/DIN/etc.
  const addResolutionToCase = (meetingType, template) => {
    if (resolutions.some(r => r.resolution_id === template.id && r.meeting_type === meetingType)) {
      setConfirm({
        title: 'Already linked for this meeting type',
        message: `"${template.resolution_title}" is already linked here. Add another copy anyway? Each copy gets its own fields to fill in separately (e.g. a different director's name/DIN) on the Event Details tab.`,
        confirmLabel: 'Add Anyway',
        onConfirm: () => { setConfirm(null); insertResolutionInstance(meetingType, template); },
        onCancel: () => setConfirm(null),
      });
      return;
    }
    insertResolutionInstance(meetingType, template);
  };
  const insertResolutionInstance = (meetingType, template) => {
    const sibling = resolutions.find(r => r.meeting_type === meetingType);
    setResolutions(prev => [...prev, {
      resolution_id: template.id, resolution_code: template.resolution_code || null,
      meeting_type: meetingType, meeting_id: sibling?.meeting_id || null,
      resolution_title: template.resolution_title, resolution_type: template.resolution_type,
      business_classification: template.business_classification || null,
      template_body: template.template_body || '', explanatory_statement: template.explanatory_statement || '',
      status: 'Pending', rendered_resolution: '', votes: {}, manually_edited: false,
      // "Take Data" — pre-fill from the Statutory Auditor Master for the
      // auditor-lifecycle resolutions (re-appointment/casual-vacancy/
      // removal); empty for everything else, same as before.
      field_values: auditorResolutionPrefill(template.resolution_code, auditors),
      meeting_data: sibling?.meeting_data ? { ...sibling.meeting_data } : {},
    }]);
    setAddResOpen(false); setAddResType(''); setAddResSearch('');
  };
  const removeResolution = (idx) => {
    const r = resolutions[idx];
    if (r.status !== 'Pending') {
      setAlert({ message: `This resolution is ${r.status} — revise it back to Pending first if it really needs to be removed.`, title: 'Cannot remove' });
      return;
    }
    setConfirm({
      title: 'Remove this resolution?', message: `"${r.resolution_title}" will be unlinked from this event filing.`, confirmLabel: 'Remove',
      onConfirm: () => { setResolutions(prev => prev.filter((_, i) => i !== idx)); setConfirm(null); },
      onCancel: () => setConfirm(null),
    });
  };

  const regenerateResolution = (idx) => {
    const r = resolutions[idx];
    const next = fillTemplate(r.template_body, fieldContextFor(r));
    if (r.manually_edited && r.rendered_resolution !== next) {
      setConfirm({
        message: 'This will overwrite the hand-edited resolution text. Continue?', title: 'Overwrite draft?', confirmLabel: 'Overwrite',
        onConfirm: () => { updateResolution(idx, { rendered_resolution: next, manually_edited: false }); setConfirm(null); },
        onCancel: () => setConfirm(null),
      });
    } else {
      updateResolution(idx, { rendered_resolution: next, manually_edited: false });
    }
  };

  // Explicitly unlocks an already-Passed/Rejected resolution for editing.
  // Deliberately a separate, confirmed action — never silent — so a
  // finalized resolution can't drift out of sync with what actually got
  // certified without the user consciously choosing to revise it. Votes
  // are cleared too — a revise means redoing the vote, not editing around it.
  const reviseResolution = (idx) => {
    setConfirm({
      message: `This resolution is marked ${resolutions[idx]?.status}. Revising it will unlock the draft, meeting particulars and votes for editing, resetting status back to Pending until every present voter casts a vote again.`,
      title: 'Revise this resolution?', confirmLabel: 'Revise',
      onConfirm: () => { updateResolution(idx, { status: 'Pending', votes: {} }); setConfirm(null); },
      onCancel: () => setConfirm(null),
    });
  };

  // Whether a resolution is even eligible to start collecting votes:
  // draft text finalized (no blank/unresolved placeholders), meeting date
  // set, and quorum actually met — voting before any of that is moot.
  const resolutionReadiness = (r) => {
    const effectiveText = (r.manually_edited || r.status === 'Passed' || r.status === 'Rejected')
      ? (r.rendered_resolution || '').trim()
      : fillTemplate(r.template_body, fieldContextFor(r)).trim();
    if (!effectiveText) return { ready: false, reason: 'The resolution draft is empty.' };
    const unresolved = findUnresolvedTokens(effectiveText);
    if (unresolved.length > 0) {
      return { ready: false, reason: `Still empty on the Event Details tab: ${unresolved.map(humanizeKey).join(', ')}.` };
    }
    if (!r.meeting_data?.meeting_date) return { ready: false, reason: 'Set the meeting date in Meeting Particulars first.' };
    if (!r.meeting_data?.chairperson) return { ready: false, reason: 'Set the chairperson in Meeting Particulars first — a meeting without one isn\'t validly constituted.' };
    const isBM = r.meeting_type === 'BM';
    const attendees = r.meeting_data?.attendees || [];
    const quorumNeeded = isBM ? boardQuorum(directors.length) : generalMeetingQuorum(companyType, shareholders.length);
    if (attendees.length < quorumNeeded) return { ready: false, reason: `Quorum not met (${attendees.length}/${quorumNeeded}) — mark enough ${isBM ? 'directors' : 'members'} present first.` };
    return { ready: true, effectiveText };
  };

  // Records one person's vote. The instant every present voter has cast
  // one, the resolution auto-locks Passed/Rejected from the tally — no
  // separate confirmation step, per how this is meant to work: the vote
  // count itself is the decision, not a button after it.
  const castVote = (idx, personName, choice) => {
    const r = resolutions[idx];
    if (r.status === 'Passed' || r.status === 'Rejected') return;
    const readiness = resolutionReadiness(r);
    if (!readiness.ready) { setAlert({ message: readiness.reason, title: 'Incomplete' }); return; }

    const isBM = r.meeting_type === 'BM';
    const presentRoster = r.meeting_data?.attendees || [];
    const nextVotes = { ...(r.votes || {}) };
    if (nextVotes[personName] === choice) delete nextVotes[personName]; // click again to unset
    else nextVotes[personName] = choice;

    const allVoted = presentRoster.length > 0 && presentRoster.every(name => nextVotes[name]);
    if (!allVoted) { updateResolution(idx, { votes: nextVotes }); return; }

    const { forWeight, againstWeight } = tallyVotes(nextVotes, presentRoster, isBM, shareholders);
    const passed = evaluateResolution(r.resolution_type, forWeight, againstWeight);
    updateResolution(idx, {
      votes: nextVotes,
      status: passed ? 'Passed' : 'Rejected',
      rendered_resolution: readiness.effectiveText,
    });
  };

  // Per-resolution CTC/Notice downloads used to live here — removed since
  // the Documents tab covers the same output at the meeting level (full
  // agenda, correct cross-case numbering) and having both was duplicate.

  // ═══════════════════════════════════════════════════════════════════════
  // MEETING-LEVEL DOCUMENTS — Notice / Minutes / Attendance Sheet / CTC,
  // built from the meeting's FULL agenda (every resolution across every
  // case sharing this meeting_id, not just this case's slice), ordered by
  // case id then position within each case's resolutions array (stable,
  // reproducible) — uses the CURRENT case's live `resolutions` state (not
  // the cached copy inside meetingSiblings) so unsaved edits still number
  // correctly.
  // ═══════════════════════════════════════════════════════════════════════
  const getMeetingAgendaItems = (meetingId) => {
    const siblingCases = [...(meetingSiblings[meetingId] || [])].sort((a, b) => a.id - b.id);
    const items = [];
    siblingCases.forEach(c => {
      // Siblings now span Event Filing AND Annual Filing cases (see
      // /api/mca/meetings/:id/usage) — ids are only unique WITHIN a
      // table, so "current case" must also match on module, or an
      // Annual Filing sibling that happens to share this case's numeric
      // id would be mistaken for the case itself.
      const isCurrent = (c.module || 'event') === 'event' && c.id === item.id;
      const resArr = isCurrent ? resolutions : (c.resolutions || []);
      resArr.forEach(r => {
        if (r.meeting_id !== meetingId) return;
        const isFinal = r.manually_edited || r.status === 'Passed' || r.status === 'Rejected';
        const text = isFinal
          ? (r.rendered_resolution || '').trim()
          : (isCurrent ? fillTemplate(r.template_body, fieldContextFor(r)).trim() : '');
        items.push({ resolution: r, caseTitle: c.title, isCurrent, isFinal, text });
      });
    });
    return items.map((it, i) => ({ ...it, itemNo: i + 1 }));
  };

  // Representative meeting_data for a meeting_id — read from this case's
  // own resolutions (attendance/venue/chairperson sync to the same shared
  // mca_meetings row regardless of which sibling case last saved it).
  const getMeetingData = (meetingId) => resolutions.find(r => r.meeting_id === meetingId)?.meeting_data || {};

  const meetingLabel = (type) => type === 'BM' ? 'Board Meeting' : type === 'AGM' ? 'Annual General Meeting' : 'Extraordinary General Meeting';

  // "Nth meeting of this type this company has held" — same count the
  // register-sync's serial-number logic uses, just read-only here (no
  // register entry required to know the number).
  const getMeetingSerialNumber = async (type) => {
    try {
      const url = type === 'BM'
        ? `/api/mca/companies/${item.company_id}/board-meetings`
        : `/api/mca/companies/${item.company_id}/general-meetings`;
      const list = await fetchJson(url);
      return (Array.isArray(list) ? list.length : 0) + 1;
    } catch { return null; }
  };

  // GM roster's total weight (sum of shareholding) — the denominator for
  // both vote-share and shorter-notice-consent percentage calculations.
  const totalMemberWeight = shareholders.reduce((sum, s) => sum + (Number(s.no_of_shares) || 0), 0);

  // ═══════════════════════════════════════════════════════════════════════
  // SHARE TRANSFER DOCUMENTS — Request Letter, Application for
  // Registration, Form SH-4, Acknowledgement. Only rendered when this
  // case's Event Details carry a transfers[] array (see
  // TransferRowsEditor). Each is ONE Word file with a repeating section
  // per row, matching how the real attachments already batch multiple
  // instances into one docx — a Request Letter per transferor, one
  // Application per distinct transferee (grouping every row that goes to
  // them), one SH-4 per transferor, one Acknowledgement per transferor.
  // ═══════════════════════════════════════════════════════════════════════
  const transfers = eventData.transfers || [];

  const downloadRequestLettersDoc = () => {
    if (transfers.length === 0) return;
    const html = transfers.map((t, i) => `
      ${i > 0 ? '<p class="page-break"></p>' : ''}
      <p class="doc-title">Request Letter for Transfer of Shares</p>
      <p class="field-row">Date: ${t.transfer_date ? new Date(t.transfer_date).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '__________'}</p>
      <p class="body-narrative" style="margin-top:8pt;">To,<br/>The Board of Directors<br/>${item.company_name}<br/>CIN: ${item.cin_llpin || '—'}</p>
      <p class="body-narrative" style="margin-top:8pt;"><b>Subject: Request for transfer of equity shares held in the Company.</b></p>
      <p class="body-narrative" style="margin-top:8pt;">Dear Sir(s),</p>
      <p class="body-narrative" style="margin-top:6pt;">I, ${(t.transferor_name || '').toUpperCase()}${t.transferor_din ? ` (DIN: ${t.transferor_din})` : ''}, a registered shareholder of the Company holding shares under Folio No. ${t.transferor_folio || '—'}, hereby request the Board of Directors to register the transfer of ${t.no_of_shares || 0} equity shares of Rs. ${t.face_value || 10}/- each, as detailed below, in favour of the transferee named herein, and to record the said transfer in the Register of Members of the Company.</p>
      <p class="body-narrative" style="margin-top:6pt;">I confirm that the shares proposed to be transferred are fully paid-up and free from any lien, charge or encumbrance, and that a duly executed and stamped instrument of transfer in Form SH-4 is/shall be delivered to the Company along with the relevant share certificate for registration under Section 56 of the Companies Act, 2013.</p>
      <table><thead><tr><th>Transferor's Name</th><th>No. of Shares</th><th>Face Value (Rs.)</th><th>Transferor's Folio No.</th><th>Transferee's Folio No.</th><th>Transferee's Name</th></tr></thead>
      <tbody><tr><td>${t.transferor_name || '—'}</td><td style="text-align:center;">${t.no_of_shares || 0}</td><td style="text-align:center;">${t.face_value || 10}</td><td style="text-align:center;">${t.transferor_folio || '—'}</td><td style="text-align:center;">${t.transferee_folio || 'NA'}</td><td>${t.transferee_name || '—'}</td></tr></tbody></table>
      <p class="body-narrative">Consideration for the said transfer: Rs. ${(Number(t.consideration_per_share || 0) * Number(t.no_of_shares || 0)).toLocaleString('en-IN')}/- (Rupees ${numberToWordsIndian(Number(t.consideration_per_share || 0) * Number(t.no_of_shares || 0))} only), i.e. Rs. ${t.consideration_per_share || 0}/- per share.</p>
      <p class="body-narrative" style="margin-top:6pt;">Kindly treat this letter as my consent for the said transfer and take the same on record.</p>
      <div class="sig-block">
        <p class="sig-for">Yours faithfully,</p>
        <div class="sig-name-block">_____________________<br/><span class="sig-name">${t.transferor_name || '—'}</span><br/>(Transferor)${t.transferor_din ? ` DIN: ${t.transferor_din}` : ''}</div>
        <p class="sig-meta">Folio No.: ${t.transferor_folio || '—'}</p>
      </div>
    `).join('');
    downloadTransferStatutoryWord(`Request Letters - ${item.company_name}`, html);
  };

  const downloadApplicationForRegistrationDoc = () => {
    if (transfers.length === 0) return;
    const byTransferee = {};
    transfers.forEach(t => {
      const key = t.transferee_name || 'Unknown';
      if (!byTransferee[key]) byTransferee[key] = { info: t, rows: [] };
      byTransferee[key].rows.push(t);
    });
    const html = Object.values(byTransferee).map(({ info: t, rows }, i) => {
      const total = rows.reduce((s, r) => s + (Number(r.no_of_shares) || 0), 0);
      const totalConsideration = rows.reduce((s, r) => s + (Number(r.no_of_shares) || 0) * (Number(r.consideration_per_share) || 0), 0);
      return `
        ${i > 0 ? '<p class="page-break"></p>' : ''}
        <p class="co-header">${item.company_name}</p>
        <p class="co-meta">CIN: ${item.cin_llpin || '—'}</p>
        <p class="co-meta last">Regd. Office: ${item.registered_office || '—'}</p>
        <p class="doc-title">Application for Registration of Transfer of Shares</p>
        <p class="field-row">Date: ${t.transfer_date ? new Date(t.transfer_date).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '__________'}</p>
        <p class="body-narrative" style="margin-top:8pt;">To,<br/>The Board of Directors<br/>${item.company_name}<br/>CIN: ${item.cin_llpin || '—'}</p>
        <p class="body-narrative" style="margin-top:8pt;"><b>Subject: Application to acquire equity shares and for registration as a member of the Company.</b></p>
        <p class="body-narrative" style="margin-top:8pt;">Dear Sir(s),</p>
        <p class="body-narrative" style="margin-top:6pt;">I, ${(t.transferee_name || '').toUpperCase()}${t.transferee_pan ? ` (PAN: ${t.transferee_pan})` : ''}, resident of ${t.transferee_address || '—'}, hereby apply to acquire, by way of transfer, an aggregate of ${total} equity shares of Rs. ${t.face_value || 10}/- each of the Company from the existing shareholders in the manner set out below, and request the Board to register the said transfer in my name and to enter my name in the Register of Members of the Company.</p>
        <p class="body-narrative" style="margin-top:6pt;">I agree to accept and hold the said shares subject to the Memorandum and Articles of Association of the Company and to the conditions on which the said shares are presently held by the transferor(s).</p>
        <table><thead><tr><th>Transferor</th><th>Folio No.</th><th>No. of Shares</th><th>Face Value (Rs.)</th></tr></thead>
        <tbody>${rows.map(r => `<tr><td>${r.transferor_name || '—'}</td><td style="text-align:center;">${r.transferor_folio || '—'}</td><td style="text-align:center;">${r.no_of_shares || 0}</td><td style="text-align:center;">${r.face_value || 10}</td></tr>`).join('')}
        <tr><td colspan="2" style="text-align:right;"><b>Total</b></td><td style="text-align:center;"><b>${total}</b></td><td style="text-align:center;"><b>${totalConsideration.toLocaleString('en-IN')}</b></td></tr></tbody></table>
        <div class="sig-block">
          <p class="sig-for">Yours faithfully,</p>
          <div class="sig-name-block">_____________________<br/><span class="sig-name">${t.transferee_name || '—'}</span><br/>(Transferee)</div>
        </div>
      `;
    }).join('');
    downloadTransferStatutoryWord(`Application for Registration - ${item.company_name}`, html);
  };

  const downloadSh4Doc = () => {
    if (transfers.length === 0) return;
    const html = transfers.map((t, i) => {
      const consideration = (Number(t.consideration_per_share) || 0) * (Number(t.no_of_shares) || 0);
      const stampDuty = (consideration * 0.00015).toFixed(2);
      const folio = t.transferee_shareholder_id ? t.transferee_folio : 'NA';
      return `
      ${i > 0 ? '<p class="page-break"></p>' : ''}
      <p class="doc-title">FORM NO. SH-4</p>
      <p class="doc-title">Securities Transfer Form</p>
      <p class="doc-provision">[Pursuant to section 56 and sub-rule (1) of rule 11 of the Companies (Share Capital and Debentures) Rules, 2014]</p>
      <p class="field-row">Date of execution: ${t.transfer_date ? new Date(t.transfer_date).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '-') : '__________'}</p>
      <p class="body-narrative" style="margin-top:6pt;">FOR THE CONSIDERATION stated below, the &ldquo;Transferor(s)&rdquo; named do hereby transfer to the &ldquo;Transferee(s)&rdquo; named the securities specified below subject to the conditions on which the said securities are now held by the Transferor(s), and the Transferee(s) do hereby agree to accept and hold the said securities subject to the conditions aforesaid.</p>
      <table><tbody>
        <tr><td>CIN</td><td>${item.cin_llpin || '—'}</td></tr>
        <tr><td>Name of the company (in full)</td><td>${(item.company_name || '').toUpperCase()}</td></tr>
        <tr><td>Name of the Stock Exchange</td><td>Not Applicable (unlisted)</td></tr>
      </tbody></table>
      <p class="field-row">Description of Securities:</p>
      <table><tbody>
        <tr><td>Kind / Class of Securities</td><td>Nominal value of each unit (Rs.)</td><td>Amount called up per unit (Rs.)</td><td>Amount paid up per unit (Rs.)</td></tr>
        <tr><td>Equity Shares</td><td>Rs. ${t.face_value || 10}/- (Rupees ${numberToWordsIndian(t.face_value || 10)} only)</td><td>Rs. ${t.face_value || 10}/- (Rupees ${numberToWordsIndian(t.face_value || 10)} only)</td><td>Rs. ${t.face_value || 10}/- (Rupees ${numberToWordsIndian(t.face_value || 10)} only)</td></tr>
      </tbody></table>
      <p class="field-row">Number of securities transferred:</p>
      <table><tbody>
        <tr><td colspan="2">No. of securities being transferred</td><td colspan="2">Consideration received (INR)</td></tr>
        <tr><td>In figures</td><td>In words</td><td>In words</td><td>In figures</td></tr>
        <tr><td>${t.no_of_shares || 0}</td><td>${numberToWordsIndian(t.no_of_shares || 0)}</td><td>${consideration}</td><td>${numberToWordsIndian(consideration)} Only</td></tr>
      </tbody></table>
      <table><tbody>
        <tr><td>Distinctive number</td><td>From</td><td>${t.distinctive_from || '—'}</td></tr>
        <tr><td></td><td>To</td><td>${t.distinctive_to || '—'}</td></tr>
        <tr><td>Corresponding Certificates Nos.</td><td colspan="2">${t.certificate_no || '—'}</td></tr>
      </tbody></table>
      <p class="field-row" style="margin-top:6pt;">Transferor's particulars:</p>
      <table><tbody>
        <tr><td>Name</td><td>${t.transferor_name || '—'}</td></tr>
        <tr><td>DIN / PAN</td><td>DIN: ${t.transferor_din || '—'}&nbsp;&nbsp;&nbsp;PAN: ${t.transferor_pan || '—'}</td></tr>
        <tr><td>Signature</td><td></td></tr>
      </tbody></table>
      <table><tbody>
        <tr><td>I, hereby confirm that the transferor has signed before me. Signature of witness: ________________________<br/>Name: ${t.witness_name || '—'}<br/>Address: ${t.witness_address || '—'}</td></tr>
      </tbody></table>
      <p class="field-row" style="margin-top:6pt;">Transferee's particulars:</p>
      <table><tbody>
        <tr><td>Name</td><td>${t.transferee_name || '—'}</td></tr>
        <tr><td>Father's / Husband's Name</td><td>${t.transferee_father_husband || '—'}</td></tr>
        <tr><td>Address</td><td>${t.transferee_address || '—'}</td></tr>
        <tr><td>Occupation</td><td>${t.transferee_occupation || '—'}</td></tr>
        <tr><td>Existing Folio No. (if any)</td><td>${folio}</td></tr>
        <tr><td>PAN</td><td>${t.transferee_pan || '—'}</td></tr>
        <tr><td>Signature</td><td></td></tr>
      </tbody></table>
      <p class="field-row" style="margin-top:6pt;">_____________________________</p>
      <p class="field-row">Folio No. of Transferee: ${folio} &nbsp;&nbsp;&nbsp; Specimen Signature of Transferee</p>
      <p class="field-row" style="margin-top:6pt;">Value of stamps affixed (Rs.): ${stampDuty} (i.e. 0.015% of the consideration) — to be affixed by way of share transfer stamps and cancelled.</p>
      <p class="field-row">Stamps</p>
      <table><tbody><tr><td style="height:60pt;"></td></tr></tbody></table>
      <p class="field-row" style="margin-top:6pt;">Enclosures:</p>
      <p class="field-row">(1) Certificate of shares or debentures or other securities</p>
      <p class="field-row">(2) If no certificate is issued, letter of allotment.</p>
      <p class="field-row">(3) Others, specify……………………………………….</p>
      `;
    }).join('');
    downloadTransferStatutoryWord(`SH-4 Securities Transfer Forms - ${item.company_name}`, html);
  };

  const downloadAcknowledgementDoc = () => {
    if (transfers.length === 0) return;
    const html = transfers.map((t, i) => `
      ${i > 0 ? '<p class="page-break"></p>' : ''}
      <p class="body-narrative">To,<br/>${t.transferee_name || '—'}<br/>${t.transferee_father_husband ? `D/o or S/o ${t.transferee_father_husband}<br/>` : ''}${t.transferee_address || '—'}</p>
      <p class="body-narrative" style="margin-top:8pt;"><b>Sub: Acknowledgement of Receipt of Consideration in respect of Transfer of ${t.no_of_shares || 0} shares of ${item.company_name}</b></p>
      <p class="body-narrative" style="margin-top:6pt;">I, ${t.transferor_name || '—'}, do hereby affirm, confirm and state as under:</p>
      <p class="body-narrative" style="margin-top:6pt;">1. THAT I have transferred a total of ${t.no_of_shares || 0} equity shares of face value Rs. ${t.face_value || 10}/- each to ${t.transferee_name || '—'} at a price of Rs. ${t.consideration_per_share || 0}/- per share, as mentioned in the Share Transfer Deed.</p>
      <p class="body-narrative">2. THAT for the purpose of executing the above said transfer, I have duly signed the Share Transfer Deed.</p>
      <p class="body-narrative">3. THAT the total consideration for the above said transfer is Rs. ${((Number(t.consideration_per_share) || 0) * (Number(t.no_of_shares) || 0)).toLocaleString('en-IN')}/- and I hereby acknowledge the receipt of the full amount from the said transferee.</p>
      <p class="body-narrative">4. THAT all the shares transferred herewith are free from all encumbrances and I am fully authorised and rightful to transfer the same, and that after such transfer neither I nor any other person related to me nor my legal heirs have any rights/claims vested in such shares.</p>
      <p class="body-narrative">5. THAT the share certificate(s) transferred herewith are the only share certificate(s) I held in respect of those shares.</p>
      <p class="body-narrative">6. THAT the consideration mentioned above is the absolute consideration for such transfer, and no promise/request has been made to me for any other consideration in respect of the transfer of shares mentioned in the Share Transfer Deed referred to in this undertaking.</p>
      <div class="sig-block">
        <p class="sig-for">Sd/-</p>
        <div class="sig-name-block">${t.transferor_name || '—'}</div>
        <p class="sig-meta">${t.transferor_folio ? `Folio No.: ${t.transferor_folio}` : ''}</p>
      </div>
    `).join('');
    downloadTransferStatutoryWord(`Acknowledgement of Consideration - ${item.company_name}`, html);
  };

  // ═══════════════════════════════════════════════════════════════════════
  // DIRECTOR APPOINTMENT — DIR-2 (Consent to Act) & DIR-8 (Non-Disqualification
  // Declaration). Reads directly off each RES-DIR-001 (Appointment of
  // Additional Director) resolution INSTANCE's own field_values, not
  // eventData — this event supports appointing several directors in one
  // case, each their own resolution instance with its own DIN/address/etc.
  // (see the multi-instance resolution work), so one Word file here still
  // means one section per director, same batching convention as the
  // Transfer documents above. Wording matches the standard MCA forms
  // exactly — nothing paraphrased.
  // ═══════════════════════════════════════════════════════════════════════
  const directorAppointments = resolutions.filter(r => r.meeting_type === 'BM' && r.field_values?.additional_director_name);

  const downloadDir2Doc = () => {
    if (directorAppointments.length === 0) return;
    const html = directorAppointments.map((r, i) => {
      const fv = r.field_values || {};
      const dateFmt = fv.additional_director_appointment_date
        ? new Date(fv.additional_director_appointment_date).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' })
        : '__________';
      return `
      ${i > 0 ? '<p class="page-break"></p>' : ''}
      <p class="body-narrative">To,<br/>${item.company_name}</p>
      <p class="body-narrative" style="margin-top:8pt;"><b>Subject: Consent to act as a director</b></p>
      <p class="body-narrative" style="margin-top:6pt;">I, ${fv.additional_director_name || '—'}, hereby give my consent to act as director of ${item.company_name}, pursuant to sub-section (5) of section 152 of the Companies Act, 2013 and certify that I am not disqualified to become a director under the Companies Act, 2013.</p>
      <table><tbody>
        <tr><th>S.No</th><th>Particulars</th><th>Details</th></tr>
        <tr><td style="text-align:center;">1</td><td>DIN/PAN</td><td>${fv.additional_director_din || '—'}</td></tr>
        <tr><td style="text-align:center;">2</td><td>Name (in full):</td><td>${(fv.additional_director_name || '—').toUpperCase()}</td></tr>
        <tr><td style="text-align:center;">3</td><td>Father's Name (In full):</td><td>${(fv.additional_director_father_name || '—').toUpperCase()}</td></tr>
        <tr><td style="text-align:center;">4</td><td>Address:</td><td>${fv.additional_director_address || '—'}</td></tr>
        <tr><td style="text-align:center;">5</td><td>E-mail id:</td><td>${fv.additional_director_email || '—'}</td></tr>
        <tr><td style="text-align:center;">6</td><td>Mobile no.</td><td>${fv.additional_director_mobile || '—'}</td></tr>
        <tr><td style="text-align:center;">7</td><td>Income-tax PAN</td><td>${fv.additional_director_pan || '—'}</td></tr>
        <tr><td style="text-align:center;">8</td><td>Occupation:</td><td>${fv.additional_director_occupation || '—'}</td></tr>
        <tr><td style="text-align:center;">9</td><td>Educational Qualification</td><td>${fv.additional_director_education || '—'}</td></tr>
        <tr><td style="text-align:center;">10</td><td>Date of Birth</td><td>${fv.additional_director_dob ? new Date(fv.additional_director_dob).toLocaleDateString('en-IN') : '—'}</td></tr>
        <tr><td style="text-align:center;">11</td><td>Place of Birth</td><td>${fv.additional_director_pob || '—'}</td></tr>
        <tr><td style="text-align:center;">12</td><td>Nationality</td><td>${fv.additional_director_nationality || '—'}</td></tr>
        <tr><td style="text-align:center;">13</td><td>No. of companies in which I am already a Director and the names of the companies in which I am a Managing Director, Chief Executive Officer, Whole time Director, Secretary, Chief Financial Officer, Manager.</td><td>—</td></tr>
        <tr><td style="text-align:center;">14</td><td>Particulars of membership No. and Certificate of practice No. if the applicant is a member of any professional Institute. Specifically state NIL if none.</td><td>NIL</td></tr>
      </tbody></table>
      <p class="field-row" style="margin-top:8pt;"><b>Declaration</b></p>
      <p class="body-narrative">I declare that I have not been convicted of any offence in connection with the promotion, formation or management of any company or LLP and have not been found guilty of any fraud or misfeasance or of any breach of duty to any company under this Act or any previous company law in the last five years. I further declare that if appointed my total Directorship in all the companies shall not exceed the prescribed number of companies in which a person can be appointed as a Director.</p>
      <p class="field-row" style="margin-top:10pt;">${(fv.additional_director_name || '—').toUpperCase()} &nbsp;&nbsp;&nbsp; Date: ${dateFmt}</p>
      <p class="field-row">DIN: ${fv.additional_director_din || '—'} &nbsp;&nbsp;&nbsp; Place: __________</p>
      <p class="field-row">Address: ${fv.additional_director_address || '—'}</p>
      <p class="field-row" style="margin-top:8pt;"><b>Attachments</b></p>
      <p class="field-row">1. Proof of identity;</p>
      <p class="field-row">2. Proof of residence;</p>
      `;
    }).join('');
    downloadTransferStatutoryWord(`Form DIR-2 (Consent to Act as Director) - ${item.company_name}`, html);
  };

  const downloadDir8Doc = () => {
    if (directorAppointments.length === 0) return;
    const html = directorAppointments.map((r, i) => {
      const fv = r.field_values || {};
      const dateFmt = fv.additional_director_appointment_date
        ? new Date(fv.additional_director_appointment_date).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' })
        : '__________';
      return `
      ${i > 0 ? '<p class="page-break"></p>' : ''}
      <p class="doc-title" style="text-align:left;">Form DIR-8</p>
      <p class="body-narrative"><b>Intimation by Director</b></p>
      <p class="doc-provision">[Pursuant to section 164(2) and rule 14(1) of Companies (Appointment and Qualification of Directors) Rules, 2014]</p>
      <p class="field-row" style="margin-top:8pt;">Registration No. of Company : ${item.cin_llpin || '__________'}</p>
      <p class="field-row">Nominal Capital : Rs. __________</p>
      <p class="field-row">Paid-up Capital : Rs. __________</p>
      <p class="field-row">Name of Company : ${item.company_name}</p>
      <p class="field-row">Address of Registered Office : ${item.registered_office || '—'}</p>
      <p class="body-narrative" style="margin-top:8pt;">To,<br/>The Board of Directors<br/>${item.company_name}</p>
      <p class="body-narrative" style="margin-top:8pt;">I, ${fv.additional_director_name || '—'}, son/daughter of ${fv.additional_director_father_name || '—'}, resident of ${fv.additional_director_address || '—'}, director in the Company hereby give notice that I am/was a director in the following companies during the last three years:</p>
      <table><tbody>
        <tr><th>S. No.</th><th>Name of the Company</th><th>Date of Appointment</th><th>Date of Cessation</th></tr>
        <tr><td style="text-align:center;">1</td><td style="text-align:center;">NA</td><td style="text-align:center;">-</td><td style="text-align:center;">-</td></tr>
      </tbody></table>
      <p class="body-narrative" style="margin-top:8pt;">I further confirm that I have not incurred disqualification under section 164(2) of the Companies Act, 2013 in any of the above companies, in the previous financial year, and that I, at present, stand free from any disqualification from being a director.</p>
      <p class="field-row" style="margin-top:10pt;">Signature: _______________</p>
      <p class="field-row">${(fv.additional_director_name || '—').toUpperCase()}</p>
      <p class="field-row">DIN: ${fv.additional_director_din || '—'}</p>
      <p class="field-row">Address: ${fv.additional_director_address || '—'}</p>
      <p class="field-row">Date: ${dateFmt}</p>
      <p class="field-row">Place: __________</p>
      `;
    }).join('');
    downloadTransferStatutoryWord(`Form DIR-8 (Non-Disqualification Declaration) - ${item.company_name}`, html);
  };

  // ═══════════════════════════════════════════════════════════════════════
  // DIRECTOR RESIGNATION — a fillable draft of the Resignation Letter
  // (normally the director's own letter, generated here as a ready-to-sign
  // reference/on-file copy, same convention as Request Letters for share
  // transfers). Reads each RES-DIR-002 (Resignation of Director)
  // resolution instance's own field_values — one per resigning director.
  // ═══════════════════════════════════════════════════════════════════════
  const directorResignations = resolutions.filter(r => r.meeting_type === 'BM' && r.field_values?.resigning_director_name);

  const downloadResignationLetterDoc = () => {
    if (directorResignations.length === 0) return;
    const html = directorResignations.map((r, i) => {
      const fv = r.field_values || {};
      const noticeDateFmt = fv.resignation_notice_date
        ? new Date(fv.resignation_notice_date).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' })
        : '__________';
      const effectiveDateFmt = fv.resignation_effective_date
        ? new Date(fv.resignation_effective_date).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' })
        : '__________';
      return `
      ${i > 0 ? '<p class="page-break"></p>' : ''}
      <p class="doc-title" style="text-align:left;">Resignation Letter</p>
      <p class="field-row" style="text-align:right;">Date: ${noticeDateFmt}</p>
      <p class="body-narrative">To,<br/>The Board of Directors<br/>${item.company_name}${item.cin_llpin ? `<br/>CIN: ${item.cin_llpin}` : ''}<br/>Registered Address: ${item.registered_office || '—'}</p>
      <p class="body-narrative" style="margin-top:8pt;"><b>Sub: Resignation from the post of ${fv.resigning_director_designation || 'Director'} of the Company</b></p>
      <p class="body-narrative" style="margin-top:6pt;">Dear Sir(s),</p>
      <p class="body-narrative" style="margin-top:6pt;">I, ${fv.resigning_director_name || '—'} (DIN: ${fv.resigning_director_din || '—'}), ${fv.resigning_director_designation || 'Director'} of the Company, due to personal and unavoidable circumstances, hereby tender my resignation from the position of ${fv.resigning_director_designation || 'Director'} of the Company. Kindly accept this letter as my resignation with effect from ${effectiveDateFmt}.</p>
      <p class="body-narrative" style="margin-top:6pt;">Kindly acknowledge receipt of this resignation letter and arrange to file the necessary forms with the office of the Registrar of Companies to that effect.</p>
      <p class="body-narrative" style="margin-top:8pt;">Thanking you,</p>
      <p class="body-narrative">Yours sincerely,</p>
      <div class="sig-block">
        <p class="sig-for">Signature:</p>
        <div class="sig-name-block">${(fv.resigning_director_name || '—').toUpperCase()}</div>
        <p class="sig-meta">DIN: ${fv.resigning_director_din || '—'}</p>
      </div>
      `;
    }).join('');
    downloadTransferStatutoryWord(`Director Resignation Letter - ${item.company_name}`, html);
  };

  // Manual "Sync Shareholding" — mirrors the meeting register sync
  // pattern: explicit, user-controlled, idempotent on re-click.
  const [syncingShareholding, setSyncingShareholding] = useState(false);
  const syncShareholding = async () => {
    setSyncingShareholding(true);
    try {
      const result = await fetchJson(`/api/mca/event-filings/${item.id}/sync-shareholding`, { method: 'POST' });
      setAlert({
        message: `${result.applied} transfer(s) applied to the shareholder register.${result.note ? ` ${result.note}` : ''}`,
        title: 'Shareholding Synced', variant: 'success',
      });
    } catch (e) {
      setAlert({ message: e.message || 'Could not sync shareholding.', title: 'Sync failed' });
    } finally {
      setSyncingShareholding(false);
    }
  };

  const downloadMeetingNoticeDoc = async (meetingId, type) => {
    const items = getMeetingAgendaItems(meetingId);
    const md = getMeetingData(meetingId);
    const shorter = !!shorterNoticeByMeeting[meetingId];

    // ── Board Meeting Notice — Sec 173(2)/(3) + SS-1. Same statutory
    // engine as AGM/EGM (header/title/notes hierarchy, page-break rules),
    // with BM-specific content: no Ordinary/Special Business split, "Notes
    // on Agenda" (draft resolution text) under each item per SS-1, and the
    // Sec 173(2) video-conferencing intimation note instead of GM's proxy
    // notes. ──
    if (type === 'BM') {
      // Board Meetings are not serially numbered — only AGMs are (they run
      // year-wise, one per financial year). EGMs and Board Meetings have no
      // "Nth Meeting" counting per the user's explicit instruction.
      const meetingDay = md.meeting_date ? new Date(md.meeting_date).toLocaleDateString('en-IN', { weekday: 'long' }) : '—';
      const meetingDateFmt = md.meeting_date ? new Date(md.meeting_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : '—';

      const bmNotes = [];
      bmNotes.push('A Director who intends to participate through Video Conferencing or Other Audio-Visual Means shall intimate the Company/Chairman of his intention sufficiently in advance, in accordance with Section 173(2) of the Companies Act, 2013 read with the applicable Rules, so that the Company can make necessary arrangements.');
      bmNotes.push('Any item(s) of business not included in this Agenda may be taken up with the consent of a majority of the Directors present at the Meeting, at least one of whom shall be an Independent Director, if the Company is required to have one.');
      if (shorter) bmNotes.push('This Meeting is being held at shorter notice, consent for which has been obtained from the requisite majority of Directors in terms of the proviso to Section 173(3) of the Companies Act, 2013.');

      // A Board Meeting Notice states only WHAT will be discussed — the
      // operative "RESOLVED THAT" text belongs in the Minutes, recorded
      // AFTER the Board actually resolves it, never drafted in advance
      // into the Notice calling the meeting. Each agenda item is just a
      // short descriptive line naming the matter.
      const renderBmItem = (i) => `<li>To consider and, if thought fit, approve ${cleanLegalTitle(i.resolution.resolution_title)}.</li>`;
      const headerHtml = buildLetterheadHtml(item);

      const html = `
        <p class="doc-title">Notice of Board Meeting</p>
        <p class="doc-provision">[Pursuant to Section 173 of the Companies Act, 2013 and Secretarial Standard-1]</p>

        <p class="body-narrative">NOTICE is hereby given that the Meeting of the Board of Directors of ${item.company_name} will be held on ${meetingDay}, the ${meetingDateFmt} at ${formatTime12h(md.meeting_time)} at ${md.venue || '__________'}${shorter ? ', on shorter notice,' : ''}, to transact the following business:</p>

        <p class="section-heading">Agenda</p>
        ${items.length ? `<ol class="agenda-list">${items.map(renderBmItem).join('')}</ol>` : '<p class="body-narrative">No agenda items linked to this meeting yet.</p>'}

        <p class="section-heading">Notes</p>
        <div class="statutory-notes">
          ${bmNotes.map((n, i) => `<p class="note-item"><b>${i + 1}.</b> ${n}</p>`).join('')}
        </div>

        <div class="sig-block">
          By Order of the Board<br/>
          For ${item.company_name}<br/><br/><br/>
          _____________________<br/>
          <span class="sig-name">${md.chairperson || 'Director'}</span><br/>
          Director<br/><br/>
          Place: <br/>
          Date: ${md.notice_date ? new Date(md.notice_date).toLocaleDateString('en-IN') : ''}
        </div>
      `;
      downloadNoticeStatutoryWord(`Notice ${type} - ${item.company_name}`, html, headerHtml);
      return;
    }

    const isAGM = type === 'AGM';
    const serial = isAGM ? await getMeetingSerialNumber('AGM') : null;
    const serialLabel = serial ? `${serial}${ordinal(serial)} ` : '';
    const meetingDay = md.meeting_date ? new Date(md.meeting_date).toLocaleDateString('en-IN', { weekday: 'long' }) : '—';
    const meetingDateFmt = md.meeting_date ? new Date(md.meeting_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : '—';

    const ordinaryItems = items.filter(i => i.resolution.business_classification === 'ORDINARY_BUSINESS');
    const specialItems = items.filter(i => i.resolution.business_classification !== 'ORDINARY_BUSINESS');
    const explanatoryItems = specialItems.filter(i => i.resolution.explanatory_statement?.trim());
    const needsNewPage = items.length > 2;

    const renderItem = (i) => `
      <p class="item-title">Item No. ${i.itemNo}: ${cleanLegalTitle(i.resolution.resolution_title)}</p>
      ${splitResolutionParagraphs(i.text).map(p => `<p class="resolution-quote">"${formatResolutionQuote(p)}"</p>`).join('')}
    `;

    const notes = [];
    notes.push('A Member entitled to attend and vote at the Meeting is entitled to appoint a proxy to attend and vote instead of himself/herself, and the proxy need not be a Member of the Company.');
    notes.push('Proxies, in order to be effective, must be deposited at the Registered Office of the Company, duly completed and signed, not less than forty-eight (48) hours before the commencement of the Meeting (Form MGT-11).');
    notes.push('Corporate Members intending to send their authorised representatives to attend the Meeting are requested to send a duly certified copy of the Board Resolution / Power of Attorney authorising their representative to attend and vote on their behalf.');
    notes.push('Voting on the resolution(s) set out in this Notice shall be conducted by poll.');
    if (explanatoryItems.length) notes.push('The Explanatory Statement pursuant to Section 102 of the Companies Act, 2013 in respect of the Special Business set out above is annexed hereto.');
    if (shorter) notes.push(`This Meeting is being held at shorter notice, consent for which has been obtained from the requisite majority of Members in terms of the proviso to Section 101(1) of the Companies Act, 2013.`);

    const headerHtml = buildLetterheadHtml(item);
    const html = `
      <p class="doc-title">Notice of ${isAGM ? 'Annual' : 'Extraordinary'} General Meeting</p>
      <p class="doc-provision">[Pursuant to Section ${isAGM ? '96' : '100'} of the Companies Act, 2013 and applicable Secretarial Standards]</p>

      <p class="body-narrative">NOTICE is hereby given that the ${serialLabel}${isAGM ? 'Annual' : 'Extraordinary'} General Meeting of the Members of ${item.company_name} will be held on ${meetingDay}, the ${meetingDateFmt} at ${formatTime12h(md.meeting_time)} at ${md.venue || '__________'}${shorter ? ', on shorter notice,' : ''} to transact the following business:</p>

      ${ordinaryItems.length ? `<p class="section-heading">Ordinary Business</p>${ordinaryItems.map(renderItem).join('')}` : ''}
      ${specialItems.length ? `<p class="section-heading">Special Business</p>${specialItems.map(renderItem).join('')}` : ''}

      <p class="section-heading">Notes</p>
      <div class="statutory-notes">
        ${notes.map((n, i) => `<p class="note-item"><b>${i + 1}.</b> ${n}</p>`).join('')}
      </div>

      <div class="sig-block">
        By Order of the Board<br/>
        For ${item.company_name}<br/><br/><br/>
        _____________________<br/>
        <span class="sig-name">${md.chairperson || 'Director'}</span><br/>
        Director<br/><br/>
        Place: <br/>
        Date: ${md.notice_date ? new Date(md.notice_date).toLocaleDateString('en-IN') : ''}
      </div>

      ${explanatoryItems.length ? `
        <div class="explanatory-statement${needsNewPage ? ' new-page' : ''}">
          <p class="doc-title">Explanatory Statement</p>
          <p class="doc-provision">[Pursuant to Section 102 of the Companies Act, 2013]</p>
          ${explanatoryItems.map(i => `
            <p class="item-title">Item No. ${i.itemNo}: ${cleanLegalTitle(i.resolution.resolution_title)}</p>
            ${textToHtml(renderExplanatoryStatement(i.resolution.explanatory_statement, i.itemNo, fieldContextFor(i.resolution)))}
          `).join('')}
          <p>None of the Directors or Key Managerial Personnel of the Company, or their relatives, is concerned or interested, financially or otherwise, in the resolution(s) set out above, except to the extent of their shareholding, if any.</p>
        </div>
      ` : ''}
    `;
    downloadNoticeStatutoryWord(`Notice ${type} - ${item.company_name}`, html, headerHtml);
  };

  const downloadMeetingMinutesDoc = async (meetingId, type) => {
    const items = getMeetingAgendaItems(meetingId);
    const md = getMeetingData(meetingId);
    const extras = minutesExtrasByMeeting[meetingId] || {};

    // ── Board Meeting Minutes — Sec 118 + SS-1, same statutory engine as
    // AGM/EGM Minutes (typography, page-break rules), with BM-specific
    // content: Quorum under Sec 174(1) (1/3rd of total directors or 2,
    // whichever is higher — not Sec 103's GM quorum), Sec 184 interest
    // disclosure, confirmation of previous Minutes, and a single Agenda
    // (no Ordinary/Special Business split — that's a GM-only concept). ──
    if (type === 'BM') {
      const attendees = md.attendees || [];
      const roster = directors.map(d => d.director_name);
      const absentees = roster.filter(n => !attendees.includes(n));
      const quorumNeeded = boardQuorum(directors.length);
      const quorumMet = attendees.length >= quorumNeeded;
      const meetingDay = md.meeting_date ? new Date(md.meeting_date).toLocaleDateString('en-IN', { weekday: 'long' }) : '—';
      const meetingDateFmt = md.meeting_date ? new Date(md.meeting_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : '—';
      const mode = md.venue_mode === 'vc' ? 'VC' : 'Physical';

      const presentEntries = attendees.map(name => {
        const d = directors.find(x => x.director_name === name);
        return `<span class="entry-name">${name}</span> — ${d?.designation || 'Director'} (DIN: ${d?.din || '—'}), ${mode}`;
      });
      const otherEntries = absentees.map(name => `<span class="entry-name">${name}</span> — Leave of Absence granted`);
      if (extras.cs_name) otherEntries.push(`<span class="entry-name">${extras.cs_name}</span> — Company Secretary${extras.cs_membership_no ? ` (Mem. No: ${extras.cs_membership_no})` : ''}`);
      if (extras.auditor_name) otherEntries.push(`<span class="entry-name">${extras.auditor_name}</span> — Invitee${extras.auditor_firm_name ? `, M/s ${extras.auditor_firm_name}` : ''}`);

      const renderBmMinutesItem = (i) => {
        const votes = i.resolution.votes || {};
        const { forWeight, againstWeight, abstainWeight } = tallyVotes(votes, attendees, true, shareholders);
        const outcome = i.resolution.status === 'Passed'
          ? (againstWeight === 0 ? 'CARRIED UNANIMOUSLY' : 'CARRIED by requisite majority')
          : i.resolution.status === 'Rejected' ? 'NOT CARRIED' : 'Result pending';
        return `
          <p class="item-title">Item No. ${i.itemNo}: ${cleanLegalTitle(i.resolution.resolution_title)}</p>
          <p class="procedural-narrative">The Chairman placed the matter before the Board for its consideration and, upon deliberation, the Board resolved as under:</p>
          ${i.isFinal
            ? splitResolutionParagraphs(i.text).map(p => `<p class="resolution-quote">"${formatResolutionQuote(p)}"</p>`).join('')
            : `<p class="resolution-quote"><em>${i.isCurrent ? 'Draft — resolution not yet marked Passed.' : `Draft in "${i.caseTitle}" — not yet finalized there.`}</em></p>`}
          ${(forWeight + againstWeight) > 0 ? `<p class="vote-result">Voting — For: ${forWeight}, Against: ${againstWeight}, Abstained: ${abstainWeight}. ${outcome}.</p>` : ''}
        `;
      };

      const headerHtml = buildLetterheadHtml(item);
      const html = `
        <p class="doc-title">Minutes of the Meeting of the Board of Directors</p>
        <p class="opening-narrative">The Meeting was held on ${meetingDay}, the ${meetingDateFmt}, at ${md.venue || '__________'}. The Meeting commenced at ${formatTime12h(md.meeting_time)}${extras.meeting_end_time ? ` and concluded at ${formatTime12h(extras.meeting_end_time)}` : ''}.</p>

        <table class="attendance-box"><thead><tr><th>Directors Present</th><th>Leave of Absence / Invitees Present</th></tr></thead>
          <tbody><tr>
            <td>${presentEntries.length ? presentEntries.map(e => `<p style="margin-bottom:4pt;">${e}</p>`).join('') : 'None recorded.'}</td>
            <td>${otherEntries.length ? otherEntries.map(e => `<p style="margin-bottom:4pt;">${e}</p>`).join('') : 'None recorded.'}</td>
          </tr></tbody>
        </table>

        <p class="proc-heading">1. Quorum</p>
        <p class="procedural-narrative">The Chairman noted that ${attendees.length} Director(s) were present, against a minimum quorum of ${quorumNeeded} required under Section 174(1) of the Companies Act, 2013 (one-third of the total strength of the Board, or two Directors, whichever is higher), and confirmed that ${quorumMet ? 'quorum was present and the Meeting was validly constituted' : 'quorum was NOT present'}.</p>

        <p class="proc-heading">2. Chairperson</p>
        <p class="procedural-narrative">${md.chairperson || '_____________________'} chaired the Meeting in accordance with the Articles of Association of the Company.</p>

        <p class="proc-heading">3. Confirmation of Previous Minutes</p>
        <p class="procedural-narrative">The Minutes of the preceding Meeting of the Board of Directors were tabled before the Board, confirmed as a true and correct record of the proceedings, and signed by the Chairman.</p>

        <p class="proc-heading">4. Disclosure of Interest (Section 184)</p>
        <p class="procedural-narrative">${extras.interest_disclosure?.trim() || 'None of the Directors were concerned or interested, financially or otherwise, in any of the resolutions considered at the Meeting.'}</p>

        <p class="section-heading">Agenda</p>
        ${items.map(renderBmMinutesItem).join('') || '<p class="procedural-narrative">No agenda items linked to this meeting.</p>'}

        <p class="procedural-narrative" style="margin-top:10pt;">There being no further business, the Meeting concluded with a vote of thanks to the Chair.</p>

        <p class="minute-entry">Entered in the Minute Book. Place: ${extras.signatory_place || item.registered_office || '__________'} &nbsp; Date: ${meetingDateFmt}</p>

        <div class="sig-block">
          <span class="sig-name">${md.chairperson || '_____________________'}</span><br/>
          Chairperson of the Meeting<br/>
          <span style="font-weight:normal;font-size:9.5pt;">Each page of these Minutes has been initialed, and this page has been signed, by the Chairperson, in accordance with Section 118 of the Companies Act, 2013 and Secretarial Standard-1.</span>
        </div>
      `;
      downloadMinutesStatutoryWord(`Minutes ${type} - ${item.company_name}`, html, headerHtml);
      return;
    }

    const isAGM = type === 'AGM';
    const attendees = md.attendees || [];
    const categories = extras.attendee_categories || {};
    const proxies = extras.proxies || [];
    const gmDirectorNames = extras.gm_directors_present || [];

    const serial = isAGM ? await getMeetingSerialNumber('AGM') : null;
    const serialLabel = serial ? `${serial}${ordinal(serial)} ` : '';
    const meetingDay = md.meeting_date ? new Date(md.meeting_date).toLocaleDateString('en-IN', { weekday: 'long' }) : '—';
    const meetingDateFmt = md.meeting_date ? new Date(md.meeting_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : '—';

    const quorumCount = attendees.length;
    const quorumRequired = generalMeetingQuorum(companyType, shareholders.length);
    const quorumMet = quorumCount >= quorumRequired;

    const memberEntries = attendees.map(name => {
      const sh = shareholders.find(s => s.member_name === name);
      const cat = ATTENDEE_CATEGORY_LABELS[categories[name] || 'self'];
      return `<span class="entry-name">${name}</span> — Folio ${sh?.folio_number || '—'}, ${sh?.no_of_shares || 0} shares (${cat})`;
    }).concat(proxies.map(p => `<span class="entry-name">${p.name}</span> — Proxy for Folio ${p.represents_folio || '—'} (${p.shares || 0} shares, Form MGT-11)`));

    const directorEntries = gmDirectorNames.map(name => {
      const d = directors.find(x => x.director_name === name);
      return `<span class="entry-name">${name}</span> — ${d?.designation || 'Director'} (DIN: ${d?.din || '—'})`;
    });
    if (extras.cs_name) directorEntries.push(`<span class="entry-name">${extras.cs_name}</span> — Company Secretary (Mem. No: ${extras.cs_membership_no || '—'})`);
    if (extras.auditor_name) directorEntries.push(`<span class="entry-name">${extras.auditor_name}</span> — Statutory Auditor${extras.auditor_firm_name ? `, M/s ${extras.auditor_firm_name}` : ''} (FRN: ${extras.auditor_frn || '—'})`);
    const absentDirectors = directors.filter(d => !gmDirectorNames.includes(d.director_name)).map(d => `<span class="entry-name">${d.director_name}</span> — Leave of Absence granted`);

    const ordinaryItems = items.filter(i => i.resolution.business_classification === 'ORDINARY_BUSINESS');
    const specialItems = items.filter(i => i.resolution.business_classification !== 'ORDINARY_BUSINESS');
    const renderMinutesItem = (i) => {
      const votes = i.resolution.votes || {};
      const { forWeight, againstWeight, abstainWeight } = tallyVotes(votes, attendees, false, shareholders);
      const outcome = i.resolution.status === 'Passed'
        ? (againstWeight === 0 ? 'CARRIED UNANIMOUSLY' : (i.resolution.resolution_type === 'SPECIAL' ? 'CARRIED by the requisite supermajority' : 'CARRIED by requisite majority'))
        : i.resolution.status === 'Rejected' ? 'NOT CARRIED' : 'Result pending';
      return `
        <p class="item-title">Item No. ${i.itemNo}: ${cleanLegalTitle(i.resolution.resolution_title)}</p>
        <p class="procedural-narrative">The following resolution was placed before the Meeting and, upon deliberation, the Members present resolved as under:</p>
        ${i.isFinal
          ? splitResolutionParagraphs(i.text).map(p => `<p class="resolution-quote">"${formatResolutionQuote(p)}"</p>`).join('')
          : '<p class="resolution-quote"><em>Draft — resolution not yet marked Passed.</em></p>'}
        ${(forWeight + againstWeight) > 0 ? `<p class="vote-result">Voting by poll — For: ${forWeight}, Against: ${againstWeight}, Abstained: ${abstainWeight}. ${outcome}.</p>` : ''}
      `;
    };

    const headerHtml = buildLetterheadHtml(item);
    const html = `
      <p class="doc-title">Minutes of the ${serialLabel}${isAGM ? 'Annual General Meeting' : 'Extraordinary General Meeting'}</p>
      <p class="opening-narrative">The Meeting was held on ${meetingDay}, the ${meetingDateFmt}, at ${md.venue || '__________'}. The Meeting commenced at ${formatTime12h(md.meeting_time)}${extras.meeting_end_time ? ` and concluded at ${formatTime12h(extras.meeting_end_time)}` : ''}.</p>

      <table class="attendance-box"><thead><tr><th>Members / Corporate Representatives / Proxies Present</th><th>Directors, KMP &amp; Auditors Present</th></tr></thead>
        <tbody><tr>
          <td>${memberEntries.length ? memberEntries.map(e => `<p style="margin-bottom:4pt;">${e}</p>`).join('') : 'None recorded.'}</td>
          <td>${directorEntries.length ? directorEntries.map(e => `<p style="margin-bottom:4pt;">${e}</p>`).join('') : 'None recorded.'}${absentDirectors.length ? absentDirectors.map(e => `<p style="margin-bottom:4pt;">${e}</p>`).join('') : ''}</td>
        </tr></tbody>
      </table>

      <p class="proc-heading">1. Quorum</p>
      <p class="procedural-narrative">The Chairperson noted that ${quorumCount} Member(s)/Authorised Representative(s) were present in person, against a minimum quorum of ${quorumRequired} required under Section 103(1) of the Companies Act, 2013, and confirmed that ${quorumMet ? 'quorum was present and the Meeting was validly constituted' : 'quorum was NOT present'}.</p>

      <p class="proc-heading">2. Chairperson</p>
      <p class="procedural-narrative">${md.chairperson || '_____________________'} chaired the Meeting in accordance with the Articles of Association of the Company.</p>

      ${ordinaryItems.length ? `<p class="section-heading">Ordinary Business</p>${ordinaryItems.map(renderMinutesItem).join('')}` : ''}
      ${specialItems.length ? `<p class="section-heading">Special Business</p>${specialItems.map(renderMinutesItem).join('')}` : ''}

      <p class="procedural-narrative" style="margin-top:10pt;">There being no further business, the Meeting concluded with a vote of thanks to the Chair.</p>

      <p class="minute-entry">Entered in the Minute Book. Place: ${extras.signatory_place || item.registered_office || '__________'} &nbsp; Date: ${meetingDateFmt}</p>

      <div class="sig-block">
        <span class="sig-name">${md.chairperson || '_____________________'}</span><br/>
        Chairperson of the Meeting
      </div>
    `;
    downloadMinutesStatutoryWord(`Minutes ${type} - ${item.company_name}`, html, headerHtml);
  };

  const downloadAttendanceSheetDoc = async (meetingId, type) => {
    const md = getMeetingData(meetingId);

    // ── Board Meeting Attendance Sheet — Sec 174(1) quorum, same statutory
    // engine as AGM/EGM (header/title/table typography), with BM-specific
    // sections: A. Directors (no members/proxies/corporate-rep concept —
    // that's Sec 105/112/113, general-meeting-only), B. Company Secretary
    // & Invitees/Auditors. ──
    if (type === 'BM') {
      const extras = minutesExtrasByMeeting[meetingId] || {};
      const attendees = md.attendees || [];
      const mode = md.venue_mode === 'vc' ? 'VC' : 'Physical';
      const meetingDay = md.meeting_date ? new Date(md.meeting_date).toLocaleDateString('en-IN', { weekday: 'long' }) : '—';
      const meetingDateFmt = md.meeting_date ? new Date(md.meeting_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : '—';
      const quorumRequired = boardQuorum(directors.length);
      const quorumMet = attendees.length >= quorumRequired;

      const directorRows = directors.map(d => ({
        name: d.director_name, din: d.din || '—', designation: d.designation || 'Director',
        present: attendees.includes(d.director_name),
      }));
      const inviteeRows = [];
      if (extras.cs_name) inviteeRows.push({ name: extras.cs_name, rep: 'Company Secretary', ref: extras.cs_membership_no ? `Mem. No: ${extras.cs_membership_no}` : '—' });
      if (extras.auditor_name) inviteeRows.push({ name: extras.auditor_name, rep: extras.auditor_firm_name ? `Invitee, M/s ${extras.auditor_firm_name}` : 'Invitee', ref: extras.auditor_frn ? `FRN: ${extras.auditor_frn}` : '—' });

      const headerHtml = buildLetterheadHtml(item);
      const html = `
        <p class="doc-title">Attendance Sheet of the Meeting of the Board of Directors</p>

        <table class="meta-table"><tbody>
          <tr><td class="label">Day &amp; Date</td><td>${meetingDay}, ${meetingDateFmt}</td><td class="label">Time of Meeting</td><td>${formatTime12h(md.meeting_time)}</td></tr>
          <tr><td class="label">Venue</td><td colspan="3">${md.venue || '—'}</td></tr>
        </tbody></table>

        <p class="section-heading">A. Attendance of Directors</p>
        <table class="att-table">
          <thead><tr><th style="width:5%;">S. No.</th><th>Name of Director</th><th style="width:16%;">DIN</th><th style="width:22%;">Designation</th><th style="width:14%;">Mode of Presence</th><th class="sig">Signature</th></tr></thead>
          <tbody>
            ${directorRows.map((d, i) => `<tr><td class="num">${i + 1}.</td><td>${d.name}</td><td>${d.din}</td><td>${d.designation}</td><td class="num">${d.present ? mode : 'Absent'}</td><td></td></tr>`).join('') || '<tr><td colspan="6">No directors on record.</td></tr>'}
          </tbody>
        </table>

        <p class="section-heading">B. Company Secretary &amp; Invitees / Auditors Present</p>
        <table class="att-table">
          <thead><tr><th style="width:5%;">S. No.</th><th>Name</th><th>Representation / Capacity</th><th style="width:20%;">Membership / FRN</th><th class="sig">Signature</th></tr></thead>
          <tbody>
            ${inviteeRows.map((r, i) => `<tr><td class="num">${i + 1}.</td><td>${r.name}</td><td>${r.rep}</td><td>${r.ref}</td><td></td></tr>`).join('') || '<tr><td colspan="5">None recorded.</td></tr>'}
          </tbody>
        </table>

        <div class="quorum-block">
          <p class="qb-title">Quorum Verification &amp; Chairman Confirmation</p>
          <p><b>Quorum Determination (Section 174(1)):</b></p>
          <p>Total Directors Present: <b>${attendees.length}</b></p>
          <p>Minimum Quorum Required (one-third of total strength, or 2, whichever is higher): <b>${quorumRequired}</b></p>
          <p>Status: <b>${quorumMet ? 'Quorum Present and Meeting Validly Constituted' : 'Quorum NOT Present'}</b></p>
        </div>

        <div class="sig-block">
          <span class="sig-name">${md.chairperson || '_____________________'}</span><br/>
          Chairperson of the Meeting<br/>
          Date: ${meetingDateFmt}
        </div>
      `;
      downloadAttendanceStatutoryWord(`Attendance Sheet ${type} - ${item.company_name}`, html, headerHtml);
      return;
    }

    const extras = minutesExtrasByMeeting[meetingId] || {};
    const attendees = md.attendees || [];
    const categories = extras.attendee_categories || {};
    const proxies = extras.proxies || [];
    const memberRows = attendees.map(name => {
      const sh = shareholders.find(s => s.member_name === name);
      const shares = Number(sh?.no_of_shares) || 0;
      return {
        name, folio: sh?.folio_number || sh?.dp_id || '—',
        category: ATTENDEE_CATEGORY_LABELS[categories[name] || 'self'],
        shares, pct: totalMemberWeight > 0 ? (shares / totalMemberWeight) * 100 : 0,
      };
    });
    const proxyRows = proxies.map(p => ({
      name: p.name, folio: p.represents_folio || '—', category: 'Proxy (Form MGT-11)',
      shares: Number(p.shares) || 0, pct: totalMemberWeight > 0 ? ((Number(p.shares) || 0) / totalMemberWeight) * 100 : 0,
    }));
    const allSectionARows = [...memberRows, ...proxyRows];
    const totalShares = allSectionARows.reduce((sum, r) => sum + r.shares, 0);
    const totalPct = totalMemberWeight > 0 ? (totalShares / totalMemberWeight) * 100 : 0;

    const gmDirectorNames = extras.gm_directors_present || [];
    const directorRows = gmDirectorNames.map(name => {
      const d = directors.find(x => x.director_name === name);
      return { name, din: d?.din || '—', designation: d?.designation || 'Director' };
    });
    const mode = md.venue_mode === 'vc' ? 'VC' : 'Physical';

    const serial = type === 'AGM' ? await getMeetingSerialNumber('AGM') : null;
    const serialLabel = serial ? `${serial}${ordinal(serial)}` : '';
    const meetingDay = md.meeting_date ? new Date(md.meeting_date).toLocaleDateString('en-IN', { weekday: 'long' }) : '—';
    const meetingDateFmt = md.meeting_date ? new Date(md.meeting_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : '—';

    const quorumCount = attendees.length;
    const quorumRequired = generalMeetingQuorum(companyType, shareholders.length);
    const quorumMet = quorumCount >= quorumRequired;
    const chairpersonDin = directors.find(d => d.director_name === md.chairperson)?.din || '—';

    const headerHtml = buildLetterheadHtml(item);
    const html = `
      <p class="doc-title">Attendance Sheet of the ${serialLabel ? `${serialLabel} ` : ''}${type === 'AGM' ? 'Annual General Meeting' : 'Extraordinary General Meeting'}</p>

      <table class="meta-table"><tbody>
        <tr><td class="label">Day &amp; Date</td><td>${meetingDay}, ${meetingDateFmt}</td><td class="label">Time of Meeting</td><td>${formatTime12h(md.meeting_time)}</td></tr>
        <tr><td class="label">Venue</td><td colspan="3">${md.venue || '—'}</td></tr>
      </tbody></table>

      <p class="section-heading">A. Attendance of Members / Corporate Representatives / Proxies</p>
      <table class="att-table">
        <thead><tr>
          <th style="width:5%;">S. No.</th><th>Name of Member / Authorised Rep / Proxy</th><th>Registered Folio No. / DP ID &amp; Client ID</th>
          <th style="width:16%;">Category</th><th style="width:10%;">No. of Equity Shares Held</th><th style="width:9%;">% of Total Capital</th><th class="sig">Signature</th>
        </tr></thead>
        <tbody>
          ${allSectionARows.map((r, i) => `<tr><td class="num">${i + 1}.</td><td>${r.name}</td><td>${r.folio}</td><td>${r.category}</td><td class="num">${r.shares}</td><td class="num">${r.pct.toFixed(2)}%</td><td></td></tr>`).join('') || '<tr><td colspan="7">No members marked present yet.</td></tr>'}
          ${allSectionARows.length > 0 ? `<tr class="total-row"><td colspan="4" class="num">TOTAL</td><td class="num">${totalShares}</td><td class="num">${totalPct.toFixed(2)}%</td><td></td></tr>` : ''}
        </tbody>
      </table>

      <p class="section-heading">B. Attendance of Directors &amp; Key Managerial Personnel (KMP)</p>
      <table class="att-table">
        <thead><tr><th style="width:5%;">S. No.</th><th>Name of Director / Official</th><th style="width:16%;">DIN / PAN</th><th style="width:22%;">Designation</th><th style="width:14%;">Mode of Presence</th><th class="sig">Signature</th></tr></thead>
        <tbody>
          ${directorRows.map((d, i) => `<tr><td class="num">${i + 1}.</td><td>${d.name}</td><td>${d.din}</td><td>${d.designation}</td><td class="num">${mode}</td><td></td></tr>`).join('')}
          ${extras.cs_name ? `<tr><td class="num">${directorRows.length + 1}.</td><td>${extras.cs_name}</td><td>${extras.cs_membership_no || '—'}</td><td>Company Secretary</td><td class="num">${mode}</td><td></td></tr>` : ''}
          ${directorRows.length === 0 && !extras.cs_name ? '<tr><td colspan="6">No directors/KMP marked present yet.</td></tr>' : ''}
        </tbody>
      </table>

      <p class="section-heading">C. Attendance of Auditors &amp; Special Invitees</p>
      <table class="att-table">
        <thead><tr><th style="width:5%;">S. No.</th><th>Name of Invitee / Auditor</th><th>Representation / Firm Name</th><th style="width:20%;">Membership / FRN</th><th class="sig">Signature</th></tr></thead>
        <tbody>
          ${extras.auditor_name ? `<tr><td class="num">1.</td><td>${extras.auditor_name}</td><td>${extras.auditor_firm_name ? `M/s ${extras.auditor_firm_name}, Statutory Auditors` : 'Statutory Auditors'}</td><td>${extras.auditor_frn ? `FRN: ${extras.auditor_frn}` : '—'}</td><td></td></tr>` : ''}
          ${extras.scrutinizer_name ? `<tr><td class="num">${extras.auditor_name ? 2 : 1}.</td><td>${extras.scrutinizer_name}</td><td>Scrutinizer / Secretarial Auditor</td><td>${extras.scrutinizer_mem_no ? `CP / Mem No: ${extras.scrutinizer_mem_no}` : '—'}</td><td></td></tr>` : ''}
          ${!extras.auditor_name && !extras.scrutinizer_name ? '<tr><td colspan="5">No auditors/special invitees recorded.</td></tr>' : ''}
        </tbody>
      </table>

      <div class="quorum-block">
        <p class="qb-title">Quorum Verification &amp; Chairman Confirmation</p>
        <p><b>Quorum Determination (Section 103):</b></p>
        <p>Total Members Personally Present / Authorised Representatives u/s 113: <b>${quorumCount}</b></p>
        <p>Minimum Quorum Required under Section 103(1): <b>${quorumRequired}</b></p>
        <p>Status: <b>${quorumMet ? 'Quorum Present and Meeting Validly Constituted' : 'Quorum NOT Present'}</b></p>
      </div>

      <div class="sig-block">
        <span class="sig-name">${md.chairperson || '_____________________'}</span><br/>
        Chairperson of the Meeting / Authorised Director<br/>
        DIN: ${chairpersonDin}<br/>
        Date: ${meetingDateFmt}
      </div>
    `;
    downloadAttendanceStatutoryWord(`Attendance Sheet ${type} - ${item.company_name}`, html, headerHtml);
  };

  const downloadMeetingCtcDoc = async (meetingId, type, onlySelected) => {
    const items = getMeetingAgendaItems(meetingId);
    const selection = ctcSelectionByMeeting[meetingId] || {};
    const chosen = onlySelected ? items.filter(i => selection[i.itemNo]) : items;
    if (chosen.length === 0) {
      setAlert({ message: 'Select at least one resolution to include in the CTC.', title: 'Nothing selected' });
      return;
    }
    const md = getMeetingData(meetingId);
    const extras = minutesExtrasByMeeting[meetingId] || {};
    const signatoryName = extras.signatory_name || md.chairperson || '';
    const signatoryRole = extras.signatory_role || 'Director';
    const signatoryMeta = extras.signatory_din_or_membership
      ? (signatoryRole === 'Company Secretary' ? `Membership No: ${extras.signatory_din_or_membership}` : `DIN: ${extras.signatory_din_or_membership}`)
      : '';

    const isBM = type === 'BM';
    // Only AGMs are serially numbered (one per financial year) — Board
    // Meetings and EGMs are not counted, per the user's explicit instruction.
    const serial = type === 'AGM' ? await getMeetingSerialNumber('AGM') : null;
    const serialLabel = serial ? `${serial}${ordinal(serial)} ` : '';
    const meetingDay = md.meeting_date ? new Date(md.meeting_date).toLocaleDateString('en-IN', { weekday: 'long' }) : '—';
    const meetingDateFmt = md.meeting_date ? new Date(md.meeting_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : '—';

    const distinctTypes = [...new Set(chosen.map(i => i.resolution.resolution_type))];
    const singleType = distinctTypes.length === 1 ? distinctTypes[0] : null;
    const typeLabel = (t) => t === 'SPECIAL' ? 'Special' : 'Ordinary';

    // The preamble is always rendered in caps, per the user's explicit
    // instruction, for every meeting type (BM/AGM/EGM) — done by upper-
    // casing the assembled sentence rather than the individual fields, so
    // it stays correct regardless of how the underlying data is cased.
    const preamble = (isBM
      ? `Certified true copy of the resolution${chosen.length > 1 ? 's' : ''} passed by the Board of Directors of ${item.company_name} (CIN: ${item.cin_llpin || '—'}) at the ${serialLabel}Meeting of the Board of Directors of the Company held on ${meetingDay}, the ${meetingDateFmt}, commencing at ${formatTime12h(md.meeting_time)}${extras.meeting_end_time ? ` and concluding at ${formatTime12h(extras.meeting_end_time)}` : ''}, at ${md.venue || '__________'}.`
      : `Certified true copy of the ${singleType ? `<b>${typeLabel(singleType)} Resolution</b>` : 'resolution(s)'} passed at the ${serialLabel}${type === 'AGM' ? 'Annual General Meeting' : 'Extraordinary General Meeting'} of the Members of ${item.company_name} (CIN: ${item.cin_llpin || '—'}) held on ${meetingDay}, the ${meetingDateFmt}, at ${formatTime12h(md.meeting_time)}, at the Registered Office situated at ${md.venue || item.registered_office || '__________'}.`
    ).toUpperCase();

    // CTC of AGM/EGM resolutions must state ORDINARY BUSINESS / SPECIAL
    // BUSINESS (Sec 102 classification) exactly as the Notice does via its
    // section headings — this is independent of the Ordinary/Special
    // Resolution (Sec 114 voting-threshold) label already shown per item.
    // A Board Meeting has no such split (Sec 102 is a general-meeting-only
    // concept), so it keeps a single flat agenda list.
    const renderCtcItem = (i) => `
      <p class="item-title">Item No. ${i.itemNo}: ${cleanLegalTitle(i.resolution.resolution_title)}${!isBM && !singleType ? ` (${typeLabel(i.resolution.resolution_type)} Resolution)` : ''}</p>
      ${i.isFinal
        ? splitResolutionParagraphs(i.text).map(p => `<p class="resolution-quote">"${formatResolutionQuote(p)}"</p>`).join('')
        : '<p class="resolution-quote"><em>Not yet finalized.</em></p>'}
    `;
    const ordinaryChosen = !isBM ? chosen.filter(i => i.resolution.business_classification === 'ORDINARY_BUSINESS') : [];
    const specialChosen = !isBM ? chosen.filter(i => i.resolution.business_classification !== 'ORDINARY_BUSINESS') : [];

    const headerHtml = buildLetterheadHtml(item);
    const html = `
      <p class="doc-title">Certified True Copy of Resolution${chosen.length > 1 ? 's' : ''}</p>
      <p class="preamble">${preamble}</p>

      ${isBM
        ? chosen.map(renderCtcItem).join('')
        : `${ordinaryChosen.length ? `<p class="section-heading">Ordinary Business</p>${ordinaryChosen.map(renderCtcItem).join('')}` : ''}${specialChosen.length ? `<p class="section-heading">Special Business</p>${specialChosen.map(renderCtcItem).join('')}` : ''}`}

      <p class="ctc-banner">CERTIFIED TRUE COPY</p>

      <div class="sig-block">
        <p class="sig-for">For ${item.company_name}</p>
        <div class="sig-name-block">
          _____________________<br/>
          <span class="sig-name">${signatoryName || '__________'}</span><br/>
          ${signatoryRole}
        </div>
        ${signatoryMeta ? `<p class="sig-meta">${signatoryMeta}</p>` : ''}
        <p class="sig-meta">Place: ${extras.signatory_place || item.registered_office || '__________'} &nbsp; Date: ${meetingDateFmt}</p>
      </div>
    `;
    downloadCtcStatutoryWord(`CTC ${type} - ${item.company_name}`, html, headerHtml);
  };

  // ═══════════════════════════════════════════════════════════════════════
  // SHORTER NOTICE CONSENT — a real generated document, not a checkbox.
  // BM: about Independent Director participation/ratification, no
  // shareholding math (Sec 173(3) proviso, SS-1 Para 1.3.11). GM: a real
  // ≥95% weighted-consent threshold (Sec 101(1) proviso) — AGM against
  // members entitled to vote, EGM against paid-up voting capital; we use
  // the same total-shareholding weight both ways since the app doesn't
  // distinguish non-voting share classes. GM consent is given by MEMBERS,
  // in their shareholder capacity — one individually-addressed letter per
  // consenting member (Sec 101(1) + SS-2), not a tabular sheet.
  // ═══════════════════════════════════════════════════════════════════════
  const downloadShorterConsentDoc = async (meetingId, type) => {
    const md = getMeetingData(meetingId);
    const extras = minutesExtrasByMeeting[meetingId] || {};
    const isBM = type === 'BM';
    const consenters = shorterConsentByMeeting[meetingId] || md.attendees || [];

    // ── Board Meeting shorter-notice consent — Sec 173(3) proviso + SS-1
    // Para 1.3.11. Consent is given by Directors (not Members — that's the
    // GM-only Sec 101(1) provision below), recorded as one certificate the
    // consenting Directors sign together, since the Board is one body
    // acting collectively — unlike GM consent, which the law frames as
    // each Member's individual act and so is drafted as separate letters.
    if (isBM) {
      const majorityRequired = Math.floor(directors.length / 2) + 1;
      const consentedCount = consenters.length;
      const majorityMet = consentedCount >= majorityRequired;
      const ids = directors.filter(d => d.is_independent_director);
      const idPresent = ids.some(d => consenters.includes(d.director_name));
      const statusLine = ids.length === 0
        ? 'The Company has no Independent Directors on its Board; consent to the shorter notice was accorded in the ordinary course by the majority of Directors set out below.'
        : idPresent
          ? `At least one Independent Director (${ids.filter(d => consenters.includes(d.director_name)).map(d => d.director_name).join(', ')}) has consented, in accordance with the proviso to Section 173(3) of the Companies Act, 2013 and SS-1 Para 1.3.11.`
          : 'No Independent Director has consented as of this record. Where the Company is required to have an Independent Director, the resolution(s) passed at a meeting held on shorter notice shall be final only on ratification by at least one Independent Director present at the meeting, or in his/her absence, by at least one Independent Director subsequently, in accordance with SS-1 Para 1.3.11.';
      const meetingDateFmt = md.meeting_date ? new Date(md.meeting_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : '—';

      const html = `
        <p class="doc-ref">THE COMPANIES ACT, 2013</p>
        <p class="doc-ref">[Pursuant to the proviso to Section 173(3) of the Companies Act, 2013 and Secretarial Standard-1]</p>
        <p class="doc-subtitle">Consent of Directors for Holding Board Meeting at Shorter Notice</p>

        <p class="meta">${item.company_name}</p>
        <p class="meta">CIN: ${item.cin_llpin || '—'}</p>
        <p class="meta">Regd. Office: ${item.registered_office || '—'}</p>

        <p class="body-text">We, the undersigned Directors of ${item.company_name}, being not less than the majority of the total number of Directors of the Company (${consentedCount} of ${directors.length} Directors, against a majority requirement of ${majorityRequired}), do hereby consent to the Meeting of the Board of Directors of the Company scheduled to be held on ${meetingDateFmt} at ${md.venue || '__________'} being convened at shorter notice than the seven days' notice in writing otherwise required under Section 173(3) of the Companies Act, 2013.</p>
        <p class="body-text">${statusLine}</p>
        <p class="body-text">${majorityMet ? 'This consent is accorded by not less than the majority of Directors, as required by law.' : 'This consent does NOT yet meet the majority-of-Directors threshold required by the proviso to Section 173(3) — additional Directors must consent before this Meeting can validly be held at shorter notice.'}</p>

        <table class="sig-table"><tbody>
          <tr><td class="sig-label" style="width:40%;font-weight:bold;">Director</td><td style="font-weight:bold;">Consented</td><td style="font-weight:bold;">Signature</td></tr>
          ${directors.map(d => `<tr><td>${d.director_name}${d.is_independent_director ? ' (Independent Director)' : ''}${d.din ? `<br/><span style="font-weight:normal;font-size:9.5pt;">DIN: ${d.din}</span>` : ''}</td><td>${consenters.includes(d.director_name) ? 'Yes' : '—'}</td><td></td></tr>`).join('') || '<tr><td colspan="3">No directors on record.</td></tr>'}
        </tbody></table>

        <p class="sig-label" style="margin-top:20pt;">Date: ${md.notice_date ? new Date(md.notice_date).toLocaleDateString('en-IN') : '__________'} &nbsp; Place: ${extras.signatory_place || item.registered_office || '__________'}</p>
      `;
      downloadStatutoryWord(`Shorter Notice Consent BM - ${item.company_name}`, html);
      return;
    }

    const consentingMembers = shareholders.filter(s => consenters.includes(s.member_name));
    if (consentingMembers.length === 0) {
      setAlert({ message: 'No members are marked as consenting yet — tick them above first.', title: 'Nothing to generate' });
      return;
    }

    const isAGM = type === 'AGM';
    const serial = isAGM ? await getMeetingSerialNumber('AGM') : null;
    const serialLabel = serial ? `${serial}${ordinal(serial)} ` : '';
    const meetingDay = md.meeting_date ? new Date(md.meeting_date).toLocaleDateString('en-IN', { weekday: 'long' }) : '—';
    const meetingDateFmt = md.meeting_date ? new Date(md.meeting_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : '—';

    const docTitle = isAGM
      ? 'Consent by Shareholder / Member for Holding Annual General Meeting at Shorter Notice'
      : 'Consent by Shareholder / Member for Holding Extra-Ordinary General Meeting at Shorter Notice';
    const subject = isAGM
      ? `Consent for convening the ${serialLabel}Annual General Meeting of ${item.company_name} at Shorter Notice`
      : `Consent for convening an Extra-Ordinary General Meeting ("EGM") of ${item.company_name} at Shorter Notice`;
    const holdPhrase = isAGM ? `hold the ${serialLabel}Annual General Meeting of the Company` : 'hold the Extra-Ordinary General Meeting of the Company';
    const trailingClause = isAGM ? '' : ', to transact the Special Business as set out in the draft Notice of the EGM';

    const letters = consentingMembers.map(s => {
      const shares = Number(s.no_of_shares) || 0;
      const faceValue = Number(s.face_value) || 10;
      const pct = totalMemberWeight > 0 ? (shares / totalMemberWeight) * 100 : 0;
      const folioOrClientId = s.folio_number || s.dp_id || '—';
      return `
        <div class="letter-page">
          <p class="doc-ref">THE COMPANIES ACT, 2013</p>
          <p class="doc-ref">[Pursuant to Section 101(1) of the Companies Act, 2013 and Secretarial Standard-2]</p>
          <p class="doc-subtitle">${docTitle}</p>

          <p class="meta">To,</p>
          <p class="meta">The Board of Directors,</p>
          <p class="meta">${item.company_name}</p>
          <p class="meta">CIN: ${item.cin_llpin || '—'}</p>
          <p class="meta">Regd. Office: ${item.registered_office || '—'}</p>

          <p class="subject">Subject: ${subject}</p>

          <p class="body-text">Dear Sir(s) / Madam(s),</p>
          <p class="body-text">I / We, <b>${s.member_name}</b>, residing at / having registered office at ${s.address || '__________'}, holding ${shares} (${numberToWordsIndian(shares)}) Equity Shares of INR ${faceValue}/- (Indian Rupees ${numberToWordsIndian(faceValue)} only) each, fully paid-up, representing ${pct.toFixed(2)}% of the total paid-up equity share capital carrying voting rights of ${item.company_name} ("the Company"), registered under Folio No. / DP ID &amp; Client ID: ${folioOrClientId}, do hereby give my / our consent pursuant to the proviso to Section 101(1) of the Companies Act, 2013 read with Secretarial Standard-2 on General Meetings, to ${holdPhrase} on shorter notice on ${meetingDay}, the ${meetingDateFmt} at ${formatTime12h(md.meeting_time)} at ${md.venue || '__________'} or any adjournment thereof${trailingClause}.</p>
          <p class="body-text">I / We hereby confirm that this consent shall be treated as valid consent in writing under the provisions of the Companies Act, 2013.</p>
          <p class="body-text">Thanking you,</p>
          <p class="body-text">Yours faithfully,</p>

          <table class="sig-table"><tbody>
            <tr><td colspan="2">____________________________________________</td></tr>
            <tr><td colspan="2">Signature of the Member / Authorised Signatory</td></tr>
            <tr><td colspan="2" style="height:10pt;"></td></tr>
            <tr><td class="sig-label">Name of Member</td><td>: ${s.member_name}</td></tr>
            <tr><td class="sig-label">Folio No. / DP-Client ID</td><td>: ${folioOrClientId}</td></tr>
            <tr><td class="sig-label">No. of Shares Held</td><td>: ${shares}</td></tr>
            <tr><td class="sig-label">Percentage of Holding</td><td>: ${pct.toFixed(2)}%</td></tr>
            <tr><td colspan="2" style="height:10pt;"></td></tr>
            <tr><td class="sig-label">Date</td><td>: ${md.notice_date ? new Date(md.notice_date).toLocaleDateString('en-IN') : '__________'}</td></tr>
            <tr><td class="sig-label">Place</td><td>: __________</td></tr>
          </tbody></table>
        </div>
      `;
    }).join('');

    downloadStatutoryWord(`Shorter Notice Consent ${type} - ${item.company_name}`, letters);
  };

  const toggleShorterConsent = (meetingId, name) => setShorterConsentByMeeting(prev => {
    const current = prev[meetingId] || getMeetingData(meetingId).attendees || [];
    const next = current.includes(name) ? current.filter(n => n !== name) : [...current, name];
    return { ...prev, [meetingId]: next };
  });

  const toggleCtcSelection = (meetingId, itemNo) => setCtcSelectionByMeeting(prev => ({
    ...prev,
    [meetingId]: { ...prev[meetingId], [itemNo]: !prev[meetingId]?.[itemNo] },
  }));

  // Attendance Sheet / Minutes (AGM/EGM) extras — member category (Sec
  // 103/112/113 all count toward quorum; Sec 105 proxies don't, so
  // they're a wholly separate list), directors/KMP present as invitees,
  // and auditor/CS/scrutinizer particulars. Same minutesExtrasByMeeting
  // bag everything else here already rides in, so it round-trips through
  // the existing autosave with no further wiring.
  const setAttendeeCategory = (meetingId, name, category) => setMinutesExtrasByMeeting(prev => ({
    ...prev, [meetingId]: { ...prev[meetingId], attendee_categories: { ...(prev[meetingId]?.attendee_categories || {}), [name]: category } },
  }));
  const toggleGmDirectorPresent = (meetingId, name) => setMinutesExtrasByMeeting(prev => {
    const current = prev[meetingId]?.gm_directors_present || [];
    const next = current.includes(name) ? current.filter(n => n !== name) : [...current, name];
    return { ...prev, [meetingId]: { ...prev[meetingId], gm_directors_present: next } };
  });
  const [proxyDraft, setProxyDraft] = useState({});
  const updateProxyDraft = (meetingId, patch) => setProxyDraft(prev => ({ ...prev, [meetingId]: { ...prev[meetingId], ...patch } }));
  const addProxy = (meetingId) => {
    const draft = proxyDraft[meetingId];
    if (!draft?.name?.trim()) return;
    setMinutesExtrasByMeeting(prev => {
      const current = prev[meetingId]?.proxies || [];
      return { ...prev, [meetingId]: { ...prev[meetingId], proxies: [...current, { id: `proxy_${Date.now()}`, name: draft.name.trim(), represents_folio: draft.folio || '', shares: draft.shares || 0 }] } };
    });
    setProxyDraft(prev => ({ ...prev, [meetingId]: { name: '', folio: '', shares: '' } }));
  };
  const removeProxy = (meetingId, proxyId) => setMinutesExtrasByMeeting(prev => {
    const current = prev[meetingId]?.proxies || [];
    return { ...prev, [meetingId]: { ...prev[meetingId], proxies: current.filter(p => p.id !== proxyId) } };
  });

  // ═══════════════════════════════════════════════════════════════════════
  // STATUTORY REGISTER SYNC — manual, per meeting. BM goes to
  // mca_board_meetings (+ per-director attendance rows); AGM/EGM goes to
  // mca_general_meetings. Only date/time/venue/mode/chairman/quorum/
  // attendance are filled from what Event Filings actually tracks —
  // register-only fields (meeting_number, financial_year, SRN, company
  // secretary, notice_mode, etc.) are left for manual entry in Registers.
  // If a register row already exists for this company+date, the user is
  // asked whether to update it or record a separate new row.
  // ═══════════════════════════════════════════════════════════════════════
  const requestRegisterSync = (meetingId, type) => {
    const md = getMeetingData(meetingId);
    if (!md.meeting_date) {
      setAlert({ message: 'Set the meeting date in Meeting Particulars first.', title: 'Incomplete' });
      return;
    }
    const attendees = md.attendees || [];
    if (attendees.length === 0) {
      setConfirm({
        title: 'No one marked present',
        message: `Nobody is ticked as present for this ${type === 'BM' ? 'Board Meeting' : 'meeting'} in Meeting Particulars, so it will sync with 0 attendance and quorum not met. Continue anyway, or go back and mark attendance first?`,
        confirmLabel: 'Sync Anyway',
        onCancel: () => setConfirm(null),
        onConfirm: () => { setConfirm(null); checkAndSyncRegister(meetingId, type); },
      });
      return;
    }
    checkAndSyncRegister(meetingId, type);
  };

  const checkAndSyncRegister = async (meetingId, type) => {
    const md = getMeetingData(meetingId);
    setRegisterSyncingId(meetingId);
    try {
      const listUrl = type === 'BM'
        ? `/api/mca/companies/${item.company_id}/board-meetings`
        : `/api/mca/companies/${item.company_id}/general-meetings`;
      const list = await fetchJson(listUrl);
      const targetDate = String(md.meeting_date).slice(0, 10);
      const rows = Array.isArray(list) ? list : [];
      const match = rows.find(m =>
        String(m.meeting_date).slice(0, 10) === targetDate && (type === 'BM' || m.meeting_type === type)
      );
      if (match) {
        setRegisterSyncingId(null);
        setRegisterSyncChoice({ meetingId, type, existing: match });
      } else {
        // Serial number continues on from whatever's already in this
        // company's register for this meeting type, so it doesn't need
        // to be typed in by hand.
        await performRegisterSync(meetingId, type, null, rows.length + 1);
      }
    } catch (e) {
      setAlert({ message: 'Could not check the Statutory Register for an existing entry.', title: 'Sync failed' });
      setRegisterSyncingId(null);
    }
  };

  const performRegisterSync = async (meetingId, type, existing, serialHint) => {
    setRegisterSyncingId(meetingId);
    setRegisterSyncChoice(null);
    try {
      const md = getMeetingData(meetingId);
      const isBM = type === 'BM';
      const attendees = md.attendees || [];
      const mode = md.venue_mode === 'vc' ? 'Video Conference' : 'Physical';
      const meetingDay = new Date(md.meeting_date).toLocaleDateString('en-IN', { weekday: 'long' });
      const noticeDate = md.notice_date || (item.created_at ? String(item.created_at).slice(0, 10) : null);
      const serialNumber = existing ? existing.serial_number : serialHint;

      const payload = isBM ? {
        serial_number: serialNumber,
        meeting_day: meetingDay,
        status: 'Held',
        meeting_date: md.meeting_date,
        meeting_time: md.meeting_time || null,
        venue: md.venue || null,
        mode,
        notice_date: noticeDate,
        notice_mode: 'Physical',
        total_strength: directors.length,
        quorum_required: boardQuorum(directors.length),
        quorum_present: attendees.length >= boardQuorum(directors.length),
        chairman: md.chairperson || null,
        event_filing_meeting_id: meetingId,
      } : {
        serial_number: serialNumber,
        meeting_day: meetingDay,
        status: 'Held',
        meeting_type: type,
        meeting_date: md.meeting_date,
        meeting_time: md.meeting_time || null,
        venue: md.venue || null,
        mode,
        notice_date: noticeDate,
        notice_mode: 'Physical',
        total_members: shareholders.length,
        members_present: attendees.length,
        quorum_present: attendees.length >= generalMeetingQuorum(companyType, shareholders.length),
        event_filing_meeting_id: meetingId,
        chairman: md.chairperson || null,
        shorter_notice: !!shorterNoticeByMeeting[meetingId],
      };

      const base = isBM
        ? `/api/mca/companies/${item.company_id}/board-meetings`
        : `/api/mca/companies/${item.company_id}/general-meetings`;
      const saved = existing
        ? await fetchJson(`${base}/${existing.id}`, { method: 'PUT', body: JSON.stringify({ ...existing, ...payload }) })
        : await fetchJson(base, { method: 'POST', body: JSON.stringify(payload) });

      if (isBM) {
        const existingAttendance = existing
          ? await fetchJson(`${base}/${saved.id}/attendance`).catch(() => [])
          : [];
        await Promise.all(directors.map(d => {
          const attended = attendees.includes(d.director_name);
          const existingRow = existingAttendance.find(a => a.director_id === d.id || a.director_name === d.director_name);
          return existingRow
            ? fetchJson(`${base}/${saved.id}/attendance/${existingRow.id}`, { method: 'PUT', body: JSON.stringify({ attended, mode_of_attendance: mode, din: d.din || null, designation: d.designation || 'Director' }) }).catch(() => {})
            : fetchJson(`${base}/${saved.id}/attendance`, { method: 'POST', body: JSON.stringify({ director_id: d.id, director_name: d.director_name, din: d.din || null, designation: d.designation || 'Director', mode_of_attendance: mode, attended }) }).catch(() => {});
        }));

        // Agenda — one row per resolution in this meeting's full agenda.
        // Only Board Meetings have an agenda sub-resource in the register.
        const agendaItems = getMeetingAgendaItems(meetingId);
        const mgt14Applicable = filings.some(f => f.form === 'MGT-14');
        const existingAgenda = existing
          ? await fetchJson(`${base}/${saved.id}/agendas`).catch(() => [])
          : [];
        await Promise.all(agendaItems.map(i => {
          const votes = i.resolution.votes || {};
          const presentRoster = i.resolution.meeting_data?.attendees || [];
          const { forWeight, againstWeight, abstainWeight } = tallyVotes(votes, presentRoster, true, shareholders);
          const agendaPayload = {
            serial_number: i.itemNo,
            agenda_title: i.resolution.resolution_title,
            agenda_type: 'Resolution',
            resolution_required: true,
            resolution_type: i.resolution.resolution_type === 'SPECIAL' ? 'Special' : 'Ordinary',
            resolution_text: i.text || null,
            resolution_passed: i.resolution.status === 'Passed' ? true : i.resolution.status === 'Rejected' ? false : null,
            votes_for: forWeight,
            votes_against: againstWeight,
            votes_abstained: abstainWeight,
            mgt14_applicable: mgt14Applicable,
          };
          const existingRow = existingAgenda.find(a => a.serial_number === i.itemNo || a.agenda_title === i.resolution.resolution_title);
          return existingRow
            ? fetchJson(`${base}/${saved.id}/agendas/${existingRow.id}`, { method: 'PUT', body: JSON.stringify(agendaPayload) }).catch(() => {})
            : fetchJson(`${base}/${saved.id}/agendas`, { method: 'POST', body: JSON.stringify(agendaPayload) }).catch(() => {});
        }));
      }

      setAlert({
        message: `${meetingLabel(type)} ${existing ? 'updated in' : 'recorded in'} the Statutory Register${isBM ? ', with attendance and agenda' : ''}. Register-only fields (meeting number, SRN, etc.) still need filling in directly there.`,
        title: existing ? 'Register Updated' : 'Register Updated',
        variant: 'success',
      });
    } catch (e) {
      setAlert({ message: e.message || 'Could not sync this meeting to the register.', title: 'Sync failed' });
    } finally {
      setRegisterSyncingId(null);
    }
  };

  const updateFiling = (idx, patch) => setFilings(prev => prev.map((f, i) => i === idx ? { ...f, ...patch } : f));

  // Filing & Tracking is fully auto-transitioning — no "Mark as Filed" /
  // "Approved" / "Rejected" buttons. The data entered IS the decision:
  //   SRN + Filing Date both present  → Filed
  //   Approval Date entered           → Approved (Rejection Reason cleared)
  //   Rejection Reason entered        → Rejected (Approval Date cleared)
  // Once Approved/Rejected every field locks; "Edit" (below) is the only
  // way back, same escape-hatch pattern as resolutions' "↺ Revise".
  const updateFilingAuto = (idx, patch) => {
    setFilings(prev => prev.map((f, i) => {
      if (i !== idx) return f;
      const next = { ...f, ...patch };
      if ('rejection_reason' in patch && patch.rejection_reason?.trim()) {
        next.status = 'Rejected';
        next.approval_date = null;
      } else if ('approval_date' in patch && isPlausibleDate(patch.approval_date)) {
        next.status = 'Approved';
        next.rejection_reason = '';
      } else if (next.status === 'Pending' && next.srn?.trim() && isPlausibleDate(next.filing_date)) {
        next.status = 'Filed';
      }
      return next;
    }));
  };

  const editFiling = (idx) => {
    setConfirm({
      title: 'Edit this filing?',
      message: `This form is marked ${filings[idx]?.status}. Editing unlocks the Approval Date / Rejection Reason for correction and reverts status to Filed until you re-enter one.`,
      confirmLabel: 'Edit',
      onCancel: () => setConfirm(null),
      onConfirm: () => { updateFiling(idx, { status: 'Filed', approval_date: null, rejection_reason: '' }); setConfirm(null); },
    });
  };

  // Fires the register/company-master sync exactly once, the moment
  // every form on this case is Approved — not on a button click.
  const approvalSyncRef = useRef(false);
  const runApprovalSync = async () => {
    if (approvalSyncRef.current) return;
    approvalSyncRef.current = true;
    setSyncing(true);
    while (savingRef.current) await new Promise(r => setTimeout(r, 50));
    savingRef.current = true;
    try {
      await fetchJson(`/api/mca/event-filings/${item.id}/details`, {
        method: 'PATCH',
        body: JSON.stringify({ filings, status: 'Approved', event_data: eventData }),
      });
      const synced = await fetchJson(`/api/mca/event-filings/${item.id}/sync`, { method: 'POST' });
      setItem(prev => ({ ...prev, synced_at: synced.synced_at }));
      setAlert({
        message: synced.sync_result?.synced
          ? `This case's outcome has been pushed into ${synced.sync_result.target?.replace('_', ' ')}.`
          : (synced.sync_result?.note || 'All filings approved — no automatic sync configured for this event type yet, update the register manually.'),
        title: synced.sync_result?.synced ? 'Synced' : 'All Filings Approved',
        variant: synced.sync_result?.synced ? 'success' : 'error',
      });
    } catch {
      setAlert({ message: 'All filings approved, but the sync call failed — try again from the Documents tab.', title: 'Sync error' });
    } finally {
      setSyncing(false);
      savingRef.current = false;
      if (dirtyRef.current) saveCase();
      approvalSyncRef.current = false;
    }
  };

  useEffect(() => {
    if (loading) return;
    if (filings.length > 0 && filings.every(f => f.status === 'Approved') && !item.synced_at) {
      runApprovalSync();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filings, item.synced_at, loading]);

  const updateChecklistItem = (id, field, value) => setChecklist(prev => prev.map(c => c.id === id ? { ...c, [field]: value } : c));
  const removeChecklistItem = (id) => setChecklist(prev => prev.filter(c => c.id !== id));
  const addChecklistItem = (task) => { if (!task.trim()) return; setChecklist(prev => [...prev, { id: `att_${Date.now()}`, task, status: 'Pending', remarks: '' }]); };

  if (loading) return <div className="py-24 text-center text-gray-400 text-sm">Loading case…</div>;

  const meetingGroups = [...new Set(resolutions.map(r => r.meeting_id).filter(Boolean))]
    .map(mid => ({ meetingId: mid, type: resolutions.find(r => r.meeting_id === mid)?.meeting_type }));

  const tabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'details', label: 'Event Details' },
    { key: 'resolutions', label: `Resolutions (${resolutions.length})` },
    { key: 'documents', label: `Documents (${meetingGroups.length})` },
    { key: 'attachments', label: `Attachments (${checklist.length})` },
    { key: 'filing', label: `Filing & Tracking (${filings.length})` },
  ];

  return (
    <div className="space-y-5">
      <style>{DARK_STYLE_OVERRIDES}</style>
      {alert && <AlertModal {...alert} onClose={() => setAlert(null)} />}
      {confirm && <ConfirmModal {...confirm} />}
      {registerSyncChoice && (
        <RegisterSyncChoiceModal
          title="Existing register entry found"
          message={`${meetingLabel(registerSyncChoice.type)} on this date already has an entry in the Statutory Register. Update it with this meeting's details, or record a separate new row?`}
          onUpdate={() => performRegisterSync(registerSyncChoice.meetingId, registerSyncChoice.type, registerSyncChoice.existing)}
          onCreateNew={() => performRegisterSync(registerSyncChoice.meetingId, registerSyncChoice.type, null, (registerSyncChoice.existing?.serial_number || 0) + 1)}
          onCancel={() => setRegisterSyncChoice(null)}
        />
      )}

      <div className="flex items-center justify-between">
        <div>
          <button onClick={handleBack} className="group text-xs font-semibold text-gray-400 hover:text-[#0f1f45] mb-1.5 flex items-center gap-1.5 transition-colors">
            <span className="inline-flex transition-transform duration-200 group-hover:-translate-x-0.5">{Icon.back}</span>
            Back to Event Filings
          </button>
          <h3 className="text-xl font-bold text-[#0f1f45]">{item.title}</h3>
          <p className="text-sm text-gray-500 mt-0.5">{item.company_name} · {item.event_name}</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[11px] text-gray-400">{saveStatus === 'saving' ? 'Saving…' : saveStatus === 'saved' ? 'Saved' : saveStatus === 'error' ? 'Save failed' : ''}</span>
          <span className={`px-3 py-1.5 rounded-full text-xs font-semibold ${STATUS_COLORS[status] || 'bg-gray-100 text-gray-600'}`}>{status}</span>
          <button onClick={handleDeleteCase} title="Delete case" className="w-8 h-8 rounded-lg border border-gray-200 text-gray-400 hover:text-red-500 hover:border-red-200 flex items-center justify-center">{Icon.trash}</button>
        </div>
      </div>

      {allSiblingCases.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
          <p className="text-xs font-semibold text-gray-600 mb-2">🗓 Meeting Round — this meeting covers {allSiblingCases.length + 1} case{allSiblingCases.length + 1 !== 1 ? 's' : ''}</p>
          <div className="flex flex-wrap gap-2">
            <span className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-blue-100 text-blue-700">{item.title} (this case)</span>
            {allSiblingCases.map(c => (
              <button key={`${c.module || 'event'}-${c.id}`}
                onClick={() => c.module === 'annual'
                  ? (onNavigate ? onNavigate('annual-filings', { caseId: c.id }) : setAlert({ message: 'Open this from Annual Filings.', title: 'Different module' }))
                  : onOpenSibling({ id: c.id })}
                className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-gray-100 text-gray-600 hover:bg-gray-200">
                {c.event_name || c.title} · {c.status}{c.module === 'annual' ? ' ↗' : ''}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm">
        <div className="flex border-b border-gray-100 overflow-x-auto">
          {tabs.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className={`px-5 py-3.5 text-sm font-semibold whitespace-nowrap border-b-2 -mb-px ${activeTab === tab.key ? 'border-[#0f1f45] text-[#0f1f45]' : 'border-transparent text-gray-400 hover:text-gray-600'}`}>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'overview' && (
        <div className="grid grid-cols-3 gap-5">
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden col-span-3">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h4 className="text-base font-bold text-[#0f1f45]">Status Panel</h4>
                <p className="text-xs text-gray-400 mt-0.5">Click any row to jump straight there.</p>
              </div>
              <span className={`px-3 py-1.5 rounded-full text-xs font-semibold ${completeCount === statusRows.length ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                {completeCount} / {statusRows.length} complete
              </span>
            </div>
            <div className="divide-y divide-gray-50">
              {statusRows.map(row => (
                <button key={row.label} onClick={() => setActiveTab(row.tab)} className="w-full flex items-center justify-between px-5 py-3 hover:bg-gray-50 text-left">
                  <div className="flex items-center gap-3">
                    <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${row.ok ? 'bg-green-500' : 'bg-amber-400'}`} />
                    <span className="text-sm font-medium text-gray-700">{row.label}</span>
                  </div>
                  <span className={`text-xs ${row.ok ? 'text-green-600' : 'text-amber-600'}`}>{row.detail}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 col-span-2">
            <h4 className="text-base font-bold text-[#0f1f45] mb-4">Stage Progress</h4>
            <div className="space-y-3">
              {STAGE_ORDER.map((stage, index) => {
                const currentIndex = status === 'Rejected' ? -1 : STAGE_ORDER.indexOf(status);
                const done = index < currentIndex;
                const current = index === currentIndex;
                return (
                  <div key={stage} className="flex items-center gap-3">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${done ? 'bg-green-100 text-green-700' : current ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-400'}`}>
                      {done ? '✓' : index + 1}
                    </div>
                    <p className={`text-sm ${current ? 'font-semibold text-gray-800' : 'text-gray-500'}`}>{stage}</p>
                  </div>
                );
              })}
              {status === 'Rejected' && (
                <div className="flex items-center gap-3 mt-2 pt-2 border-t border-gray-100">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold bg-red-100 text-red-700">✕</div>
                  <p className="text-sm font-semibold text-red-600">One or more filings rejected — see Filing & Tracking</p>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
            <h4 className="text-base font-bold text-[#0f1f45] mb-4">Case Summary</h4>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-gray-400">Company</span><span className="text-gray-700 font-medium">{item.company_name}</span></div>
              <div className="flex justify-between"><span className="text-gray-400">Event</span><span className="text-gray-700 font-medium">{item.event_name}</span></div>
              <div className="flex justify-between"><span className="text-gray-400">Resolutions</span><span className="text-gray-700 font-medium">{resolutions.length}</span></div>
              <div className="flex justify-between"><span className="text-gray-400">Forms</span><span className="text-gray-700 font-medium">{filings.map(f => f.form).join(', ') || '—'}</span></div>
              <div className="flex justify-between"><span className="text-gray-400">Created</span><span className="text-gray-700 font-medium">{item.created_at ? new Date(item.created_at).toLocaleDateString('en-IN') : '—'}</span></div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'details' && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
          <h4 className="text-base font-bold text-[#0f1f45] mb-1">Event Details</h4>
          <p className="text-sm text-gray-400 mb-5">Grouped by resolution — only the resolutions actually linked to this case (Resolutions tab) appear here. If a field shouldn't be here, remove that resolution on the Resolutions tab instead.</p>
          {resolutionsWithPlaceholders.length === 0 ? (
            <p className="text-sm text-gray-400">No {'{{placeholders}}'} in any linked resolution — nothing to fill in here.</p>
          ) : (
            <div className="space-y-4">
              {resolutionsWithPlaceholders.map(({ resolution: r, resIdx, instanceLabel, keys, choiceFields }, i) => {
                const accent = meetingAccent(r.meeting_type);
                return (
                  <div key={i} className={`rounded-lg border border-gray-100 border-l-4 ${accent.border} p-4`}>
                    <p className={`text-xs font-semibold mb-3 ${accent.text}`}>{r.resolution_title}{instanceLabel} <span className="font-normal text-gray-400">— {r.meeting_type}</span></p>
                    {keys.includes('transfers_table') && (
                      <div className="mb-4">
                        <TransferRowsEditor
                          transfers={eventData.transfers || []}
                          onChange={next => setEventData(prev => ({ ...prev, transfers: next }))}
                          shareholders={shareholders}
                          defaultDate={r.meeting_data?.meeting_date || ''}
                        />
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-4">
                      {keys.filter(key => !TRANSFER_COMPUTED_KEYS.includes(key)).map(key => {
                        const choiceOptions = choiceFields[key];
                        return (
                          <div key={key} className={NUMBERED_CLAUSE_KEYS.includes(key) ? 'col-span-2' : ''}>
                            <label className="block text-xs font-semibold text-gray-600 mb-1.5">{humanizeKey(key)}</label>
                            {NUMBERED_CLAUSE_KEYS.includes(key) ? (
                              <NumberedClauseEditor
                                clauses={resolutionFieldValue(r, `${key}__clauses`) || ['']}
                                onChange={next => updateResolutionField(resIdx, { [`${key}__clauses`]: next, [key]: formatNumberedClauses(next) })}
                              />
                            ) : choiceOptions ? (
                              <select
                                value={resolutionFieldValue(r, key)}
                                onChange={e => updateResolutionField(resIdx, { [key]: e.target.value })}
                                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-blue-500"
                              >
                                <option value="">Select…</option>
                                {choiceOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                              </select>
                            ) : isDateKey(key) ? (
                              <input
                                type="date"
                                value={resolutionFieldValue(r, key)}
                                onChange={e => updateResolutionField(resIdx, { [key]: e.target.value })}
                                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-blue-500"
                              />
                            ) : (
                              <input
                                value={resolutionFieldValue(r, key)}
                                onChange={e => updateResolutionField(resIdx, { [key]: e.target.value })}
                                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-blue-500"
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <div className="mt-5 pt-5 border-t border-gray-100">
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Event Date <span className="font-normal text-gray-400">— general record-keeping only</span></label>
            <input type="date" value={eventDate} onChange={e => setEventDate(e.target.value)}
              className="w-48 border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-blue-500" />
            <p className="text-[11px] text-gray-400 mt-1.5 max-w-md">
              Just for sorting/filtering this case in the list — it doesn't appear anywhere in the resolution text. Any date fields <em>above</em> (Proof Date, Effective Date, etc.) are what actually get used in the drafted resolution; the Board/General Meeting date is set separately on the Resolutions tab.
            </p>
          </div>
        </div>
      )}

      {activeTab === 'resolutions' && (
        <div className="space-y-5">
          {resolutions.length === 0 && (
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-10 text-center text-gray-400 text-sm">This event has no linked resolutions.</div>
          )}
          {resolutions.map((r, idx) => {
            const accent = meetingAccent(r.meeting_type);
            const isPassed = r.status === 'Passed';
            const isRejected = r.status === 'Rejected';
            const locked = isPassed || isRejected;
            return (
              <div key={idx} className={`bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden border-l-4 ${accent.border}`}>
                <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <IconBadge accent={accent}>{Icon.meeting}</IconBadge>
                    <div>
                      <p className="text-sm font-bold text-[#0f1f45]">{r.resolution_title}</p>
                      <p className={`text-[11px] mt-0.5 ${accent.textLight}`}>
                        {r.resolution_type} Resolution · {r.meeting_type}
                        {r.meeting_type !== 'BM' && r.business_classification && (r.business_classification === 'ORDINARY_BUSINESS' ? ' · Ordinary Business' : ' · Special Business')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${isPassed ? 'bg-green-100 text-green-700' : isRejected ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>{r.status}</span>
                    {!locked && <button onClick={() => removeResolution(idx)} title="Remove this resolution" className="text-gray-300 hover:text-red-500">{Icon.trash}</button>}
                  </div>
                </div>
                {locked && (
                  <div className={`px-5 py-2.5 border-b flex items-center justify-between ${isPassed ? 'bg-green-50 border-green-100' : 'bg-red-50 border-red-100'}`}>
                    <p className={`text-[11px] ${isPassed ? 'text-green-700' : 'text-red-700'}`}>
                      {isPassed ? '🔒 Passed and locked' : '🔒 Rejected and locked'} — the draft, meeting particulars and votes can't be edited without revising first, so a finalized outcome can't drift out of sync with what the votes actually showed.
                    </p>
                    <button onClick={() => reviseResolution(idx)} className={`text-[11px] font-semibold hover:underline flex-shrink-0 ml-3 ${isPassed ? 'text-green-700' : 'text-red-700'}`}>↺ Revise</button>
                  </div>
                )}
                <div className="grid grid-cols-3 gap-5 p-5">
                  <div className="col-span-2">
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs font-semibold text-gray-600">Resolution Draft</label>
                      {!locked && (
                        <button onClick={() => regenerateResolution(idx)} className="text-xs font-semibold text-blue-600 hover:underline">↻ Regenerate from template</button>
                      )}
                    </div>
                    <textarea rows={8} value={(r.manually_edited || r.status === 'Passed') ? (r.rendered_resolution || '') : fillTemplate(r.template_body, fieldContextFor(r))}
                      onChange={e => !locked && updateResolution(idx, { rendered_resolution: e.target.value, manually_edited: true })}
                      readOnly={locked}
                      style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', lineHeight: 1.7 }}
                      className={`w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-blue-500 ${locked ? 'bg-gray-50 cursor-not-allowed' : ''}`} />
                  </div>
                  <div>
                    <p className={`text-xs font-semibold mb-2 ${accent.textLight}`}>{r.meeting_type} Particulars <span className="font-normal text-gray-400">— the meeting actually held</span></p>
                    {r.meeting_id && meetingSiblings[r.meeting_id]?.length > 1 && (
                      <p className="text-[11px] text-amber-600 mb-2">⚠ {meetingSiblings[r.meeting_id].length - 1} other case{meetingSiblings[r.meeting_id].length - 1 !== 1 ? 's' : ''} share this meeting — editing here updates them too.</p>
                    )}
                    <div className="space-y-2.5">
                      {r.meeting_data?.notice_date && (
                        <p className="text-[10px] text-gray-400">Notice sent {new Date(r.meeting_data.notice_date).toLocaleDateString('en-IN')} — the meeting can't be held before then.</p>
                      )}
                      <div className="grid grid-cols-2 gap-2">
                        <input type="date" disabled={locked} value={r.meeting_data?.meeting_date || ''} min={r.meeting_data?.notice_date || undefined}
                          onChange={e => {
                            const nextDate = e.target.value;
                            if (r.meeting_data?.notice_date && nextDate && nextDate < r.meeting_data.notice_date) {
                              setAlert({ message: `This can't be earlier than the notice date (${new Date(r.meeting_data.notice_date).toLocaleDateString('en-IN')}).`, title: 'Invalid date' });
                              return;
                            }
                            updateResolution(idx, { meeting_data: { ...r.meeting_data, meeting_date: nextDate } });
                          }}
                          className={`w-full border border-gray-200 rounded-lg px-2 py-2 text-sm focus:outline-none focus:border-blue-500 ${locked ? 'bg-gray-50' : ''}`} />
                        <input type="time" disabled={locked} value={r.meeting_data?.meeting_time || ''} onChange={e => updateResolution(idx, { meeting_data: { ...r.meeting_data, meeting_time: e.target.value } })}
                          className={`w-full border border-gray-200 rounded-lg px-2 py-2 text-sm focus:outline-none focus:border-blue-500 ${locked ? 'bg-gray-50' : ''}`} />
                      </div>
                      <div>
                        <label className="text-[10px] text-gray-400">Concluded At</label>
                        <input type="time" disabled={locked}
                          value={(minutesExtrasByMeeting[r.meeting_id] || {}).meeting_end_time || ''}
                          onChange={e => setMinutesExtrasByMeeting(prev => ({ ...prev, [r.meeting_id]: { ...prev[r.meeting_id], meeting_end_time: e.target.value } }))}
                          className={`w-full border border-gray-200 rounded-lg px-2 py-2 text-sm focus:outline-none focus:border-blue-500 ${locked ? 'bg-gray-50' : ''}`} />
                      </div>
                      <div className="flex items-center gap-1.5">
                        <button type="button" disabled={locked} onClick={() => updateResolution(idx, { meeting_data: { ...r.meeting_data, venue_mode: 'physical' } })}
                          className={`px-2 py-1 rounded-full text-[10px] font-semibold border ${(r.meeting_data?.venue_mode || 'physical') === 'physical' ? 'bg-[#0f1f45] text-white border-[#0f1f45]' : 'bg-white text-gray-500 border-gray-200'}`}>Physical</button>
                        <button type="button" disabled={locked} onClick={() => updateResolution(idx, { meeting_data: { ...r.meeting_data, venue_mode: 'vc', venue: r.meeting_data?.venue || 'Through Video Conferencing (VC) / Other Audio-Visual Means' } })}
                          className={`px-2 py-1 rounded-full text-[10px] font-semibold border ${r.meeting_data?.venue_mode === 'vc' ? 'bg-[#0f1f45] text-white border-[#0f1f45]' : 'bg-white text-gray-500 border-gray-200'}`}>Audio/Video</button>
                      </div>
                      <input disabled={locked} value={r.meeting_data?.venue || ''} onChange={e => updateResolution(idx, { meeting_data: { ...r.meeting_data, venue: e.target.value } })} placeholder="Venue"
                        className={`w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500 ${locked ? 'bg-gray-50' : ''}`} />
                      <select disabled={locked} value={r.meeting_data?.chairperson || ''} onChange={e => {
                        const name = e.target.value;
                        // Chairing implies attending — add them to the present
                        // list too, so picking a chairperson can't silently
                        // leave attendance/quorum at zero.
                        const attendees = r.meeting_data?.attendees || [];
                        const nextAttendees = (r.meeting_type === 'BM' && name && !attendees.includes(name)) ? [...attendees, name] : attendees;
                        updateResolution(idx, { meeting_data: { ...r.meeting_data, chairperson: name, attendees: nextAttendees } });
                      }} className={`w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500 ${locked ? 'bg-gray-50' : ''}`}>
                        <option value="">Chairperson…</option>
                        {directors.map(d => <option key={d.id} value={d.director_name}>{d.director_name}{d.din ? ` (DIN: ${d.din})` : ''}</option>)}
                      </select>

                      {(() => {
                        const isBM = r.meeting_type === 'BM';
                        const roster = isBM ? directors : shareholders;
                        const rosterKey = isBM ? 'director_name' : 'member_name';
                        const meetingDate = r.meeting_data?.meeting_date;
                        // A director can't validly be "present" at a
                        // meeting held before their appointment or on/after
                        // their cessation date — strip any such name out of
                        // whatever's stored, so a stale attendee doesn't
                        // quietly count toward quorum, voting, or the
                        // generated Minutes/Attendance Sheet.
                        const rawAttendees = r.meeting_data?.attendees || [];
                        const attendees = (isBM && meetingDate)
                          ? rawAttendees.filter(name => {
                              const d = directors.find(x => x.director_name === name);
                              if (!d) return true;
                              return !(d.appointment_date && d.appointment_date > meetingDate) && !(d.cessation_date && d.cessation_date < meetingDate);
                            })
                          : rawAttendees;
                        const quorumNeeded = isBM ? boardQuorum(directors.length) : generalMeetingQuorum(companyType, shareholders.length);
                        const quorumMet = attendees.length >= quorumNeeded;
                        const toggle = (name) => {
                          const next = attendees.includes(name) ? attendees.filter(n => n !== name) : [...attendees, name];
                          updateResolution(idx, { meeting_data: { ...r.meeting_data, attendees: next } });
                        };
                        return (
                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <p className="text-[11px] font-semibold text-gray-500">{isBM ? 'Directors' : 'Members'} Present</p>
                              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${quorumMet ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                {attendees.length}/{roster.length} present · quorum {quorumNeeded} · {quorumMet ? '✓ Met' : '✗ Not Met'}
                              </span>
                            </div>
                            {roster.length === 0 ? (
                              <p className="text-[11px] text-gray-400">{isBM ? 'No directors on record for this company.' : 'No shareholders on record for this company.'}</p>
                            ) : (
                              <div className="flex flex-wrap gap-1.5">
                                {roster.map(p => {
                                  const ineligible = isBM && !!meetingDate && (
                                    (p.appointment_date && p.appointment_date > meetingDate) ||
                                    (p.cessation_date && p.cessation_date < meetingDate)
                                  );
                                  const ineligibleReason = !ineligible ? '' : (p.appointment_date && p.appointment_date > meetingDate)
                                    ? `Not yet appointed as on this meeting's date — appointed ${formatDateForDoc(p.appointment_date)}`
                                    : `Already ceased as on this meeting's date — ceased ${formatDateForDoc(p.cessation_date)}`;
                                  return (
                                    <label key={p.id} title={ineligibleReason || undefined}
                                      className={`flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-lg ${(locked || ineligible) ? 'bg-gray-50 opacity-60 cursor-not-allowed' : 'bg-gray-50'}`}>
                                      <input type="checkbox" disabled={locked || ineligible} checked={!ineligible && attendees.includes(p[rosterKey])} onChange={() => toggle(p[rosterKey])} />
                                      {p[rosterKey]}{isBM && p.din ? ` (DIN: ${p.din})` : ''}{ineligible ? ' ⚠' : ''}
                                    </label>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {(() => {
                        const isBM = r.meeting_type === 'BM';
                        const meetingDate = r.meeting_data?.meeting_date;
                        const rawAttendees = r.meeting_data?.attendees || [];
                        const attendees = (isBM && meetingDate)
                          ? rawAttendees.filter(name => {
                              const d = directors.find(x => x.director_name === name);
                              if (!d) return true;
                              return !(d.appointment_date && d.appointment_date > meetingDate) && !(d.cessation_date && d.cessation_date < meetingDate);
                            })
                          : rawAttendees;
                        const quorumNeeded = isBM ? boardQuorum(directors.length) : generalMeetingQuorum(companyType, shareholders.length);
                        const quorumMet = attendees.length >= quorumNeeded;
                        if (attendees.length === 0) return null;
                        if (!quorumMet) {
                          return <p className="text-[11px] text-gray-400 pt-2 border-t border-gray-100 mt-1">Voting opens once quorum is met.</p>;
                        }
                        if (!r.meeting_data?.chairperson) {
                          return <p className="text-[11px] text-gray-400 pt-2 border-t border-gray-100 mt-1">Voting opens once a chairperson is set.</p>;
                        }
                        const votes = r.votes || {};
                        const { forWeight, againstWeight, abstainWeight } = tallyVotes(votes, attendees, isBM, shareholders);
                        const cast = forWeight + againstWeight;
                        const forPct = cast > 0 ? Math.round((forWeight / cast) * 100) : 0;
                        const threshold = r.resolution_type === 'SPECIAL' ? 75 : 50;
                        const votedCount = attendees.filter(n => votes[n]).length;
                        return (
                          <div className="pt-3 border-t border-gray-100 mt-1">
                            <div className="flex items-center justify-between mb-1.5">
                              <p className="text-[11px] font-semibold text-gray-500">Voting {isBM ? '' : '(share-weighted)'}</p>
                              <span className="text-[10px] text-gray-400">{votedCount}/{attendees.length} voted</span>
                            </div>
                            <div className="space-y-1.5">
                              {attendees.map(name => (
                                <div key={name} className="flex items-center justify-between gap-2">
                                  <span className="text-[11px] text-gray-600 truncate">{name}{isBM ? (() => { const din = directors.find(d => d.director_name === name)?.din; return din ? ` (DIN: ${din})` : ''; })() : ''}</span>
                                  <div className="flex items-center gap-1 flex-shrink-0">
                                    {['for', 'against', 'abstain'].map(choice => (
                                      <button key={choice} type="button" disabled={locked} onClick={() => castVote(idx, name, choice)}
                                        className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase border ${votes[name] === choice
                                          ? (choice === 'for' ? 'bg-green-600 text-white border-green-600' : choice === 'against' ? 'bg-red-600 text-white border-red-600' : 'bg-gray-500 text-white border-gray-500')
                                          : 'bg-white text-gray-400 border-gray-200'}`}>
                                        {choice === 'for' ? 'F' : choice === 'against' ? 'A' : 'Ab'}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                            {cast > 0 && (
                              <div className="mt-2">
                                <div className="h-1.5 rounded-full bg-red-200 overflow-hidden">
                                  <div className="h-full bg-green-500" style={{ width: `${forPct}%` }} />
                                </div>
                                <p className="text-[10px] text-gray-500 mt-1">{forPct}% in favor{abstainWeight > 0 ? ` · ${abstainWeight} abstained` : ''} — needs ≥{threshold}% of votes cast</p>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
            {(() => {
              const availableTypes = [...new Set(resolutions.map(r => r.meeting_type))];
              if (!addResOpen) {
                return availableTypes.length === 0
                  ? <p className="text-xs text-gray-400">Link a meeting to this case first (via the wizard) before adding more resolutions.</p>
                  : <button onClick={() => setAddResOpen(true)} className="text-xs font-semibold text-blue-600 hover:underline">+ Add Resolution</button>;
              }
              const options = (addResType ? (addResOptionsByType[addResType] || []) : []).filter(r => {
                if (resolutions.some(x => x.resolution_id === r.id && x.meeting_type === addResType)) return false;
                const q = addResSearch.toLowerCase();
                return !q || r.resolution_title?.toLowerCase().includes(q) || r.category?.toLowerCase().includes(q);
              });
              return (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs font-semibold text-gray-600">Add a resolution to this event filing</p>
                    <button onClick={() => { setAddResOpen(false); setAddResType(''); setAddResSearch(''); }} className="text-[11px] font-semibold text-gray-400 hover:text-gray-600">Cancel</button>
                  </div>
                  <div className="flex gap-2 mb-3">
                    {availableTypes.map(t => (
                      <button key={t} onClick={() => setAddResType(t)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${addResType === t ? 'bg-[#0f1f45] text-white border-[#0f1f45]' : 'border-gray-200 text-gray-600'}`}>{t}</button>
                    ))}
                  </div>
                  {addResType && (
                    <>
                      <input value={addResSearch} onChange={e => setAddResSearch(e.target.value)} placeholder="Search resolutions…" autoFocus
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-blue-500 mb-2" />
                      <div className="space-y-1.5 max-h-56 overflow-y-auto">
                        {options.map(r => (
                          <button key={r.id} onClick={() => addResolutionToCase(addResType, r)}
                            className="w-full flex items-start gap-2 px-3 py-2 rounded-lg border border-gray-200 text-left text-xs hover:bg-gray-50">
                            <span><span className="font-semibold text-[#0f1f45]">{r.resolution_code} — {r.resolution_title}</span><span className="block text-gray-400">{r.resolution_type} · {r.category}</span></span>
                          </button>
                        ))}
                        {options.length === 0 && <p className="px-3 py-4 text-center text-xs text-gray-400">No matching resolutions in Resolution Master.</p>}
                      </div>
                    </>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {activeTab === 'documents' && (
        <div className="space-y-5">
          {meetingGroups.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-10 text-center text-gray-400 text-sm">No meeting linked to this case yet — set a meeting date on the Resolutions tab first.</div>
          ) : meetingGroups.map(({ meetingId, type }) => {
            const accent = meetingAccent(type);
            const items = getMeetingAgendaItems(meetingId);
            const selection = ctcSelectionByMeeting[meetingId] || {};
            const selectedCount = items.filter(i => selection[i.itemNo]).length;
            const allSelected = items.length > 0 && selectedCount === items.length;
            const isBM = type === 'BM';
            const md = getMeetingData(meetingId);
            const gapDays = noticeGapDays(type, md.notice_date, md.meeting_date);
            const requiredDays = requiredNoticeDays(type);
            const isShort = gapDays !== null && gapDays < requiredDays;
            const consentRoster = isBM ? directors.map(d => d.director_name) : shareholders.map(s => s.member_name);
            const consenters = shorterConsentByMeeting[meetingId] || md.attendees || [];
            const extras = minutesExtrasByMeeting[meetingId] || {};
            const setExtra = (field, value) => setMinutesExtrasByMeeting(prev => ({ ...prev, [meetingId]: { ...prev[meetingId], [field]: value } }));
            return (
              <div key={meetingId} className={`bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden border-l-4 ${accent.border}`}>
                <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                  <div>
                    <p className={`text-sm font-bold ${accent.text}`}>{meetingLabel(type)}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{items.length} agenda item{items.length !== 1 ? 's' : ''}{meetingSiblings[meetingId]?.length > 1 ? ` across ${meetingSiblings[meetingId].length} cases` : ''}</p>
                    {type !== 'BM' && (
                      <p className="text-[11px] text-amber-600 mt-1.5">⚠ A Board Meeting approving the notice to call this {type} should be held and documented separately — if it's tracked as its own event in this workspace, its documents appear in that case's own Documents tab.</p>
                    )}
                  </div>
                  <button onClick={() => requestRegisterSync(meetingId, type)} disabled={registerSyncingId === meetingId}
                    title="Pushes date/time/venue/mode/chairman/quorum/attendance into the Statutory Register. Register-only fields (meeting number, SRN, etc.) still need filling in there directly."
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 text-[11px] font-semibold disabled:opacity-50 flex-shrink-0">
                    {registerSyncingId === meetingId ? 'Syncing…' : '⇪ Sync to Register'}
                  </button>
                </div>

                <div className="p-5 space-y-4">
                  <div>
                    <p className="text-xs font-semibold text-gray-600 mb-2">Notice & Minutes</p>
                    <label className="flex items-center gap-2 text-xs text-gray-600 mb-2.5">
                      <input type="checkbox" checked={!!shorterNoticeByMeeting[meetingId]} onChange={() => setShorterNoticeByMeeting(prev => ({ ...prev, [meetingId]: !prev[meetingId] }))} />
                      Issue on shorter notice (consent obtained)
                    </label>
                    {/* Concluded At now lives on the Resolution tab, next to
                        Meeting Date/Place — it's meeting-level data, set once
                        where the meeting particulars are already edited. */}
                    {!isBM && (
                      <div className="mb-2.5">
                        <input value={extras.chairman_remarks || ''} onChange={e => setExtra('chairman_remarks', e.target.value)} placeholder="Chairman's remarks (optional)" className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-blue-500" />
                      </div>
                    )}
                    {!isBM && (
                      <input value={extras.scrutinizer_name || ''} onChange={e => setExtra('scrutinizer_name', e.target.value)} placeholder="Scrutinizer name (if polled)"
                        className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-blue-500 mb-2.5" />
                    )}
                    {isBM && (
                      <input value={extras.interest_disclosure || ''} onChange={e => setExtra('interest_disclosure', e.target.value)} placeholder="Directors' interest disclosure (Sec 184) — leave blank for the standard 'none interested' line"
                        className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-blue-500 mb-2.5" />
                    )}
                    <div className="flex flex-wrap gap-2">
                      <button onClick={() => downloadMeetingNoticeDoc(meetingId, type)} className="px-3 py-2 rounded-lg border border-gray-200 text-gray-600 text-xs font-semibold">📄 Notice of Meeting (Word)</button>
                      <button onClick={() => downloadMeetingMinutesDoc(meetingId, type)} className="px-3 py-2 rounded-lg border border-gray-200 text-gray-600 text-xs font-semibold">📄 Minutes (Word)</button>
                      <button onClick={() => downloadAttendanceSheetDoc(meetingId, type)} className="px-3 py-2 rounded-lg border border-gray-200 text-gray-600 text-xs font-semibold">📄 Attendance Sheet (Word)</button>
                    </div>
                  </div>

                  {!isBM && (
                    <div className="pt-4 border-t border-gray-100">
                      <p className="text-xs font-semibold text-gray-600 mb-1">Attendance Sheet Details</p>
                      <p className="text-[11px] text-gray-400 mb-3">Classify each present member (Sec 103/112/113 all count toward quorum), add any proxies (Sec 105 — excluded from quorum), and record which directors/KMP/auditors attended.</p>

                      {(md.attendees || []).length > 0 && (
                        <div className="mb-3">
                          <p className="text-[11px] font-semibold text-gray-500 mb-1">Member Category</p>
                          <div className="space-y-1">
                            {(md.attendees || []).map(name => (
                              <div key={name} className="flex items-center justify-between gap-2 text-xs">
                                <span className="text-gray-700">{name}</span>
                                <select value={extras.attendee_categories?.[name] || 'self'} onChange={e => setAttendeeCategory(meetingId, name, e.target.value)} className="border border-gray-200 rounded-lg px-2 py-1 text-[11px]">
                                  <option value="self">Self</option>
                                  <option value="corp_rep">Corporate Rep. (Sec 113)</option>
                                  <option value="nominee">President/Governor Nominee (Sec 112)</option>
                                </select>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="mb-3">
                        <p className="text-[11px] font-semibold text-gray-500 mb-1">Proxies (Form MGT-11) — excluded from quorum</p>
                        {(extras.proxies || []).length > 0 && (
                          <div className="space-y-1 mb-2">
                            {extras.proxies.map(p => (
                              <div key={p.id} className="flex items-center justify-between gap-2 text-xs bg-gray-50 rounded-lg px-2 py-1">
                                <span>{p.name} — represents {p.represents_folio || 'folio ?'} ({p.shares || 0} shares)</span>
                                <button onClick={() => removeProxy(meetingId, p.id)} className="text-red-500 text-[11px] font-semibold">Remove</button>
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="grid grid-cols-4 gap-1.5">
                          <input value={proxyDraft[meetingId]?.name || ''} onChange={e => updateProxyDraft(meetingId, { name: e.target.value })} placeholder="Proxy name" className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs col-span-2" />
                          <input value={proxyDraft[meetingId]?.folio || ''} onChange={e => updateProxyDraft(meetingId, { folio: e.target.value })} placeholder="Represents Folio" className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs" />
                          <input type="number" value={proxyDraft[meetingId]?.shares || ''} onChange={e => updateProxyDraft(meetingId, { shares: e.target.value })} placeholder="Shares" className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs" />
                        </div>
                        <button onClick={() => addProxy(meetingId)} className="mt-1.5 text-[11px] font-semibold text-blue-600">+ Add Proxy</button>
                      </div>

                      <div className="mb-3">
                        <p className="text-[11px] font-semibold text-gray-500 mb-1">Directors / KMP Present</p>
                        <div className="flex flex-wrap gap-1.5">
                          {directors.map(d => (
                            <label key={d.id} className="flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-lg bg-gray-50">
                              <input type="checkbox" checked={(extras.gm_directors_present || []).includes(d.director_name)} onChange={() => toggleGmDirectorPresent(meetingId, d.director_name)} />{d.director_name}{d.din ? ` (DIN: ${d.din})` : ''}
                            </label>
                          ))}
                          {directors.length === 0 && <p className="text-[11px] text-gray-400">No directors on record.</p>}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-2 mb-2">
                        <input value={extras.cs_name || ''} onChange={e => setExtra('cs_name', e.target.value)} placeholder="Company Secretary name" className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs" />
                        <input value={extras.cs_membership_no || ''} onChange={e => setExtra('cs_membership_no', e.target.value)} placeholder="CS Membership No." className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs" />
                      </div>
                      <div className="grid grid-cols-3 gap-2 mb-2">
                        <input value={extras.auditor_name || ''} onChange={e => setExtra('auditor_name', e.target.value)} placeholder="Statutory Auditor name" className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs" />
                        <input value={extras.auditor_firm_name || ''} onChange={e => setExtra('auditor_firm_name', e.target.value)} placeholder="Auditor firm name" className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs" />
                        <input value={extras.auditor_frn || ''} onChange={e => setExtra('auditor_frn', e.target.value)} placeholder="FRN" className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs" />
                      </div>
                      <input value={extras.scrutinizer_mem_no || ''} onChange={e => setExtra('scrutinizer_mem_no', e.target.value)} placeholder="Scrutinizer membership/CP no." className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs" />
                    </div>
                  )}

                  {isShort && gapDays >= 0 && (
                    <div className="pt-4 border-t border-gray-100">
                      <p className="text-xs font-semibold text-gray-600 mb-1">Shorter Notice Consent <span className="font-normal text-amber-600">— required, only {gapDays} of {requiredDays}{!isBM ? ' clear' : ''} days given</span></p>
                      <p className="text-[11px] text-gray-400 mb-2">
                        {isBM ? 'Tick which directors consented (Independent Director presence/ratification is checked automatically).' : 'Tick which members consented — the document totals their shareholding against the 95% threshold.'}
                      </p>
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {consentRoster.map(name => (
                          <label key={name} className="flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-lg bg-gray-50">
                            <input type="checkbox" checked={consenters.includes(name)} onChange={() => toggleShorterConsent(meetingId, name)} />
                            {name}{isBM ? (() => { const din = directors.find(d => d.director_name === name)?.din; return din ? ` (DIN: ${din})` : ''; })() : ''}
                          </label>
                        ))}
                        {consentRoster.length === 0 && <p className="text-[11px] text-gray-400">No roster on record.</p>}
                      </div>
                      <button onClick={() => downloadShorterConsentDoc(meetingId, type)} className="px-3 py-2 rounded-lg border border-gray-200 text-gray-600 text-xs font-semibold">📄 Shorter Notice Consent (Word)</button>
                    </div>
                  )}

                  <div className="pt-4 border-t border-gray-100">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-semibold text-gray-600">Certified True Copy (CTC)</p>
                      {items.length > 1 && (
                        <button onClick={() => setCtcSelectionByMeeting(prev => ({ ...prev, [meetingId]: Object.fromEntries(items.map(i => [i.itemNo, !allSelected])) }))}
                          className="text-[11px] font-semibold text-blue-600">{allSelected ? 'Clear all' : 'Select all'}</button>
                      )}
                    </div>
                    {items.length > 1 && (
                      <div className="space-y-1 mb-3 border border-gray-100 rounded-lg divide-y divide-gray-50">
                        {items.map(i => (
                          <label key={i.itemNo} className="flex items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-50">
                            <input type="checkbox" checked={!!selection[i.itemNo]} onChange={() => toggleCtcSelection(meetingId, i.itemNo)} />
                            <span className="font-semibold text-gray-400">#{i.itemNo}</span> {i.resolution.resolution_title}
                          </label>
                        ))}
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-2 mb-2.5">
                      <input value={extras.signatory_name || ''} onChange={e => setExtra('signatory_name', e.target.value)} placeholder={`Signatory name (defaults to chairperson)`}
                        className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-blue-500" />
                      <select value={extras.signatory_role || 'Director'} onChange={e => setExtra('signatory_role', e.target.value)}
                        className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-blue-500">
                        <option>Director</option>
                        <option>Managing Director</option>
                        <option>Whole-time Director</option>
                        {!isBM && <option>Chairperson of the Meeting</option>}
                        <option>Company Secretary</option>
                      </select>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mb-2.5">
                      <input value={extras.signatory_din_or_membership || ''} onChange={e => setExtra('signatory_din_or_membership', e.target.value)}
                        placeholder={(extras.signatory_role || 'Director') === 'Company Secretary' ? 'Membership Number' : 'DIN'}
                        className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-blue-500" />
                      {/* Feeds "Place: __________" on the CTC/Minutes/Shorter
                          Consent sign-off lines below instead of leaving it
                          blank for the CS to fill by hand afterward. */}
                      <input value={extras.signatory_place || ''} onChange={e => setExtra('signatory_place', e.target.value)}
                        placeholder="Place (defaults to registered office)"
                        className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-blue-500" />
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button onClick={() => downloadMeetingCtcDoc(meetingId, type, false)} className="px-3 py-2 rounded-lg bg-[#0f1f45] text-white text-xs font-semibold">📄 Full CTC — All Resolutions</button>
                      {items.length > 1 && (
                        <button onClick={() => downloadMeetingCtcDoc(meetingId, type, true)} disabled={selectedCount === 0}
                          className="px-3 py-2 rounded-lg border border-gray-200 text-gray-600 text-xs font-semibold disabled:opacity-40">📄 CTC — Selected ({selectedCount})</button>
                      )}
                    </div>
                  </div>

                  {isBM && transfers.length > 0 && (
                    <div className="pt-4 border-t border-gray-100">
                      <p className="text-xs font-semibold text-gray-600 mb-1">Share Transfer Documents <span className="font-normal text-gray-400">— {transfers.length} row{transfers.length !== 1 ? 's' : ''}</span></p>
                      <div className="flex flex-wrap gap-2 mb-3">
                        <button onClick={downloadRequestLettersDoc} className="px-3 py-2 rounded-lg border border-gray-200 text-gray-600 text-xs font-semibold">📄 Request Letters (Word)</button>
                        <button onClick={downloadApplicationForRegistrationDoc} className="px-3 py-2 rounded-lg border border-gray-200 text-gray-600 text-xs font-semibold">📄 Application for Registration (Word)</button>
                        <button onClick={downloadSh4Doc} className="px-3 py-2 rounded-lg border border-gray-200 text-gray-600 text-xs font-semibold">📄 Form SH-4 (Word)</button>
                        <button onClick={downloadAcknowledgementDoc} className="px-3 py-2 rounded-lg border border-gray-200 text-gray-600 text-xs font-semibold">📄 Acknowledgement (Word)</button>
                      </div>
                      <p className="text-[11px] text-gray-400 mb-2">Applies each transfer row to the shareholder register — decrements the transferor(s), creates/increments the transferee(s), and records it in the Register of Transfers. Safe to click again; already-applied rows are skipped.</p>
                      <button onClick={syncShareholding} disabled={syncingShareholding}
                        className="px-3 py-2 rounded-lg border border-gray-200 text-gray-600 text-xs font-semibold disabled:opacity-50">
                        {syncingShareholding ? 'Syncing…' : '⇪ Sync Shareholding'}
                      </button>
                    </div>
                  )}

                  {isBM && directorAppointments.length > 0 && (
                    <div className="pt-4 border-t border-gray-100">
                      <p className="text-xs font-semibold text-gray-600 mb-1">Director Appointment Documents <span className="font-normal text-gray-400">— {directorAppointments.length} director{directorAppointments.length !== 1 ? 's' : ''}</span></p>
                      <div className="flex flex-wrap gap-2 mb-2">
                        <button onClick={downloadDir2Doc} className="px-3 py-2 rounded-lg border border-gray-200 text-gray-600 text-xs font-semibold">📄 Form DIR-2 (Word)</button>
                        <button onClick={downloadDir8Doc} className="px-3 py-2 rounded-lg border border-gray-200 text-gray-600 text-xs font-semibold">📄 Form DIR-8 (Word)</button>
                      </div>
                      <p className="text-[11px] text-gray-400">Once every filing on this case (DIR-12) is marked Approved on the Filing &amp; Tracking tab, each director's details here are pushed into Company Master → Directors automatically — nothing to re-type there.</p>
                    </div>
                  )}

                  {isBM && directorResignations.length > 0 && (
                    <div className="pt-4 border-t border-gray-100">
                      <p className="text-xs font-semibold text-gray-600 mb-1">Director Resignation Documents <span className="font-normal text-gray-400">— {directorResignations.length} director{directorResignations.length !== 1 ? 's' : ''}</span></p>
                      <div className="flex flex-wrap gap-2 mb-2">
                        <button onClick={downloadResignationLetterDoc} className="px-3 py-2 rounded-lg border border-gray-200 text-gray-600 text-xs font-semibold">📄 Resignation Letter (Word)</button>
                      </div>
                      <p className="text-[11px] text-gray-400">Once every filing on this case (DIR-12) is marked Approved on the Filing &amp; Tracking tab, each director here is marked ceased in Company Master → Directors automatically — nothing to update there manually.</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {activeTab === 'attachments' && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3">
            <IconBadge accent={ACCENTS.navy}>{Icon.checklist}</IconBadge>
            <div>
              <h4 className="text-base font-bold text-[#0f1f45]">Required Attachments</h4>
              <p className="text-xs text-gray-400 mt-0.5">Seeded from every linked resolution's requirements, combined.</p>
            </div>
          </div>
          {checklist.length === 0 ? (
            <div className="py-10 text-center text-gray-400 text-sm">No attachments required for this event.</div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/60">{['Item', 'Status', 'Remarks', ''].map(h => <th key={h} className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase">{h}</th>)}</tr>
              </thead>
              <tbody>
                {checklist.map(row => (
                  <tr key={row.id} className="border-b border-gray-50 hover:bg-gray-50/60">
                    <td className="px-4 py-3 text-sm font-medium text-gray-800">{row.task}</td>
                    <td className="px-4 py-3">
                      <select value={row.status} onChange={e => updateChecklistItem(row.id, 'status', e.target.value)}
                        className={`px-2 py-1 rounded-full text-[11px] font-semibold border-0 focus:outline-none ${ATTACHMENT_STATUS_COLORS[row.status] || 'bg-gray-100 text-gray-600'}`}>
                        <option>Pending</option><option>Collected</option><option>Completed</option><option>Not Applicable</option>
                      </select>
                    </td>
                    <td className="px-4 py-3"><input value={row.remarks || ''} onChange={e => updateChecklistItem(row.id, 'remarks', e.target.value)} placeholder="Note" className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-blue-500" /></td>
                    <td className="px-2 py-3"><button onClick={() => removeChecklistItem(row.id)} className="text-red-400 hover:text-red-600 text-xs">✕</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <AddChecklistRow onAdd={addChecklistItem} />
        </div>
      )}

      {activeTab === 'filing' && (
        <div className="space-y-5">
          {filings.length === 0 && (
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-10 text-center text-gray-400 text-sm">This event has no forms to file.</div>
          )}
          {filings.map((f, idx) => {
            const accent = filingAccent(f.status);
            const locked = f.status === 'Approved' || f.status === 'Rejected';
            return (
              <div key={idx} className={`bg-white rounded-xl border border-gray-100 shadow-sm p-5 border-l-4 ${accent.border}`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <IconBadge accent={accent}>{Icon.form}</IconBadge>
                    <p className="text-sm font-bold text-[#0f1f45] font-mono">{f.form}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${STATUS_COLORS[f.status === 'Pending' ? 'Draft' : f.status] || 'bg-gray-100 text-gray-600'}`}>{syncing && f.status === 'Approved' ? 'Syncing…' : f.status}</span>
                    {locked && (
                      <button onClick={() => editFiling(idx)} className="text-[11px] font-semibold text-gray-500 hover:underline">✎ Edit</button>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-5">
                  <div className="space-y-2.5">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">SRN</label>
                      <input disabled={locked} value={f.srn} onChange={e => updateFilingAuto(idx, { srn: e.target.value.toUpperCase() })}
                        className={`w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500 uppercase ${locked ? 'bg-gray-50' : ''}`} />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Filing Date <span className="font-normal text-gray-400">— sets Filed once SRN's in too</span></label>
                      <input type="date" disabled={locked} value={f.filing_date || ''} onChange={e => updateFilingAuto(idx, { filing_date: e.target.value })}
                        className={`w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500 ${locked ? 'bg-gray-50' : ''}`} />
                    </div>
                  </div>
                  <div className="space-y-2.5">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Approval Date <span className="font-normal text-gray-400">— sets Approved</span></label>
                      <input type="date" disabled={locked} value={f.approval_date || ''} onChange={e => updateFilingAuto(idx, { approval_date: e.target.value })}
                        className={`w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500 ${locked ? 'bg-gray-50' : ''}`} />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Rejection Reason <span className="font-normal text-gray-400">— sets Rejected</span></label>
                      <input disabled={locked} value={f.rejection_reason || ''} onChange={e => updateFilingAuto(idx, { rejection_reason: e.target.value })}
                        className={`w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500 ${locked ? 'bg-gray-50' : ''}`} />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          {filings.length > 0 && filings.every(f => f.status === 'Approved') && (
            <div className="px-5 py-3 rounded-lg bg-green-50 text-green-700 text-sm">
              {item.synced_at ? `All forms approved — synced into registers on ${new Date(item.synced_at).toLocaleDateString('en-IN')}.` : 'All forms approved.'}
            </div>
          )}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Internal Notes</label>
            <textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500" />
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// TRANSFER ROWS EDITOR — the repeating line-item table behind the
// {{transfers_table}} placeholder. One row = one transferor→transferee
// pair. Picking an existing shareholder as transferor auto-fills their
// folio/PAN-DIN/current holding straight from Company Master (the
// "pre-filled variables" this event type was actually asked for);
// transferee is either an existing member or a fresh one with an
// auto-suggested next folio number.
// ═══════════════════════════════════════════════════════════════════════
const EMPTY_TRANSFER_ROW = {
  transferor_shareholder_id: '', transferor_name: '', transferor_folio: '', transferor_din: '', transferor_pan: '',
  no_of_shares: '', face_value: '10', consideration_per_share: '10',
  distinctive_from: '', distinctive_to: '', certificate_no: '',
  transferee_mode: 'new', transferee_shareholder_id: '', transferee_name: '', transferee_folio: '',
  transferee_father_husband: '', transferee_address: '', transferee_pan: '', transferee_occupation: 'Business',
  witness_name: '', witness_address: '', transfer_date: '',
};

function TransferRowsEditor({ transfers, onChange, shareholders, defaultDate }) {
  const rows = transfers || [];
  const nextFolio = () => {
    const nums = shareholders.map(s => Number(s.folio_number)).filter(n => Number.isFinite(n));
    return nums.length ? String(Math.max(...nums) + 1) : '1';
  };

  const addRow = () => onChange([...rows, { ...EMPTY_TRANSFER_ROW, transfer_date: defaultDate || '', transferee_folio: nextFolio() }]);
  const removeRow = (idx) => onChange(rows.filter((_, i) => i !== idx));
  const updateRow = (idx, patch) => onChange(rows.map((r, i) => i === idx ? { ...r, ...patch } : r));

  const pickTransferor = (idx, shareholderId) => {
    const sh = shareholders.find(s => String(s.id) === String(shareholderId));
    if (!sh) { updateRow(idx, { transferor_shareholder_id: '', transferor_name: '', transferor_folio: '', transferor_din: '', transferor_pan: '' }); return; }
    updateRow(idx, {
      transferor_shareholder_id: sh.id,
      transferor_name: sh.member_name,
      transferor_folio: sh.folio_number || '',
      transferor_pan: sh.pan || '',
      face_value: sh.face_value || '10',
      // Auto-suggest the transferor's own distinctive range — correct
      // when the whole holding moves; wrong for a partial transfer, so
      // it stays editable rather than locked.
      distinctive_from: sh.distinctive_from || '',
      distinctive_to: sh.distinctive_to || '',
    });
  };

  const pickTransferee = (idx, shareholderId) => {
    const sh = shareholders.find(s => String(s.id) === String(shareholderId));
    if (!sh) return;
    updateRow(idx, {
      transferee_shareholder_id: sh.id, transferee_name: sh.member_name, transferee_folio: sh.folio_number || '',
      transferee_pan: sh.pan || '', transferee_address: sh.address || '', transferee_father_husband: sh.father_husband_name || '',
    });
  };

  const inputCls = "w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-blue-500";

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold text-gray-600">Transfers <span className="font-normal text-gray-400">— one row per transferor</span></p>
        <button type="button" onClick={addRow} className="text-xs font-semibold text-blue-600">+ Add Transfer</button>
      </div>
      {rows.length === 0 && <p className="text-xs text-gray-400 border border-dashed border-gray-200 rounded-lg px-3 py-4 text-center">No transfer rows yet — click "+ Add Transfer".</p>}
      <div className="space-y-3">
        {rows.map((t, idx) => (
          <div key={idx} className="border border-gray-200 rounded-lg p-3 space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-[11px] font-semibold text-gray-500">Row {idx + 1}</p>
              <button type="button" onClick={() => removeRow(idx)} className="text-red-500 text-xs">Remove</button>
            </div>

            <div className="grid grid-cols-4 gap-2">
              <div className="col-span-2">
                <label className="block text-[10px] text-gray-400 mb-0.5">Transferor (existing shareholder)</label>
                <select className={inputCls} value={t.transferor_shareholder_id} onChange={e => pickTransferor(idx, e.target.value)}>
                  <option value="">Select shareholder…</option>
                  {shareholders.map(s => <option key={s.id} value={s.id}>{s.member_name} — Folio {s.folio_number} ({s.no_of_shares} shares)</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] text-gray-400 mb-0.5">No. of Shares</label>
                <input type="number" className={inputCls} value={t.no_of_shares} onChange={e => updateRow(idx, { no_of_shares: e.target.value })} />
              </div>
              <div>
                <label className="block text-[10px] text-gray-400 mb-0.5">Consideration/Share</label>
                <input type="number" className={inputCls} value={t.consideration_per_share} onChange={e => updateRow(idx, { consideration_per_share: e.target.value })} />
              </div>
              <div>
                <label className="block text-[10px] text-gray-400 mb-0.5">Transferor DIN</label>
                <input className={inputCls} value={t.transferor_din} onChange={e => updateRow(idx, { transferor_din: e.target.value })} />
              </div>
              <div>
                <label className="block text-[10px] text-gray-400 mb-0.5">Transferor PAN</label>
                <input className={inputCls} value={t.transferor_pan} onChange={e => updateRow(idx, { transferor_pan: e.target.value })} />
              </div>
            </div>

            <div className="grid grid-cols-4 gap-2">
              <div>
                <label className="block text-[10px] text-gray-400 mb-0.5">Distinctive From</label>
                <input className={inputCls} value={t.distinctive_from} onChange={e => updateRow(idx, { distinctive_from: e.target.value })} />
              </div>
              <div>
                <label className="block text-[10px] text-gray-400 mb-0.5">Distinctive To</label>
                <input className={inputCls} value={t.distinctive_to} onChange={e => updateRow(idx, { distinctive_to: e.target.value })} />
              </div>
              <div>
                <label className="block text-[10px] text-gray-400 mb-0.5">Certificate No.</label>
                <input className={inputCls} value={t.certificate_no} onChange={e => updateRow(idx, { certificate_no: e.target.value })} />
              </div>
              <div>
                <label className="block text-[10px] text-gray-400 mb-0.5">Transfer Date</label>
                <input type="date" className={inputCls} value={t.transfer_date} onChange={e => updateRow(idx, { transfer_date: e.target.value })} />
              </div>
            </div>

            <div className="flex items-center gap-1.5 pt-1">
              <button type="button" onClick={() => updateRow(idx, { transferee_mode: 'existing' })}
                className={`px-2 py-1 rounded-full text-[10px] font-semibold border ${t.transferee_mode === 'existing' ? 'bg-[#0f1f45] text-white border-[#0f1f45]' : 'bg-white text-gray-500 border-gray-200'}`}>Existing Member</button>
              <button type="button" onClick={() => updateRow(idx, { transferee_mode: 'new', transferee_shareholder_id: '', transferee_folio: nextFolio() })}
                className={`px-2 py-1 rounded-full text-[10px] font-semibold border ${t.transferee_mode === 'new' ? 'bg-[#0f1f45] text-white border-[#0f1f45]' : 'bg-white text-gray-500 border-gray-200'}`}>New Member</button>
            </div>

            {t.transferee_mode === 'existing' ? (
              <div>
                <label className="block text-[10px] text-gray-400 mb-0.5">Transferee</label>
                <select className={inputCls} value={t.transferee_shareholder_id} onChange={e => pickTransferee(idx, e.target.value)}>
                  <option value="">Select shareholder…</option>
                  {shareholders.filter(s => String(s.id) !== String(t.transferor_shareholder_id)).map(s => <option key={s.id} value={s.id}>{s.member_name} — Folio {s.folio_number}</option>)}
                </select>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                <div><label className="block text-[10px] text-gray-400 mb-0.5">Transferee Name</label><input className={inputCls} value={t.transferee_name} onChange={e => updateRow(idx, { transferee_name: e.target.value })} /></div>
                <div><label className="block text-[10px] text-gray-400 mb-0.5">Father's/Husband's Name</label><input className={inputCls} value={t.transferee_father_husband} onChange={e => updateRow(idx, { transferee_father_husband: e.target.value })} /></div>
                <div><label className="block text-[10px] text-gray-400 mb-0.5">New Folio No.</label><input className={inputCls} value={t.transferee_folio} onChange={e => updateRow(idx, { transferee_folio: e.target.value })} /></div>
                <div className="col-span-2"><label className="block text-[10px] text-gray-400 mb-0.5">Address</label><input className={inputCls} value={t.transferee_address} onChange={e => updateRow(idx, { transferee_address: e.target.value })} /></div>
                <div><label className="block text-[10px] text-gray-400 mb-0.5">PAN</label><input className={inputCls} value={t.transferee_pan} onChange={e => updateRow(idx, { transferee_pan: e.target.value })} /></div>
                <div><label className="block text-[10px] text-gray-400 mb-0.5">Occupation</label><input className={inputCls} value={t.transferee_occupation} onChange={e => updateRow(idx, { transferee_occupation: e.target.value })} /></div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-2 pt-1">
              <div><label className="block text-[10px] text-gray-400 mb-0.5">Witness Name (for SH-4)</label><input className={inputCls} value={t.witness_name} onChange={e => updateRow(idx, { witness_name: e.target.value })} /></div>
              <div><label className="block text-[10px] text-gray-400 mb-0.5">Witness Address</label><input className={inputCls} value={t.witness_address} onChange={e => updateRow(idx, { witness_address: e.target.value })} /></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AddChecklistRow({ onAdd }) {
  const [task, setTask] = useState('');
  return (
    <div className="px-4 py-3 bg-gray-50/50 border-t border-gray-100 flex items-center gap-2">
      <input value={task} onChange={e => setTask(e.target.value)} placeholder="+ Add attachment" className="flex-1 border border-dashed border-gray-300 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:border-blue-500 bg-white" />
      <button onClick={() => { onAdd(task); setTask(''); }} className="px-3 py-1.5 rounded-lg text-white text-xs font-medium bg-[#0f1f45]">Add</button>
    </div>
  );
}

export default EventFilings;